import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { emailRegexPattern, roundToFixed, formatNumberWithCommas } from '../../helper';
import styles from './ManageTickets.module.css';
import {
  ChevronDown,
  Download,
  MoreVertical,
  Clock,
  AlertCircle,
  Package,
  Calendar,
  MapPinned,
  CheckSquare,
  Square,
  DollarSign,
  Eye,
  Edit,
  X,
  Search as SearchIcon,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  FileText,
  ArrowRight,
  ExternalLink,
  Filter
} from 'lucide-react';
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useQuery, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import { getStatusIcon } from './getStatusIcon';
import {
  sortManageTickets,
  MANAGE_TICKETS_ORDER_BY,
  getManageTicketsTrackingVariables,
  parseAndSortSeatNumbers,
} from './manageTicketSort';
import { isFailToTransferTicket } from './utils/failToTransfer';
import {
  GET_LIST_TICKETS,
  GET_MANAGE_TICKETS,
  GET_SOLD_TICKETS,
  GET_DELIST_AND_RETURN_TICKETS,
  GET_DELIST_AND_UNSOLD_TICKETS,
  GET_LISTING_REQUEST_CANCELLED_TICKETS,
  GET_DELETED_TICKETS,
} from './api/TicketsQuery';
import {
  GET_LEAGUES_LIST,
  VALIDATE_UPDATE,
  EDIT_TICKET_PRICE_BY_SUPER_USER,
  PUBLISH_TICKETS,
  UPDATE_TICKET_DETAILS,
  UPDATE_TICKET_STATUS,
  RESTORE_CANCELLED_LISTING_TO_VERIFICATION,
  GET_EXPIRED_TICKETS,
  GET_FAILED_TRANSFER_TICKETS,
  GET_TICKETS_TO_AUTO_EXPIRE,
  BULK_EXPIRE_TICKETS,
  UPDATE_PRICE_DIRECT,
  GET_MARKETPLACES_FOR_TICKETS,
  GET_TICKET_MARKETPLACES_BY_TP_IDS,
  GET_USER_FEE_PERCENT_BY_ID
} from './api/TicketsQuery';
import {
  EVENT_VIEW_GET_MANAGE_TICKETS,
  EVENT_VIEW_GET_LIST_TICKETS,
  EVENT_VIEW_GET_SOLD_TICKETS,
  EVENT_VIEW_GET_DELIST_AND_RETURN_TICKETS,
  EVENT_VIEW_GET_LISTING_REQUEST_CANCELLED_TICKETS,
  EVENT_VIEW_GET_DELETED_TICKETS,
} from './api/EventViewTicketsQuery';
import { MARK_AS_SOLD } from '../../components/shared-components/mark-as-sold/api/MarkAsSoldQuery';
import { GET_SUPER_USER_BY_UID } from '../PriceSetUp/api/PriceSetUp';
import { config } from '../../config/config';
import { Ticket, League, Seat } from '../../types/ticket.types';
import { useNavigate } from 'react-router-dom';
import ToasterWithMessage from "../../components/shared-components/toaster";
import { GET_NOTIFICATIONS } from "../../components/shared-components/notification/api/NotificationQuery";
import { getNotificationQueryVariables, mergeNotificationQueryIntoGlobalState, scheduleNotificationSyncRetries } from "../../utils/notification-query-variables";
import { globalContext } from "../../Provider/AppProvider";
import { getMailParserErrorTpIds, getManageTicketsTabFromErrorStatus, isStaffAdminEquivalent, roles } from "../../helper";
import { GET_MANAGED_USERS_BY_SALES_REP, GET_SALES_REPS_BY_EMAIL } from "../Salesrep/api/SalesrepQuery";
import { GET_USERS_LIST } from "../Users/api/UsersQuery";

type MarketplaceOption = {
  id: string;
  name: string;
  vl_fee_percentage: number;
  active?: boolean;
};

const DEFAULT_CONCIERGE_PERCENT = 20;
const SELF_PRICING_FEE_FALLBACK = 15;

/** Same fee logic as `MarkAsSold.tsx` / backend `markAsSold` (QuickAsyst % of list). */
type SoldFeeConfig = {
  feeMode: 'super' | 'default';
  superClientPercent: number | null;
};

const DEFAULT_SOLD_FEE_CONFIG: SoldFeeConfig = { feeMode: 'default', superClientPercent: null };

function effectiveSoldQuickCutPercent(
  marketplaceFeePercent: number,
  feeMode: 'super' | 'default',
  superClientPercent: number | null,
): number {
  const clientPercent =
    feeMode === 'super' && superClientPercent != null
      ? superClientPercent
      : DEFAULT_CONCIERGE_PERCENT;
  return Math.max(0, clientPercent - marketplaceFeePercent);
}

// ==================== STATUS TO TAB MAPPING ====================

/** Maps a ticket status to the appropriate tab name for auto-navigation */
function getTabForStatus(status: string): string {
  if (!status) return 'Verification';
  const normalizedStatus = (status || '').toLowerCase().replace(/\s/g, '');
  
  // Verification Tab
  if (normalizedStatus === 'tobeverified' || normalizedStatus === 'verified') {
    return 'Verification';
  }
  
  // Listed Tab
  if (normalizedStatus === 'list') {
    return 'Listed';
  }
  
  // Sold Tab
  if (normalizedStatus === 'sold') {
    return 'Sold';
  }
  
  if (normalizedStatus === 'invalid') {
    return 'Verification';
  }

  if (normalizedStatus === 'listingrequestcancelled') {
    return 'Listing Request Cancelled';
  }

  // Delisted Tab
  if (
    normalizedStatus === 'delist' ||
    normalizedStatus === 'delistinprogress' ||
    normalizedStatus === 'delistrequested' ||
    normalizedStatus === 'return'
  ) {
    return 'Delisted';
  }
  
  // Unsold Tab
  if (normalizedStatus === 'unsold') {
    return 'Unsold';
  }
  
  // Listing Expired Tab
  if (normalizedStatus === 'closed') {
    return 'Listing Expired';
  }
  
  // Deleted Tab
  if (normalizedStatus === 'deleted') {
    return 'Deleted';
  }
  
  return 'Verification';
}

const VERIFICATION_STATUSES = new Set(['ToBeVerified', 'Verified']);

/** Higher rank = further along the listing lifecycle; server wins when ahead of a stale override. */
const STATUS_PROGRESS_RANK: Record<string, number> = {
  ListingRequestCancelled: -1,
  Invalid: -1,
  ToBeVerified: 0,
  Verified: 1,
  List: 2,
  Unsold: 2,
  DelistInProgress: 2,
  DelistRequested: 2,
  Delist: 2,
  Return: 2,
  Sold: 3,
  Closed: 4,
};

const getStatusProgressRank = (status: string): number =>
  STATUS_PROGRESS_RANK[status] ?? -999;

const filterVerificationTickets = (tickets: Ticket[]): Ticket[] =>
  tickets.filter(
    (ticket) =>
      VERIFICATION_STATUSES.has(ticket.tp_status || '') &&
      !isFailToTransferTicket(ticket),
  );

/** Keep optimistic status visible until server/refetch returns the same status. */
const applyStatusOverrides = (tickets: Ticket[], overrides: Record<string, string>): Ticket[] =>
  tickets.map(ticket => {
    const overrideStatus = overrides[ticket.tp_id];
    if (!overrideStatus) return ticket;
    const serverStatus = ticket.tp_status || '';
    if (serverStatus === overrideStatus) return ticket;
    if (getStatusProgressRank(serverStatus) > getStatusProgressRank(overrideStatus)) {
      return ticket;
    }
    return {
      ...ticket,
      tp_status: overrideStatus,
      tp_updated_at: new Date().toISOString(),
    };
  });

const reconcileStatusOverrides = (
  overrides: Record<string, string>,
  serverTickets: Ticket[],
): void => {
  Object.keys(overrides).forEach(tpId => {
    const override = overrides[tpId];
    const serverTicket = serverTickets.find(t => t.tp_id === tpId);
    if (!serverTicket) return;
    const serverStatus = serverTicket.tp_status || '';
    if (
      serverStatus === override ||
      getStatusProgressRank(serverStatus) > getStatusProgressRank(override)
    ) {
      delete overrides[tpId];
    }
  });
};

type EventTicketGroupKey = 'pending' | 'listed' | 'sold' | 'delisted' | 'deleted';

const EVENT_TICKET_VIEW_SECTIONS: { key: EventTicketGroupKey; label: string; tab: string }[] = [
  { key: 'pending', label: 'Pending', tab: 'Verification' },
  { key: 'listed', label: 'Listed', tab: 'Listed' },
  { key: 'sold', label: 'Sold', tab: 'Sold' },
  { key: 'delisted', label: 'Delisted', tab: 'Delisted' },
  { key: 'deleted', label: 'Deleted', tab: 'Deleted' },
];

const createEmptyEventGroupedTickets = (): Record<EventTicketGroupKey, Ticket[]> => ({
  pending: [] as Ticket[],
  listed: [] as Ticket[],
  sold: [] as Ticket[],
  delisted: [] as Ticket[],
  deleted: [] as Ticket[],
});

const mapDeletedPlacementRows = (rows: any[]): Ticket[] =>
  (Array.isArray(rows) ? rows : []).map((row: any) => ({
    ...row,
    e_name: row?.ticket?.event?.e_name,
    e_date: row?.ticket?.event?.e_date,
    e_address: row?.ticket?.event?.e_address,
    e_brand_name: row?.ticket?.event?.e_brand_name,
    e_status: row?.ticket?.event?.e_status,
    e_time_zone: row?.ticket?.event?.e_time_zone,
    e_date_time_zone: row?.ticket?.event?.e_date_time_zone,
    e_id: row?.ticket?.event?.e_id ?? row?.ticket?.e_id?.e_id,
    t_id: row?.ticket?.t_id,
    u_id: row?.ticket?.user?.u_id,
    u_first_name: row?.ticket?.user?.u_first_name,
    u_last_name: row?.ticket?.user?.u_last_name,
    u_full_name: row?.ticket?.user?.u_full_name,
    u_email_id: row?.ticket?.user?.u_email_id,
    u_original_email: row?.ticket?.user?.u_original_email,
    u_pricing_mode: row?.ticket?.user?.u_pricing_mode,
    tp_status: row?.tp_status || 'Deleted',
  }));

const getEventViewGroupKeyForStatus = (status: string): EventTicketGroupKey | null => {
  const normalizedStatus = (status || '').toLowerCase().replace(/\s/g, '');
  if (normalizedStatus === 'listingrequestcancelled') return 'delisted';
  const tab = getTabForStatus(status);
  if (tab === 'Verification') return 'pending';
  if (tab === 'Listed') return 'listed';
  if (tab === 'Sold') return 'sold';
  if (tab === 'Delisted') return 'delisted';
  if (tab === 'Deleted') return 'deleted';
  return null;
};

const rebucketEventGroupedTickets = (
  prev: Record<EventTicketGroupKey, Ticket[]>,
  tpIds: string[],
  nextStatus: string,
  nextListPrice?: number,
): Record<EventTicketGroupKey, Ticket[]> => {
  const targetKey = getEventViewGroupKeyForStatus(nextStatus);
  if (!targetKey) return prev;

  const moving = new Set(tpIds);
  const next = createEmptyEventGroupedTickets();

  for (const section of EVENT_TICKET_VIEW_SECTIONS) {
    for (const ticket of prev[section.key] ?? []) {
      if (!moving.has(ticket.tp_id)) {
        next[section.key].push(ticket);
        continue;
      }
      next[targetKey].push({
        ...ticket,
        tp_status: nextStatus,
        tp_list_price: nextListPrice !== undefined ? nextListPrice : ticket.tp_list_price,
        tp_updated_at: new Date().toISOString(),
      });
    }
  }

  for (const key of EVENT_TICKET_VIEW_SECTIONS.map(s => s.key)) {
    next[key] = sortManageTickets(next[key]);
  }
  return next;
};

const updateEventGroupedTickets = (
  prev: Record<EventTicketGroupKey, Ticket[]>,
  tpIds: string[],
  nextStatus: string,
  priceMap: Record<string, number>,
): Record<EventTicketGroupKey, Ticket[]> => {
  const moving = new Set(tpIds);
  const next = createEmptyEventGroupedTickets();
  const now = new Date().toISOString();

  for (const section of EVENT_TICKET_VIEW_SECTIONS) {
    for (const ticket of prev[section.key] ?? []) {
      const updatedTicket = moving.has(ticket.tp_id)
        ? {
            ...ticket,
            tp_status: nextStatus,
            tp_list_price: priceMap[ticket.tp_id] ?? ticket.tp_list_price,
            tp_updated_at: now,
          }
        : ticket;
      const targetKey = getEventViewGroupKeyForStatus(updatedTicket.tp_status || '');
      if (targetKey) {
        next[targetKey].push(updatedTicket);
      }
    }
  }

  for (const key of EVENT_TICKET_VIEW_SECTIONS.map(s => s.key)) {
    next[key] = sortManageTickets(next[key]);
  }
  return next;
};

const distributeEventGroupedTickets = (tickets: Ticket[]): Record<EventTicketGroupKey, Ticket[]> => {
  const byId = new Map<string, Ticket>();
  for (const ticket of tickets) {
    byId.set(ticket.tp_id, ticket);
  }
  const next = createEmptyEventGroupedTickets();
  for (const ticket of byId.values()) {
    const key = getEventViewGroupKeyForStatus(ticket.tp_status || '');
    if (key) next[key].push(ticket);
  }
  for (const section of EVENT_TICKET_VIEW_SECTIONS) {
    next[section.key] = sortManageTickets(next[section.key]);
  }
  return next;
};

const hasActiveEventSearch = (search: string) => search.trim().length >= 2;

const isLocalStatusFilterTab = (tab: string) =>
  tab === 'Sold' || tab === 'Delisted' || tab === 'Verification' || tab === 'Listed';

const isDefaultLocalStatusFilter = (tab: string, filterStatus: string): boolean => {
  switch (tab) {
    case 'Verification':
      return !filterStatus || filterStatus === 'All Verification';
    case 'Listed':
      return !filterStatus || filterStatus === 'All Listed';
    case 'Sold':
      return !filterStatus || filterStatus === 'All Sold';
    case 'Delisted':
      return !filterStatus || filterStatus === 'All Delisted';
    default:
      return true;
  }
};

const EVENT_GROUP_TABS = new Set([
  'Verification',
  'Listed',
  'Sold',
  'Delisted',
  'Listing Request Cancelled',
  'Deleted',
]);

const isEventGroupedTab = (tab: string): boolean => EVENT_GROUP_TABS.has(tab);

const getEventGroupedTicketsForTab = (
  tab: string,
  grouped: Record<EventTicketGroupKey, Ticket[]>,
): Ticket[] => {
  switch (tab) {
    case 'Verification':
      return grouped.pending;
    case 'Listed':
      return grouped.listed;
    case 'Sold':
      return grouped.sold;
    case 'Delisted':
      return grouped.delisted.filter(
        (ticket) => ticket.tp_status !== 'ListingRequestCancelled',
      );
    case 'Listing Request Cancelled':
      return grouped.delisted.filter(
        (ticket) => ticket.tp_status === 'ListingRequestCancelled',
      );
    case 'Deleted':
      return grouped.deleted;
    default:
      return [];
  }
};

/** Read tab-badge counts from aggregate fields bundled in any ticket-tab GraphQL response. */
const extractSiblingTabCountsFromResponse = (responseData: any): Record<string, number> => {
  const counts: Record<string, number> = {};
  const assign = (tab: string, value: unknown) => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
      counts[tab] = value;
    }
  };

  // Verification counts are filtered client-side (fail-to-transfer rows are excluded),
  // so do not populate them from sibling tab responses where the aggregate count may be stale.
  assign('Listed', responseData?.filterlisttickets_aggregate?.aggregate?.count);
  assign('Sold', responseData?.FilterSoldTickets_aggregate?.aggregate?.count);
  assign('Delisted', responseData?.FilterDelisted_allTab_aggregate?.aggregate?.count ?? responseData?.filterreturntickets_aggregate?.aggregate?.count);
  assign(
    'Unsold',
    responseData?.filterUnsoldTickets_aggreagate?.aggregate?.count ??
      responseData?.filterUnsoldTickets?.aggregate?.count,
  );
  assign(
    'Listing Request Cancelled',
    responseData?.filterListingRequestCancelledTickets_aggregate?.aggregate?.count,
  );

  const expiredCount =
    responseData?.closed_placement_aggregate?.aggregate?.count ??
    (responseData?.filterreturntickets != null && responseData?.closed_orphan_placements != null
      ? responseData?.filterreturntickets_aggregate?.aggregate?.count
      : undefined);
  assign('Listing Expired', expiredCount);

  // Deleted tab count - from GET_DELETED_TICKETS query or GET_MANAGE_TICKETS deletedCount
  assign('Deleted', 
    responseData?.ticket_placement_aggregate?.aggregate?.count ?? 
    responseData?.deletedCount?.aggregate?.count
  );

  return counts;
};

/** Sold sub-filter counts from DB aggregates (no need to load all rows). */
const extractSoldStatusCountsFromResponse = (responseData: any): Record<string, number> => {
  const sold = responseData?.FilterSoldTickets_soldOpen_aggregate?.aggregate?.count ?? 0;
  const settled = responseData?.FilterSoldTickets_settled_aggregate?.aggregate?.count ?? 0;
  const inProgress = responseData?.FilterSoldTickets_inProgress_aggregate?.aggregate?.count ?? 0;
  const failed = responseData?.FilterSoldTickets_failed_aggregate?.aggregate?.count ?? 0;
  const allFromParts = sold + settled + inProgress + failed;
  const allFromAggregate = responseData?.FilterSoldTickets_aggregate?.aggregate?.count;
  return {
    'All Sold': allFromParts > 0 ? allFromParts : (allFromAggregate ?? 0),
    Sold: sold,
    Settled: settled,
    SettlementInProgress: inProgress,
    Failed: failed,
  };
};

const extractVerificationStatusCountsFromResponse = (responseData: any): Record<string, number> => {
  const toBeVerified = responseData?.FilterManageTickets_toBeVerified_aggregate?.aggregate?.count ?? 0;
  const verified = responseData?.FilterManageTickets_verified_aggregate?.aggregate?.count ?? 0;
  const allFromParts = toBeVerified + verified;
  const allFromAggregate = responseData?.filtermanagetickets_aggregate?.aggregate?.count;
  return {
    'All Verification': allFromParts > 0 ? allFromParts : (allFromAggregate ?? 0),
    ToBeVerified: toBeVerified,
    Verified: verified,
  };
};

const extractDelistedStatusCountsFromResponse = (responseData: any): Record<string, number> => {
  const delist = responseData?.FilterDelisted_delist_aggregate?.aggregate?.count ?? 0;
  const inProgress = responseData?.FilterDelisted_delistInProgress_aggregate?.aggregate?.count ?? 0;
  const returned = responseData?.FilterDelisted_return_aggregate?.aggregate?.count ?? 0;
  const allFromParts = delist + inProgress + returned;
  const allFromAggregate =
    responseData?.FilterDelisted_allTab_aggregate?.aggregate?.count ??
    responseData?.filterreturntickets_aggregate?.aggregate?.count;
  return {
    'All Delisted': allFromParts > 0 ? allFromParts : (allFromAggregate ?? 0),
    Delist: delist,
    DelistInProgress: inProgress,
    Return: returned,
  };
};

const ALL_DELISTED_STATUSES = ['Delist', 'DelistInProgress', 'DelistRequested', 'Return'];

const getDelistedStatusesForQuery = (filterStatus: string): string[] => {
  switch (filterStatus) {
    case 'Delist':
      return ['Delist'];
    case 'DelistInProgress':
      return ['DelistInProgress', 'DelistRequested'];
    case 'Return':
      return ['Return'];
    default:
      return ALL_DELISTED_STATUSES;
  }
};

/** When true, the active tab query uses pageSize/pageOffset against the DB instead of loading up to 5000 rows. */
const shouldPreferServerPagination = (ctx: {
  isEventGroupedView: boolean;
  isMailParserTpLookupView: boolean;
  isMailParserErrorView: boolean;
  isMarketingUser: boolean;
  effectiveTab: string;
}): boolean => {
  if (ctx.isEventGroupedView || ctx.isMailParserTpLookupView || ctx.isMailParserErrorView) return false;
  if (ctx.isMarketingUser) return false;
  // Verification applies fail-to-transfer filtering client-side after fetch; server pages mis-count rows.
  if (ctx.effectiveTab === 'Verification') return false;
  return true;
};

/** Tabs that still fetch a large result set and paginate in the browser. */
const usesClientSidePaginationForTab = (
  tab: string,
  ctx: { isMailParserErrorView: boolean; isMarketingUser: boolean },
) => {
  if (ctx.isMailParserErrorView || ctx.isMarketingUser) return true;
  if (tab === 'Verification') return true;
  return false;
};

const getSoldPaymentStatusForQuery = (filterStatus: string): string | null => {
  switch (filterStatus) {
    case 'Sold':
      return 'NotInitiated';
    case 'Settled':
      return 'Success';
    case 'SettlementInProgress':
      return 'Inprogress';
    case 'Failed':
      return 'Failed';
    default:
      return null;
  }
};

const STATUS_FILTER_LABELS: Record<string, string> = {
  Sold: 'Sold',
  Settled: 'Settled',
  SettlementInProgress: 'Settlement In Progress',
  Failed: 'Failed',
  DelistInProgress: 'Delist Requested',
  Delist: 'Delisted',
  Return: 'Returned',
};


// ==================== CSV EXPORT UTILITIES ====================

const escapeCSV = (field: any): string => {
  if (field === null || field === undefined) return '""';
  const stringField = String(field);
  if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
    return `"${stringField.replace(/"/g, '""')}"`;
  }
  return stringField;
};
const formatDateForCSV = (
  dateString: string,
  format: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD' = 'MM/DD/YYYY'
): string => {
  if (!dateString) return 'N/A';
  try {
    // Parse the date (works with ISO, timestamp, or plain date strings)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    let formatted = '';
    switch (format) {
      case 'DD/MM/YYYY':
        formatted = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
        break;
      case 'YYYY-MM-DD':
        formatted = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        break;
      case 'MM/DD/YYYY':
      default:
        formatted = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    }

    // Force Excel to treat it as text to avoid ##### and auto-conversion
    return `="${formatted}"`;
  } catch {
    return 'N/A';
  }
};

const normalizeClientTypeLabel = (ticket: any): string => {
  const rawType = String(
    ticket?.u_pricing_mode || ticket?.user?.u_pricing_mode || ticket?.u_client_type || ''
  ).trim().toLowerCase();
  if (rawType === 'self-pricing') return 'Self Pricing';
  if (rawType === 'concierge') return 'Concierge';
  return '';
};

const getTicketCsvNotes = (ticket: any, noteType: 'internal' | 'external'): string => {
  const fieldName = noteType === 'internal' ? 'internalNotes' : 'externalNotes';
  const directValue = ticket?.[fieldName] ?? ticket?.[`${fieldName.toLowerCase()}`];

  if (typeof directValue === 'string') return directValue;
  if (Array.isArray(directValue)) {
    return directValue
      .map((note) =>
        typeof note === 'string'
          ? note
          : note?.content || note?.n_content || note?.noteContent || ''
      )
      .filter(Boolean)
      .join(' | ');
  }

  const noteList = ticket?.user?.user_notes ?? ticket?.user?.notes ?? ticket?.notes;
  if (Array.isArray(noteList)) {
    return noteList
      .filter((note: any) =>
        String(note?.n_type || note?.noteType || '').toLowerCase() === noteType
      )
      .map((note: any) => note?.n_content || note?.noteContent || note?.content || '')
      .filter(Boolean)
      .join(' | ');
  }

  return '';
};

const parseSeatNumbersForExport = (seatNo: string | number | null | undefined): string[] => {
  if (!seatNo) return ['N/A'];
  return parseAndSortSeatNumbers(seatNo);
};

/** Sales rep commission = QuickAsyst quick-cut × rep commission rate %. */
const getSalesRepCommission = (ticket: Ticket): number => {
  const quickCut = Number(ticket.tp_quick_cut_amount ?? 0);
  const rate = Number(ticket.ticket_user?.sales_reps?.sr_commission_rate ?? 0);
  if (!Number.isFinite(quickCut) || quickCut <= 0 || !Number.isFinite(rate) || rate <= 0) {
    return 0;
  }
  return Math.round(((quickCut * rate) / 100) * 100) / 100;
};

const exportToCSV = (
  tickets: Ticket[],
  options: {
    filename?: string;
    includeHeaders?: boolean;
    includeSeatDetails?: boolean;
    includeReturnEmail?: boolean;
    includeCommission?: boolean;
    dateFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
    delimiter?: ',' | ';' | '\t';
  } = {}
) => {
  if (!tickets || tickets.length === 0) {
    const { error } = require('../../components/shared-components/toaster').default();
    error('No tickets to export');
    return;
  }

  const {
    filename = 'tickets_export',
    includeHeaders = true,
    includeSeatDetails = false,
    includeReturnEmail = false,
    includeCommission = false,
    dateFormat = 'MM/DD/YYYY',
    delimiter = ','
  } = options;

  const rows: string[] = [];
  const baseHeaders = [
    'Event Name', 'Event Date', 'Event Time', 'Venue', 'Section', 'Row', 'Seat Numbers',
    'Status', 'Client Name', 'Client Email',
    ...(includeReturnEmail ? ['Return Email'] as const : []),
    'Client Type', 'Internal Notes', 'External Notes',
    'Price',
    ...(includeCommission ? ['Commission'] as const : []),
    'Listed Date', 'Ticket ID', 'League'
  ];
  const headers = includeSeatDetails ? [...baseHeaders, 'Individual Seats'] : baseHeaders;

  if (includeHeaders) {
    rows.push(headers.join(delimiter));
  }

  // Debug: log first ticket notes structure to help identify missing note values during export
  if (tickets && tickets.length > 0) {
    const t0: any = tickets[0];
    // eslint-disable-next-line no-console
    console.log('CSV export debug - ticket[0] user:', t0?.user, 'user_notes:', t0?.user?.user_notes, 'internalNotes:', t0?.internalNotes, 'externalNotes:', t0?.externalNotes);
  }

  tickets.forEach(ticket => {
    const clientTypeLabel = normalizeClientTypeLabel(ticket);
    const internalNotes = getTicketCsvNotes(ticket, 'internal');
    const externalNotes = getTicketCsvNotes(ticket, 'external');
    const eventTime = formatEventTimeForCsv(ticket);

    if (includeSeatDetails) {
      const seatNumbers = parseSeatNumbersForExport(ticket.tp_seat_no);
      seatNumbers.forEach(seatNum => {
        const row = [
          escapeCSV(ticket.e_name || 'N/A'),
          escapeCSV(formatDateForCSV(ticket.e_date, dateFormat)),
          escapeCSV(eventTime),
          escapeCSV(ticket.e_address || 'N/A'),
          escapeCSV(ticket.tp_section || 'N/A'),
          escapeCSV(ticket.tp_row || 'N/A'),
          escapeCSV(seatNum),
          escapeCSV(ticket.tp_status || 'Unknown'),
          escapeCSV(ticket.u_full_name || `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() || 'N/A'),
          escapeCSV(ticket.u_email_id || 'N/A'),
          ...(includeReturnEmail
            ? [escapeCSV(ticket.tp_delist_requested_email?.trim() || '')]
            : []),
          escapeCSV(clientTypeLabel || ''),
          escapeCSV(internalNotes),
          escapeCSV(externalNotes),
          ticket.tp_list_price || 0,
          ...(includeCommission ? [getSalesRepCommission(ticket)] : []),
          escapeCSV(formatDateForCSV(ticket.e_date, dateFormat)),
          escapeCSV(ticket.tp_id || 'N/A'),
          escapeCSV((ticket as any).league_name || 'N/A'),
          escapeCSV(seatNum)
        ];
        rows.push(row.join(delimiter));
      });
    } else {
      const row = [
        escapeCSV(ticket.e_name || 'N/A'),
        escapeCSV(formatDateForCSV(ticket.e_date, dateFormat)),
        escapeCSV(eventTime),
        escapeCSV(ticket.e_address || 'N/A'),
        escapeCSV(ticket.tp_section || 'N/A'),
        escapeCSV(ticket.tp_row || 'N/A'),
        escapeCSV(ticket.tp_seat_no || 'N/A'),
        escapeCSV(ticket.tp_status || 'Unknown'),
        escapeCSV(ticket.u_full_name || `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() || 'N/A'),
        escapeCSV(ticket.u_email_id || 'N/A'),
        ...(includeReturnEmail
          ? [escapeCSV(ticket.tp_delist_requested_email?.trim() || '')]
          : []),
        escapeCSV(clientTypeLabel || ''),
        escapeCSV(internalNotes),
        escapeCSV(externalNotes),
        ticket.tp_list_price || 0,
        ...(includeCommission ? [getSalesRepCommission(ticket)] : []),
        escapeCSV(formatDateForCSV(ticket.e_date, dateFormat)),
        escapeCSV(ticket.tp_id || 'N/A'),
        escapeCSV((ticket as any).league_name || 'N/A')
      ];
      rows.push(row.join(delimiter));
    }
  });

  const csvString = rows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const extension = delimiter === '\t' ? 'tsv' : 'csv';
  const timestamp = new Date().toISOString().split('T')[0];
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}_${timestamp}.${extension}`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const shouldIncludeReturnEmailInExport = (tab: string) =>
  tab === 'Delisted' || tab === 'Listing Request Cancelled';

const shouldIncludeCommissionInExport = (tab: string) => tab === 'Sold';

const formatEventTimeForCsv = (ticket: any): string => {
  if (!ticket?.e_date) return '';

  try {
    // Convert PostgreSQL datetime format to ISO format
    const formattedDate = ticket.e_date
      .replace(' ', 'T')
      .replace(' +', '+');

    const parsed = new Date(formattedDate);

    if (isNaN(parsed.getTime())) return '';

    return parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return '';
  }
};
// ==================== MAIN COMPONENT ====================

interface ManageTicketsProps {
  onPriceChange?: (tpIds: string[], price: number) => Promise<void>;
}

interface SalesRepLookupResponse {
  sales_reps: Array<{
    sr_id: string;
    sr_email: string;
  }>;
}

const ManageTickets: React.FC<ManageTicketsProps> = () => {
  // Status dropdown state for table rows
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);

  // Allow only numbers in normal and decimal format for price/amount inputs
  const sanitizePriceInput = (value: string): string => {
    let sanitized = value.replace(/[^0-9.]/g, '');
    const firstDecimalIndex = sanitized.indexOf('.');
    if (firstDecimalIndex !== -1) {
      sanitized =
        sanitized.slice(0, firstDecimalIndex + 1) +
        sanitized.slice(firstDecimalIndex + 1).replace(/\./g, '');
    }
    return sanitized;
  };

  // Extracted status transitions for reuse
const getStatusTransitions = (currentStatus: string) => {
  // Normalize status value first to ensure consistent matching
  const normalizedStatus = currentStatus 
    ? ((currentStatus || '').toLowerCase().replace(/\s/g, ''))
    : '';
  
  const statusCanonicalMapLocal: Record<string, string> = {
    closed: 'Closed',
    delist: 'Delist',
    delistinprogress: 'DelistInProgress',
    delistrequested: 'DelistInProgress',
    deleted: 'Deleted',
    invalid: 'Invalid',
    list: 'List',
    listed: 'List',
    listingrequestcancelled: 'ListingRequestCancelled',
    return: 'Return',
    sold: 'Sold',
    settled: 'Settled',
    settlementinprogress: 'SettlementInProgress',
    tobeverified: 'ToBeVerified',
    unsold: 'Unsold',
    verified: 'Verified'
  };
  
  const canonicalStatus = statusCanonicalMapLocal[normalizedStatus] || currentStatus;
  
  const statusTransitions: { [key: string]: { value: string, label: string, color?: string, bg?: string }[] } = {
    ToBeVerified: [
      { value: 'Verified', label: 'Verified' },
      { value: 'ListingRequestCancelled', label: 'Listing Request Cancelled' },
       { value: 'Deleted', label: 'Delete' } 
    ],
    Verified: [
      { value: 'List', label: 'Listed' },
      { value: 'DelistInProgress', label: 'Delist Requested' },
      { value: 'Deleted', label: 'Delete' } 
    ],
    List: [
      // { value: 'DelistInProgress', label: 'Delist Requested' },
       { value: 'Delist', label: 'Delisted' },
      { value: 'Sold', label: 'Sold' },
      { value: 'Unsold', label: 'Unsold' },
      { value: 'Deleted', label: 'Delete' }
            
    ],
    DelistInProgress: [
      { value: 'Delist', label: 'Delist' },
      { value: 'List', label: 'Listed (Undo)' },
      { value: 'Return', label: 'Returned' }
    ],
    Delist: [
      { value: 'List', label: 'Listed (Undo)' },
      { value: 'DelistInProgress', label: 'Delist Requested' },
      { value: 'Return', label: 'Returned' },
      { value: 'Deleted', label: 'Delete' }
    ],
    Sold: [
      { value: 'Settled', label: 'Settled' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' }
    ],
    Settled: [
      { value: 'Sold', label: 'Sold' },
      { value: 'SettlementInProgress', label: 'Settlement In Progress' }
    ],
    SettlementInProgress: [
      { value: 'Sold', label: 'Sold' },
      { value: 'Settled', label: 'Settled' }
    ],
    Unsold: [
      { value: 'Sold', label: 'Sold' }
    ],
    Invalid: [
      { value: 'ToBeVerified', label: 'To Be Verified' }
    ],
    ListingRequestCancelled: [
      { value: 'ToBeVerified', label: 'To Be Verified' }
    ],
    Return: [
      
    ],
    Closed: [],
    Deleted: []
  };
  // Allow super users to delist directly from Listed (skip Delist Requested workflow)
  try {
    if (canonicalStatus === 'List' && (typeof isSuperUser !== 'undefined') && isSuperUser) {
      const existing = statusTransitions['List'] || [];
      if (!existing.some(s => s.value === 'Delist')) {
        statusTransitions['List'] = [{ value: 'Delist', label: 'Delist' }, ...existing];
      }
    }
  } catch (e) {
    // defensive: if isSuperUser isn't available at call-time, just ignore
  }
  return statusTransitions[canonicalStatus] || [];
};
  const soldPaymentStartedMessage =
    'It will update automatically when payment completes.';
  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = () => setOpenStatusDropdown(null);
    if (openStatusDropdown) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [openStatusDropdown]);

  // --- Custom Modal States for Status Transitions ---
  const [showListedModal, setShowListedModal] = useState(false);
  const [listedModalLoading, setListedModalLoading] = useState(false);
  const [showSoldModal, setShowSoldModal] = useState(false);
  const [listedModalTicket, setListedModalTicket] = useState<Ticket | null>(null);
  const [listedPrice, setListedPrice] = useState('');
  const [soldModalTicket, setSoldModalTicket] = useState<Ticket | null>(null);
  const [soldMarketplaceId, setSoldMarketplaceId] = useState('');
  const [soldLogitixAmount, setSoldLogitixAmount] = useState('0');
  const [soldFeeMode, setSoldFeeMode] = useState<'super' | 'default'>('default');
  const [soldSuperClientPercent, setSoldSuperClientPercent] = useState<number | null>(null);
  const [soldFeeConfigReady, setSoldFeeConfigReady] = useState(false);
  const [showBulkListModal, setShowBulkListModal] = useState(false);
  const [bulkListLoading, setBulkListLoading] = useState(false);
  const [bulkListedPrices, setBulkListedPrices] = useState<Record<string, string>>({});
  const [bulkListCommonPrice, setBulkListCommonPrice] = useState('');
  const [showBulkSoldModal, setShowBulkSoldModal] = useState(false);
  const [bulkSoldLoading, setBulkSoldLoading] = useState(false);
  const [bulkSoldMarketplaceId, setBulkSoldMarketplaceId] = useState('');
  const [bulkSoldLogitixAmounts, setBulkSoldLogitixAmounts] = useState<Record<string, string>>({});
  const [bulkSoldCommonLogitixAmount, setBulkSoldCommonLogitixAmount] = useState('');
  const [bulkSoldFeeConfigByTicketId, setBulkSoldFeeConfigByTicketId] = useState<Record<string, SoldFeeConfig>>({});
  const [bulkSoldFeeConfigReady, setBulkSoldFeeConfigReady] = useState(false);
  
  const navigate = useNavigate();
  const { error: showError, success, info } = ToasterWithMessage();
  const { globalState, handleGlobalState } = useContext<any>(globalContext);
  const apolloClient = useApolloClient();
  const userRole = globalState?.profile?.userRole || localStorage.getItem("x-hasura-user-role") || "";
  const isMarketingUser = false;
  const isSuperUser = userRole === roles.SUPER_USER;
  const canOverrideSettlementStatus = isStaffAdminEquivalent(
    String(localStorage.getItem("x-hasura-user-role") || userRole).toLowerCase(),
  );
  /** Self-pricing users: same four buckets as legacy New Tickets (no Verification / Listing Expired). */
  const SUPER_USER_MANAGE_TABS = ["Listed", "Sold", "Delisted", "Unsold"] as const;
  const loggedInEmail = (
    globalState?.profile?.mailId ||
    localStorage.getItem("adminMailId") ||
    ""
  ).toLowerCase().trim();
  const statusCanonicalMap: Record<string, string> = {
    closed: 'Closed',
    delist: 'Delist',
    delistinprogress: 'DelistInProgress',
    // Legacy/older records may still come with this value; treat it as DelistInProgress in UI logic.
    delistrequested: 'DelistInProgress',
    deleted: 'Deleted',
    invalid: 'Invalid',
    list: 'List',
    listed: 'List',
    listingrequestcancelled: 'ListingRequestCancelled',
    return: 'Return',
    settled: 'Settled',
    settlementinprogress: 'SettlementInProgress',
    sold: 'Sold',
    tobeverified: 'ToBeVerified',
    unsold: 'Unsold',
    verified: 'Verified'
  };

  const normalizeStatusValue = (value: string): string => {
    const raw = (value || '').trim();
    if (!raw) return '';
    const alphaOnlyKey = raw.toLowerCase().replace(/[^a-z]/g, '');
    return statusCanonicalMap[alphaOnlyKey] || '';
  };

  const getErrorMessage = (error: any, fallback: string): string => {
    const candidateMessages = [
      error?.graphQLErrors?.[0]?.message,
      error?.networkError?.result?.errors?.[0]?.message,
      error?.networkError?.result?.errors?.[0]?.extensions?.internal?.error?.message,
      error?.networkError?.result?.error,
      error?.networkError?.result?.message,
      error?.networkError?.message,
      error?.message,
    ];
    const raw = (candidateMessages.find((msg) => typeof msg === 'string' && msg.trim()) || fallback || '').toString().trim();
    if (!raw) return fallback;

    const normalized = raw.toLowerCase();
    if (normalized.includes('no matching ticket found in logitix')) {
      return 'No matching ticket found in Logitix for selected section/row/seat. Please verify ticket details.';
    }
    if (normalized.includes('no matching tickets found to update price')) {
      return 'No matching ticket found in Logitix for selected section/row/seat. Please verify ticket details.';
    }
    if (normalized.includes("we couldn't complete the delist request")) {
      return "We couldn't complete the delist request. Please verify section/row/seat or try again.";
    }
    if (normalized.includes('forbidden')) {
      return 'Operation not allowed for selected ticket(s). Please verify status/ownership and try again.';
    }
    if (normalized.includes('http exception when calling webhook')) {
      return 'Verification service is unreachable. Ensure the API server is running and Hasura can reach it (HASURA_ACTION_BASE_URL → /api/updateTicketStatus), then try again.';
    }
    if (normalized.includes('invalid ticket')) {
      return 'Invalid ticket selection. Please refresh and try again.';
    }
    if (normalized.includes('already exist') || normalized.includes('already exists')) {
      return 'Another ticket already exists with the same section, row, and seat for this event.';
    }
    return raw;
  };

 const resolveActionMessageType = (message: string): 'success' | 'info' | 'error' => {
  const normalized = (message || '').toLowerCase();
  if (!normalized) return 'info';

  const hasSuccess = normalized.includes('success') || normalized.includes('successfully');
  const hasUndoSuccess = normalized.includes('undo delist successful');
  const hasFailure = normalized.includes('fail') ||
                     normalized.includes('error') ||
                     normalized.includes("couldn't") ||
                     normalized.includes('forbidden') ||
                     normalized.includes('no matching');
  const hasNotFound = normalized.includes('not found');
  const hasZeroUpdated = normalized.includes('0 ticket') || normalized.includes('0 tickets');

  // Specific check for undo delist success
  if (hasUndoSuccess) return 'success';
  
  // Success without any failure indicators
  if (hasSuccess && !hasFailure && !hasZeroUpdated) return 'success';
  
  // Partial success
  if (hasSuccess && hasFailure) return 'info';
  
  // Pure failure
  if (hasFailure) return 'error';
  
  // Success with not found but still worked
  if (hasSuccess && hasNotFound) return 'info';
  
  if (hasNotFound) return 'error';
  
  return 'info';
}; 
  // UI States
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [statusFilterSourceTickets, setStatusFilterSourceTickets] = useState<Ticket[]>([]);
  const localStatusOverridesRef = useRef<Record<string, string>>({});

  const updateLocalTicketStatus = (tpIds: string[], nextStatus: string, nextListPrice?: number) => {
    const keepInActiveTab = (ticket: Ticket) =>
      !tpIds.includes(ticket.tp_id) ||
      hasActiveEventSearch(eventVenueSearch) ||
      getTabForStatus(nextStatus) === activeTab;

    tpIds.forEach(id => {
      localStatusOverridesRef.current[id] = nextStatus;
    });
    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(
        prevTickets
          .map(ticket =>
            tpIds.includes(ticket.tp_id)
              ? {
                  ...ticket,
                  tp_status: nextStatus,
                  tp_list_price: nextListPrice ?? ticket.tp_list_price,
                  tp_updated_at: new Date().toISOString()
                }
              : ticket
          )
          .filter(keepInActiveTab)
      )
    );
    setFilteredTickets(prevTickets =>
      sortManageTickets(
        prevTickets
          .map(ticket =>
            tpIds.includes(ticket.tp_id)
              ? {
                  ...ticket,
                  tp_status: nextStatus,
                  tp_list_price: nextListPrice ?? ticket.tp_list_price,
                  tp_updated_at: new Date().toISOString()
                }
              : ticket
          )
          .filter(keepInActiveTab)
      )
    );
    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev => rebucketEventGroupedTickets(prev, tpIds, nextStatus, nextListPrice));
    }
  };

  const updateLocalTicketStatusWithPriceMap = (
    tpIds: string[],
    nextStatus: string,
    priceMap: Record<string, number>,
  ) => {
    const now = new Date().toISOString();
    const keepInActiveTab = (ticket: Ticket) =>
      !tpIds.includes(ticket.tp_id) ||
      hasActiveEventSearch(eventVenueSearch) ||
      getTabForStatus(nextStatus) === activeTab;

    tpIds.forEach(id => {
      localStatusOverridesRef.current[id] = nextStatus;
    });

    const updateTicket = (ticket: Ticket) =>
      tpIds.includes(ticket.tp_id)
        ? {
            ...ticket,
            tp_status: nextStatus,
            tp_list_price: priceMap[ticket.tp_id] ?? ticket.tp_list_price,
            tp_updated_at: now,
          }
        : ticket;

    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(prevTickets.map(updateTicket).filter(keepInActiveTab))
    );

    setFilteredTickets(prevTickets =>
      sortManageTickets(prevTickets.map(updateTicket).filter(keepInActiveTab))
    );

    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev => updateEventGroupedTickets(prev, tpIds, nextStatus, priceMap));
    }
  };

  const updateLocalTicketDetails = (tpIds: string[], updates: Partial<Ticket>) => {
    const now = new Date().toISOString();
    const applyUpdate = (ticket: Ticket) =>
      tpIds.includes(ticket.tp_id)
        ? {
            ...ticket,
            ...updates,
            tp_updated_at: now,
          }
        : ticket;

    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(prevTickets.map(applyUpdate))
    );

    setFilteredTickets(prevTickets =>
      sortManageTickets(prevTickets.map(applyUpdate))
    );

    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev => {
        const next = createEmptyEventGroupedTickets();
        for (const section of EVENT_TICKET_VIEW_SECTIONS) {
          next[section.key] = sortManageTickets(
            (prev[section.key] ?? []).map(applyUpdate)
          );
        }
        return next;
      });
    }
  };

  const updateLocalTicketPaymentStatus = (tpIds: string[], paymentStatus: string | null) => {
    setStatusFilterSourceTickets(prevTickets =>
      sortManageTickets(
        prevTickets.map(ticket =>
          tpIds.includes(ticket.tp_id)
            ? { ...ticket, tp_payment_status: paymentStatus, tp_updated_at: new Date().toISOString() }
            : ticket
        )
      )
    );
    setFilteredTickets(prevTickets =>
      sortManageTickets(
        prevTickets.map(ticket =>
          tpIds.includes(ticket.tp_id)
            ? { ...ticket, tp_payment_status: paymentStatus, tp_updated_at: new Date().toISOString() }
            : ticket
        )
      )
    );
  };

  // Filter states
  const [filterStatus, setFilterStatus] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("x-hasura-user-role") === roles.SUPER_USER
      ? "All Listed"
      : "All Verification",
  );
  const [clientType, setClientType] = useState('All');
  const [resolvedClientTypeByUserId, setResolvedClientTypeByUserId] = useState<Record<string, string>>({});
  const [clientTypeCounts, setClientTypeCounts] = useState({ 'All': 0, 'Self Pricing': 0, 'Concierge': 0 });
  const [clientTypeCountsLoading, setClientTypeCountsLoading] = useState(false);
  const lastClientTypeCountsQueryKeyRef = useRef('');
  const [shouldLoadUsersList, setShouldLoadUsersList] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<string>('');
  const [activeTab, setActiveTab] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("x-hasura-user-role") === roles.SUPER_USER
      ? "Listed"
      : "Verification",
  );
  // Separate search fields
  const [eventVenueSearch, setEventVenueSearch] = useState("");
  const [debouncedEventVenueSearch, setDebouncedEventVenueSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [debouncedClientSearch, setDebouncedClientSearch] = useState("");
  // Date filter states (based on event date)
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Preset date filter: 'today' | 'tomorrow' | 'next7days' | null
  const [datePreset, setDatePreset] = useState<'today' | 'tomorrow' | 'next7days' | null>(null);
  const [customRangeStart, setCustomRangeStart] = useState<string>('');
  const [customRangeEnd, setCustomRangeEnd] = useState<string>('');
  const [showCustomRangeModal, setShowCustomRangeModal] = useState(false);

  // Pagination states
  const [pageOffset, setPageOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [eventGroupedByStatus, setEventGroupedByStatus] = useState<Record<EventTicketGroupKey, Ticket[]>>(
    () => createEmptyEventGroupedTickets(),
  );
  const [eventGroupedLoading, setEventGroupedLoading] = useState(false);
  const [eventGroupedUnsoldCount, setEventGroupedUnsoldCount] = useState(0);
  const [eventGroupedListingExpiredCount, setEventGroupedListingExpiredCount] = useState(0);
  const [mailParserErrorLoading, setMailParserErrorLoading] = useState(false);

  // Dropdown states
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showLeagueDropdown, setShowLeagueDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // Seat selection states
  const [selectedSeats, setSelectedSeats] = useState<{ [key: string]: boolean }>({});
  const [selectAllSeats, setSelectAllSeats] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Action menu states
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);

  // Price modal states
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [selectedTicketForPrice, setSelectedTicketForPrice] = useState<Ticket | null>(null);
  const [newPrice, setNewPrice] = useState<string>('');
  const [selectedTpIds, setSelectedTpIds] = useState<string[]>([]);
  const [cardFilter, setCardFilter] = useState<string | null>(null);
  const [selectedMarketplaceId, setSelectedMarketplaceId] = useState<string>('');
  const pendingFinalPriceRef = useRef<number>(0);
  /** When true, mutation onCompleted hooks skip per-item toasts (bulk flows show one summary toast). */
  const suppressListPublishChainToastsRef = useRef(false);
  const withSuppressedBulkMutationToasts = async <T,>(operation: () => Promise<T>): Promise<T> => {
    suppressListPublishChainToastsRef.current = true;
    try {
      return await operation();
    } finally {
      suppressListPublishChainToastsRef.current = false;
    }
  };
  /** `markAsSold`: tp_id for onCompleted (set before mutate; cleared after UI work). */
  const markAsSoldPendingTpIdRef = useRef<string | null>(null);
  /** Prevents duplicate success UI if Apollo invokes `onCompleted` more than once. */
  const markAsSoldUiHandledRef = useRef(false);
  const refreshTicketsTableRef = useRef<() => void | Promise<unknown>>(() => undefined);
  const processQueryDataForTableRef = useRef<(responseData: any, tab: string) => void>(() => undefined);
  const queryFetchIdRef = useRef(0);
  const prevTicketsQueryKeyRef = useRef('');
  const effectiveTabRef = useRef(activeTab);
  const hasAppliedActiveTabDataRef = useRef(false);
  const eventGroupedFetchIdRef = useRef(0);
  const refetch = useCallback(() => {
    refreshTicketsTableRef.current?.();
  }, []);

  // Card detail modal states
  const [showCardDetailModal, setShowCardDetailModal] = useState<string | null>(null);
  const showCardDetailModalRef = useRef<string | null>(null);
  const [cardDetailTickets, setCardDetailTickets] = useState<Ticket[]>([]);
  const [cardDetailLoading, setCardDetailLoading] = useState(false);
  const cardDetailRequestIdRef = useRef(0);
// Add these with your other state declarations (around line 300)
const [bulkSelectedTicketIds, setBulkSelectedTicketIds] = useState<Set<string>>(new Set());
const [bulkActionType, setBulkActionType] = useState<string>('');
const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
const [bulkSelectedTickets, setBulkSelectedTickets] = useState<Ticket[]>([]);
const [bulkNewStatus, setBulkNewStatus] = useState('');
const [bulkStatusListingPrices, setBulkStatusListingPrices] = useState<Record<string, string>>({});
const [bulkStatusListingCommonPrice, setBulkStatusListingCommonPrice] = useState('');
const [bulkStatusChangeLoading, setBulkStatusChangeLoading] = useState(false);
// Bulk price change states
const [showBulkPriceModal, setShowBulkPriceModal] = useState(false);
const [bulkPriceCommon, setBulkPriceCommon] = useState('');
const [bulkPriceIndividual, setBulkPriceIndividual] = useState<Record<string, string>>({});
const [bulkPriceLoading, setBulkPriceLoading] = useState(false);
/** Table overlay + blocks interaction while any status API is in flight. */
const [statusTableBusyCount, setStatusTableBusyCount] = useState(0);
const [statusTableBusyMessage, setStatusTableBusyMessage] = useState('Updating ticket status…');

const beginStatusTableBusy = (message = 'Updating ticket status…') => {
  setStatusTableBusyMessage(message);
  setStatusTableBusyCount((count) => count + 1);
};

const endStatusTableBusy = () => {
  setStatusTableBusyCount((count) => Math.max(0, count - 1));
};

const bumpTabCountsRefreshRef = useRef<() => void>(() => {});

const syncTicketsAfterStatusChange = async () => {
  const refresh = refreshTicketsTableRef.current;
  const tabForRefresh = effectiveTabRef.current;
  if (refresh) {
    const result = await refresh();
    if (result && typeof result === 'object' && 'data' in result && result.data) {
      processQueryDataForTableRef.current?.(result.data, tabForRefresh);
    }
  }
  await fetchTrackingCounts();
  bumpTabCountsRefreshRef.current();
  await fetchDeletedCount(); 
};
  useEffect(() => {
    showCardDetailModalRef.current = showCardDetailModal;
  }, [showCardDetailModal]);

  /** Fail-to-transfer modal: remove row + count immediately so UI does not wait on fetchTrackingCounts (auto-expire, etc.). */
  const removeTicketFromFailTransferModal = (tpId: string) => {
    if (showCardDetailModalRef.current !== 'FAILED_TRANSFER') return;
    setCardDetailTickets(prev => prev.filter(t => t.tp_id !== tpId));
    setTrackingCounts(prev => ({
      ...prev,
      failedTransfer: Math.max(0, (prev.failedTransfer ?? 0) - 1),
    }));
    setFailedTransferTicketIds(prev => {
      const next = new Set(prev);
      next.delete(tpId);
      return next;
    });
  };

  /** Optimistic row patch while Fail-to-transfer modal is open (revert on API error). */
  const patchFailTransferModalRow = (tpId: string, patch: Partial<Ticket>) => {
    if (showCardDetailModalRef.current !== 'FAILED_TRANSFER') return;
    setCardDetailTickets(prev =>
      prev.map(t => (t.tp_id === tpId ? { ...t, ...patch } : t))
    );
  };

  // Edit ticket modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicketForEdit, setSelectedTicketForEdit] = useState<Ticket | null>(null);
  const [editFormData, setEditFormData] = useState({
    section: '',
    row: '',
    seatNumbers: ''
  });

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedTicketForEdit(null);
    setEditFormData({ section: '', row: '', seatNumbers: '' });
  };

  const handleEditModalBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      closeEditModal();
    }
  };

  // Status change modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [selectedTicketForStatus, setSelectedTicketForStatus] = useState<Ticket | null>(null);
  const [newStatus, setNewStatus] = useState('');

  // Delete confirmation modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedTicketForDelete, setSelectedTicketForDelete] = useState<Ticket | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setSelectedTicketForStatus(null);
    setNewStatus('');
    setStatusChangeLoading(false);
  };

  // View details modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTicketForView, setSelectedTicketForView] = useState<Ticket | null>(null);

  // Export modal states
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeHeaders: true,
    includeSeatDetails: false,
    dateFormat: 'MM/DD/YYYY' as 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD',
    delimiter: ',' as ',' | ';' | '\t'
  });

  // Tracking card counts
  const [trackingCounts, setTrackingCounts] = useState({
    listingExpired: 0,
    failedTransfer: 0
  });

  // Set of tp_ids that are in "failed transfer" state (ToBeVerified 48+ hrs)
  const [failedTransferTicketIds, setFailedTransferTicketIds] = useState<Set<string>>(new Set<string>());

  // Tab counts — populated from aggregate fields already returned by each ticket query
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({
    Verification: 0,
    Verified: 0,
    Listed: 0,
    Sold: 0,
    Delisted: 0,
    'Listing Request Cancelled': 0,
    'Listing Expired': 0,
    Unsold: 0,
    Deleted: 0
  });
  const [tabCountsRefreshNonce, setTabCountsRefreshNonce] = useState(0);

  useEffect(() => {
    bumpTabCountsRefreshRef.current = () => setTabCountsRefreshNonce((nonce) => nonce + 1);
  }, []);
// Add these helper functions after your existing helper functions (around line 800)

const getSoldStatusFilterValue = (ticket: Ticket): 'Sold' | 'Settled' | 'SettlementInProgress' | 'Failed' | null => {
  if (normalizeStatusValue(ticket.tp_status || '') !== 'Sold') return null;
  if (ticket.tp_payment_status === 'Success') return 'Settled';
  if (ticket.tp_payment_status === 'Inprogress') return 'SettlementInProgress';
  if (ticket.tp_payment_status === 'Failed') return 'Failed';
  return 'Sold';
};

const getTicketTransitionStatus = (ticket: Ticket): string => {
  if (normalizeStatusValue(ticket.tp_status || '') !== 'Sold') {
    return ticket.tp_status || '';
  }
  const soldFilterValue = getSoldStatusFilterValue(ticket);
  if (soldFilterValue === 'Failed') return 'Sold';
  return soldFilterValue ?? 'Sold';
};

const getPaymentStatusForSettlementOverride = (status: string): string | null | undefined => {
  const normalized = normalizeStatusValue(status);
  if (normalized === 'SettlementInProgress') return 'Inprogress';
  if (normalized === 'Settled') return 'Success';
  if (normalized === 'Sold') return null;
  return undefined;
};

const getDelistedStatusFilterValue = (ticket: Ticket): 'DelistInProgress' | 'Delist' | 'Return' | null => {
  const status = ticket.tp_status || '';
  if (status === 'Delist' || status === 'Return') return status;
  if (status === 'DelistInProgress' || status === 'DelistRequested') return 'DelistInProgress';
  return null;
};

const getLocalStatusFilterValue = (ticket: Ticket, tab: string): string | null => {
  if (tab === 'Verification') {
    if (ticket.tp_status === 'ToBeVerified') return 'ToBeVerified';
    if (ticket.tp_status === 'Verified') return 'Verified';
    return null;
  }
  if (tab === 'Listed') {
    return ticket.tp_status === 'List' ? 'List' : null;
  }
  if (tab === 'Sold') return getSoldStatusFilterValue(ticket);
  if (tab === 'Delisted') return getDelistedStatusFilterValue(ticket);
  return null;
};

const applyLocalStatusFilters = (tickets: Ticket[], tab: string, selectedFilters: string[]): Ticket[] => {
  if (!isLocalStatusFilterTab(tab) || !selectedFilters.length) return tickets;

  const selected = new Set(selectedFilters);
  return tickets.filter((ticket) => {
    const filterValue = getLocalStatusFilterValue(ticket, tab);
    return filterValue ? selected.has(filterValue) : false;
  });
};

const getDefaultLocalStatusFilterByTab = (tab: string): string => {
  switch (tab) {
    case 'Verification': return 'All Verification';
    case 'Listed': return 'All Listed';
    case 'Sold': return 'All Sold';
    case 'Delisted': return 'All Delisted';
    default: return '';
  }
};

const getLocalStatusFiltersFromFilterStatus = (tab: string, filterStatus: string): string[] => {
  if (!isLocalStatusFilterTab(tab)) return [];
  if (!filterStatus || filterStatus === getDefaultLocalStatusFilterByTab(tab)) return [];
  return [filterStatus];
};

/** Server-paginated tabs only load one page in memory; dropdown counts must use aggregate totals. */
const applyAggregateStatusCounts = (
  counts: Record<string, number>,
  tab: string,
  totalCount: number,
): Record<string, number> => {
  const next = { ...counts };
  switch (tab) {
    case 'Verification':
      next['All Verification'] = totalCount;
      break;
    case 'Listed':
      next['All Listed'] = totalCount;
      break;
    case 'Sold':
      next['All Sold'] = totalCount;
      break;
    case 'Delisted':
      next['All Delisted'] = totalCount;
      break;
    case 'Unsold':
      next['All Unsold'] = totalCount;
      next['Unsold'] = totalCount;
      break;
    case 'Listing Request Cancelled':
      next['All Listing Request Cancelled'] = totalCount;
      next['ListingRequestCancelled'] = totalCount;
      break;
    default:
      break;
  }
  return next;
};

// Check if tickets can be bulk updated (same event, section, row)
const canBulkUpdateTickets = (ticketIds: string[]): { allowed: boolean; reason?: string; groupedTickets?: Map<string, Ticket[]> } => {
  if (ticketIds.length === 0) {
    return { allowed: false, reason: 'No tickets selected' };
  }
  
  const selectedTicketsList = ticketIds
    .map((id) =>
      filteredTickets.find((ticket) => ticket.tp_id === id) ??
      statusFilterSourceTickets.find((ticket) => ticket.tp_id === id),
    )
    .filter((ticket): ticket is Ticket => Boolean(ticket));
  
  // Group by event_id, section, row
  const groupKey = (ticket: Ticket) => `${ticket.e_id}|${(ticket.tp_section || '').toLowerCase().trim()}|${(ticket.tp_row || '').toLowerCase().trim()}`;
  const groups = new Map<string, Ticket[]>();
  
  for (const ticket of selectedTicketsList) {
    const key = groupKey(ticket);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key)!.push(ticket);
  }
  
  if (groups.size > 1) {
    return { 
      allowed: false, 
      reason: `Tickets must be from the same event, section, and row. Found ${groups.size} different groups.`,
      groupedTickets: groups
    };
  }
  
  return { allowed: true, groupedTickets: groups };
};

// Get available status transitions for bulk action
const getBulkStatusTransitions = (tickets: Ticket[]): { value: string; label: string }[] => {
  if (tickets.length === 0) return [];
  
  // Get all possible statuses from all tickets
  const allTransitions = new Map<string, { value: string; label: string }>();
  
  for (const ticket of tickets) {
    const transitions = getStatusTransitions(getTicketTransitionStatus(ticket));
    for (const trans of transitions) {
      if (!allTransitions.has(trans.value)) {
        allTransitions.set(trans.value, trans);
      }
    }
  }
  
  // Remove transitions that are not common to all tickets
  const result: { value: string; label: string }[] = [];
  for (const [value, trans] of allTransitions) {
    let isCommon = true;
    for (const ticket of tickets) {
      const ticketTransitions = getStatusTransitions(getTicketTransitionStatus(ticket));
      const hasTransition = ticketTransitions.some(t => t.value === value);
      if (!hasTransition) {
        isCommon = false;
        break;
      }
    }
    if (isCommon) {
      result.push(trans);
    }
  }
  
  return result;
};

const getDefaultBulkStatusListingPrices = (tickets: Ticket[]): Record<string, string> =>
  Object.fromEntries(
    tickets.map(t => [t.tp_id, t.tp_list_price != null ? String(t.tp_list_price) : ''])
  );

const areAllBulkStatusListingPricesValid = (tickets: Ticket[], prices: Record<string, string>): boolean =>
  tickets.every(ticket => {
    const value = String(prices[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !!value && !isNaN(num) && num > 0;
  });

const resetBulkSoldState = () => {
  setBulkSoldMarketplaceId('');
  setBulkSoldLogitixAmounts({});
  setBulkSoldCommonLogitixAmount('');
  setBulkSoldFeeConfigReady(false);
  setBulkSoldFeeConfigByTicketId({});
  setBulkSoldLoading(false);
};

const closeBulkStatusModal = () => {
  setShowBulkStatusModal(false);
  setBulkNewStatus('');
  setBulkStatusListingPrices({});
  setBulkStatusListingCommonPrice('');
  resetBulkSoldState();
};

const openBulkPriceModal = (ticketIds = Array.from(bulkSelectedTicketIds)) => {
  if (!ticketIds.length) {
    showError('No tickets selected');
    return;
  }

  const validation = canBulkUpdateTickets(ticketIds);
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot change price for selected tickets');
    return;
  }
  
  const selectedTickets = ticketRowsForTable
    .map((row) => row.ticket)
    .filter((ticket) => ticketIds.includes(ticket.tp_id));

  if (!selectedTickets.length) {
    showError('Selected tickets are not available for bulk pricing');
    return;
  }

  setBulkSelectedTickets(selectedTickets);
  setBulkPriceIndividual(
    Object.fromEntries(
      selectedTickets.map(t => [t.tp_id, t.tp_list_price != null ? String(t.tp_list_price) : ''])
    )
  );
  setBulkPriceCommon('');
  setShowBulkPriceModal(true);
};

const closeBulkPriceModal = () => {
  setShowBulkPriceModal(false);
  setBulkPriceCommon('');
  setBulkPriceIndividual({});
  setBulkPriceLoading(false);
};

const handleApplyCommonBulkPrice = () => {
  const value = String(bulkPriceCommon).trim();
  const priceNum = parseFloat(value);
  if (!value || isNaN(priceNum) || priceNum < 0) {
    showError('Enter a valid price to apply to all tickets');
    return;
  }
  setBulkPriceIndividual(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(priceNum)]))
  );
};

const handleBulkPriceConfirm = async () => {
  if (bulkPriceLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }

  const validation = canBulkUpdateTickets(bulkSelectedTickets.map((ticket) => ticket.tp_id));
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot change price for selected tickets');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const value = String(bulkPriceIndividual[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !value || isNaN(num) || num < 0;
  });

  if (invalidTicket) {
    showError('All selected tickets must have a valid price.');
    return;
  }

  beginStatusTableBusy('Updating ticket prices…');
  setBulkPriceLoading(true);

  try {
    await withSuppressedBulkMutationToasts(async () => {
      const tpIdsByPrice = new Map<number, string[]>();

      bulkSelectedTickets.forEach((ticket) => {
        const priceNum = Number(parseFloat(String(bulkPriceIndividual[ticket.tp_id] || '').trim()));
        const existing = tpIdsByPrice.get(priceNum) || [];
        existing.push(ticket.tp_id);
        tpIdsByPrice.set(priceNum, existing);
      });

      const results: Array<{ status: 'fulfilled' | 'rejected'; tp_ids: string[]; price: number; reason?: unknown }> = [];

      // One Logitix BulkUpdate per price group — parallel per-ticket calls split VL seat groups.
      for (const [priceNum, tp_ids] of tpIdsByPrice.entries()) {
        try {
          await editTicketPrice({
            variables: {
              tp_ids,
              tp_list_price: priceNum,
            },
          });
          results.push({ status: 'fulfilled', tp_ids, price: priceNum });
        } catch (error) {
          results.push({ status: 'rejected', tp_ids, price: priceNum, reason: error });
        }
      }

      const successIds = results
        .filter((result) => result.status === 'fulfilled')
        .flatMap((result) => result.tp_ids.map((tp_id) => ({ tp_id, price: result.price })));
      const failedCount = results
        .filter((result) => result.status === 'rejected')
        .reduce((sum, result) => sum + result.tp_ids.length, 0);

      if (successIds.length > 0) {
        setFilteredTickets(prevTickets =>
          sortManageTickets(prevTickets.map(ticket => {
            const updated = successIds.find(item => item.tp_id === ticket.tp_id);
            if (!updated) return ticket;
            return {
              ...ticket,
              tp_list_price: updated.price,
              tp_updated_at: new Date().toISOString()
            };
          }))
        );
        success(`Updated prices for ${successIds.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`);
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to update ticket prices. All seats in the same section/row must be updated together.');
      }
    });
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to update ticket prices'));
  } finally {
    closeBulkPriceModal();
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setBulkPriceLoading(false);
    endStatusTableBusy();
    await syncTicketsAfterStatusChange();
  }
};

const initBulkSoldFeeConfig = async () => {
  if ((!marketplacesList.length && !marketplacesLoading) || marketplacesError) {
    try {
      await refetchMarketplaces();
    } catch (err: any) {
      console.error('Failed to refetch marketplaces for bulk sold:', err);
    }
  }

 setBulkSoldLogitixAmounts(
  Object.fromEntries(
    bulkSelectedTickets.map(t => [t.tp_id, '0'])   
  )
);
  setBulkSoldCommonLogitixAmount('');
  setBulkSoldFeeConfigReady(false);
  setBulkSoldFeeConfigByTicketId({});

  const configs = await loadBulkSoldFeeConfigs(bulkSelectedTickets);
  setBulkSoldFeeConfigByTicketId(configs);
  setBulkSoldFeeConfigReady(true);
};

const getBulkTicketSoldFeeConfig = (
  ticketId: string,
  feeConfigByTicketId: Record<string, SoldFeeConfig>,
): SoldFeeConfig => feeConfigByTicketId[ticketId] ?? DEFAULT_SOLD_FEE_CONFIG;

const isBulkSoldTransitionReady = (
  tickets: Ticket[],
  logitixAmounts: Record<string, string>,
  marketplaceId: string,
  feeConfigReady: boolean,
  marketplaces: MarketplaceOption[],
  feeConfigByTicketId: Record<string, SoldFeeConfig>,
): boolean => {
  if (!feeConfigReady || !marketplaceId) return false;
  const mp = marketplaces.find(m => m.id === marketplaceId);
  if (!mp) return false;

  return tickets.every(ticket => {
    const listPrice = Number(ticket.tp_list_price) || 0;
    const rawValue = logitixAmounts[ticket.tp_id];
    const value = String(rawValue ?? '').trim();
    const logitixValue = value
      ? Number(parseFloat(value))
      : Math.round(listPrice * (100 - mp.vl_fee_percentage)) / 100;
    if (!Number.isFinite(logitixValue) || logitixValue <= 0) return false;
    const feeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, feeConfigByTicketId);
    const quickPct = effectiveSoldQuickCutPercent(
      mp.vl_fee_percentage,
      feeConfig.feeMode,
      feeConfig.superClientPercent,
    );
    const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
    const settlement = Math.round((logitixValue - quickCutDollars) * 100) / 100;
    return settlement > 0;
  });
};

const handleApplyBulkStatusListingPrice = () => {
  const value = String(bulkStatusListingCommonPrice).trim();
  const priceNum = parseFloat(value);
  if (!value || isNaN(priceNum) || priceNum <= 0) {
    showError('Enter a valid price to apply to all tickets');
    return;
  }
  setBulkStatusListingPrices(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(priceNum)]))
  );
};

const handleBulkNewStatusSelect = (statusValue: string) => {
  setBulkNewStatus(statusValue);
  if (normalizeStatusValue(statusValue) === 'List') {
    setBulkStatusListingPrices(getDefaultBulkStatusListingPrices(bulkSelectedTickets));
    setBulkStatusListingCommonPrice('');
    resetBulkSoldState();
  } else if (normalizeStatusValue(statusValue) === 'Sold') {
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
    setBulkSoldMarketplaceId('');
    setBulkSoldLogitixAmounts({});
    setBulkSoldCommonLogitixAmount('');
    void initBulkSoldFeeConfig();
  } else {
    setBulkStatusListingPrices({});
    setBulkStatusListingCommonPrice('');
    resetBulkSoldState();
  }
};

// Handle bulk status change
const handleBulkStatusChange = async () => {
  if (bulkSelectedTickets.length === 0 || !bulkNewStatus) return;
  if (bulkStatusChangeLoading || statusTableBusyCount > 0) return;

  beginStatusTableBusy('Updating ticket status…');
  setBulkStatusChangeLoading(true);
  const tpIds = bulkSelectedTickets.map(t => t.tp_id);
  const targetStatus = normalizeStatusValue(bulkNewStatus);
  suppressListPublishChainToastsRef.current = true;

  try {
  if (targetStatus === 'Verified') {
  // 1. All selected tickets must be ToBeVerified
  const invalidStatus = bulkSelectedTickets.some(t => t.tp_status !== 'ToBeVerified');
  if (invalidStatus) {
    showError('Only tickets with "To Be Verified" status can be verified in bulk.');
    return;
  }

  // 2. Required fields must exist
  const missingFields = bulkSelectedTickets.some(t => !t.tp_section || !t.tp_row || t.tp_seat_no === null || t.tp_list_price == null);
  if (missingFields) {
    showError('One or more selected tickets are missing required fields (section, row, seat, or price).');
    return;
  }

  try {
    await callUpdateTicketStatusRest(tpIds, true, false);

    updateLocalTicketStatus(tpIds, 'Verified');
    success(`Successfully verified ${tpIds.length} ticket(s).`);
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    closeBulkStatusModal();
    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Bulk verification failed'));
    await syncTicketsAfterStatusChange();
  }
  return;
}

    // List: publish (Verified) or undo delist (Delisted tab)
    if (targetStatus === 'List') {
      const undoToListTickets = bulkSelectedTickets.filter(ticket => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
      });
      const publishToListTickets = bulkSelectedTickets.filter(
        ticket => normalizeStatusValue(ticket.tp_status || '') === 'Verified',
      );
      const unsupportedToList = bulkSelectedTickets.filter(
        ticket => !undoToListTickets.includes(ticket) && !publishToListTickets.includes(ticket),
      );

      if (unsupportedToList.length > 0) {
        showError(
          'Only Verified tickets (publish) or Delist Requested / Delisted tickets (undo to Listed) can be moved to Listed in bulk.',
        );
        return;
      }

      if (undoToListTickets.length > 0 && publishToListTickets.length > 0) {
        showError('Select only Verified tickets or only Delisted tickets to move to Listed, not both.');
        return;
      }

      const successIds: string[] = [];

      if (undoToListTickets.length > 0) {
        const invalidUndoPrice = undoToListTickets.find(ticket => {
          const value = String(bulkStatusListingPrices[ticket.tp_id] || '').trim();
          const num = parseFloat(value);
          return !value || isNaN(num) || num <= 0;
        });

        if (invalidUndoPrice) {
          showError('All selected tickets must have a valid listing price greater than 0.');
          return;
        }

        try {
          const undoTpIds = undoToListTickets.map((ticket) => ticket.tp_id);
          await executeBatchedUndoDelistWithFallback(undoTpIds);

          const priceUpdates = undoToListTickets.map((ticket) => ({
            tp_id: ticket.tp_id,
            priceNum: Number(parseFloat(String(bulkStatusListingPrices[ticket.tp_id] || '').trim())),
          }));

          const tpIdsByPrice = new Map<number, string[]>();
          priceUpdates.forEach(({ tp_id, priceNum }) => {
            const existing = tpIdsByPrice.get(priceNum) || [];
            existing.push(tp_id);
            tpIdsByPrice.set(priceNum, existing);
          });

          await Promise.all(
            Array.from(tpIdsByPrice.entries()).map(([priceNum, tp_ids]) =>
              editTicketPrice({
                variables: { tp_ids, tp_list_price: priceNum },
              }),
            ),
          );

          const undoSuccess = priceUpdates;
          successIds.push(...undoSuccess.map((item) => item.tp_id));
          if (successIds.length > 0) {
            updateLocalTicketStatus(successIds, 'List');
            setFilteredTickets(prevTickets =>
              sortManageTickets(prevTickets.map(ticket => {
                const updated = undoSuccess.find(item => item.tp_id === ticket.tp_id);
                if (!updated) return ticket;
                return {
                  ...ticket,
                  tp_status: 'List',
                  tp_list_price: updated.priceNum,
                  tp_updated_at: new Date().toISOString(),
                };
              }))
            );
            success(`Moved ${successIds.length} ticket(s) back to Listed`);
            if (activeTab !== 'Listed') {
              switchTabUnlessEventSearch('Listed');
            }
          }
        } catch (undoError: any) {
          showError(getErrorMessage(undoError, 'Failed to move tickets back to Listed'));
        }
      } else {
        const invalidTicket = publishToListTickets.find(ticket => {
          const value = String(bulkStatusListingPrices[ticket.tp_id] || '').trim();
          const num = parseFloat(value);
          return !value || isNaN(num) || num <= 0;
        });

        if (invalidTicket) {
          showError('All selected tickets must have a valid listing price greater than 0.');
          return;
        }

        const results = await Promise.allSettled(
          publishToListTickets.map(ticket => {
            const priceNum = Number(parseFloat(String(bulkStatusListingPrices[ticket.tp_id] || '').trim()));
            return callPublishTicketsRest([ticket.tp_id], priceNum).then(() => ticket.tp_id);
          }),
        );

        const publishedIds = results
          .filter((result): result is PromiseFulfilledResult<string> => result.status === 'fulfilled')
          .map(result => result.value);
        const failedCount = results.filter(result => result.status === 'rejected').length;
        successIds.push(...publishedIds);

        if (publishedIds.length > 0) {
          updateLocalTicketStatus(publishedIds, 'List');
          setFilteredTickets(prevTickets =>
            sortManageTickets(prevTickets.map(ticket => {
              if (!publishedIds.includes(ticket.tp_id)) return ticket;
              const priceNum = Number(parseFloat(String(bulkStatusListingPrices[ticket.tp_id] || '').trim()));
              return {
                ...ticket,
                tp_status: 'List',
                tp_list_price: priceNum,
                tp_updated_at: new Date().toISOString()
              };
            }))
          );
          success(`Published ${publishedIds.length} ticket(s) to Listed${failedCount ? `, ${failedCount} failed` : ''}`);
          if (activeTab !== 'Listed') {
            switchTabUnlessEventSearch('Listed');
          }
        }

        if (failedCount > 0 && publishedIds.length === 0) {
          showError('Failed to publish selected tickets');
        }
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    const isBulkSettlementOverride =
      bulkSelectedTickets.every(ticket => normalizeStatusValue(ticket.tp_status || '') === 'Sold') &&
      (targetStatus === 'Sold' || targetStatus === 'Settled' || targetStatus === 'SettlementInProgress');

    if (isBulkSettlementOverride) {
      if (!canOverrideSettlementStatus) {
        info(soldPaymentStartedMessage);
        return;
      }
      const paymentStatus = getPaymentStatusForSettlementOverride(targetStatus);
      if (paymentStatus === undefined) {
        showError('That settlement status could not be applied.');
        return;
      }
      try {
        const result = await callUpdateSettlementPaymentStatusRest(tpIds, paymentStatus);
        const updatedIds: string[] = Array.isArray(result?.updatedTpIds) ? result.updatedTpIds : tpIds;
        if (!updatedIds.length) {
          showError('No sold tickets were updated.');
          return;
        }
        updateLocalTicketPaymentStatus(updatedIds, paymentStatus);
        success(result?.message || `Updated settlement status for ${updatedIds.length} ticket(s).`);
        closeBulkStatusModal();
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        await syncTicketsAfterStatusChange();
        syncDashboardNotifications();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to update settlement status'));
      }
      return;
    }

    // Sold - confirm from bulk status modal (same UI as single-ticket sold flow)
    if (targetStatus === 'Sold') {
      await handleBulkSoldConfirm();
      return;
    }
if (targetStatus === 'Unsold') {
  const ineligible = bulkSelectedTickets.filter(ticket => {
    const status = normalizeStatusValue(ticket.tp_status || '');
    return status !== 'List';
  });
  if (ineligible.length) {
    showError('Only Listed tickets can be marked as Unsold in bulk.');
    return;
  }

  setStatusTableBusyMessage('Marking tickets as Unsold…');
  try {
    const tpIds = bulkSelectedTickets.map(t => t.tp_id);
    const response = await callMarkTicketsUnsoldRest(tpIds);
    success(response?.message || `Marked ${tpIds.length} ticket(s) as Unsold`);
    updateLocalTicketStatus(tpIds, 'Unsold');
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    closeBulkStatusModal();

    // ✅ Switch to Unsold tab after successful update
    if (activeTab !== 'Unsold') {
      handleTabChange('Unsold');
    }

    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to mark tickets as Unsold'));
    await syncTicketsAfterStatusChange();
  }
  return;
}
    // Return — Delisted / Delist Requested tickets (Node REST API)
    if (targetStatus === 'Return') {
      const ineligible = bulkSelectedTickets.filter(ticket => {
        const status = normalizeStatusValue(ticket.tp_status || '');
        return status !== 'Delist' && status !== 'DelistInProgress' && status !== 'DelistRequested';
      });
      if (ineligible.length) {
        showError(
          'Only Delisted or Delist Requested tickets can be returned. Deselect Listing Request Cancelled or other statuses.',
        );
        return;
      }

      const eventIds = bulkSelectedTickets.map(t => t.e_id).filter(Boolean);
      const sameEvent = eventIds.length > 0 && eventIds.every(id => id === eventIds[0]);
      if (!sameEvent) {
        showError('Please select tickets from the same event to return in bulk.');
        return;
      }

      setStatusTableBusyMessage('Returning tickets…');
      try {
        const { message: returnMessage, type: returnType } = await executeReturnTicketsRest(tpIds);
        updateLocalTicketStatus(tpIds, 'Return');
        if (returnType === 'success') {
          success(returnMessage || `Returned ${tpIds.length} ticket(s)`);
        } else {
          info(returnMessage || `Returned ${tpIds.length} ticket(s)`);
        }
        setFilterStatus('Return');
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        closeBulkStatusModal();
        await syncTicketsAfterStatusChange();
      } catch (returnError: any) {
        showError(getErrorMessage(returnError, 'Failed to return tickets'));
        await syncTicketsAfterStatusChange();
      }
      return;
    }

    // DelistInProgress -> Delist: confirm delist (admin API — no updateTicketStatus / SES)
    if (targetStatus === 'Delist') {
      // const notEligible = bulkSelectedTickets.filter(ticket => {
      //   const status = normalizeStatusValue(ticket.tp_status || '');
      //   return status !== 'DelistInProgress' && status !== 'DelistRequested';
      // });
      // if (notEligible.length) {
      //   showError('Only tickets in Delist Requested status can be confirmed as Delisted.');
      //   return;
      // }

      setStatusTableBusyMessage('Confirming delist…');
      const tpIds = bulkSelectedTickets.map((ticket) => ticket.tp_id);
      let successIds: string[] = [];
      let failedCount = 0;
      try {
        const delistResult = await executeAdminOrSuperUserDelistRest(tpIds);
        successIds = delistResult.successIds;
        failedCount = tpIds.filter((id) => !successIds.includes(id)).length;
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to delist tickets'));
        setBulkSelectedTicketIds(new Set());
        setBulkSelectedTickets([]);
        closeBulkStatusModal();
        await syncTicketsAfterStatusChange();
        return;
      }

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'Delist');
        success(`Delisted ${successIds.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`);
        if (activeTab !== 'Delisted') {
          switchTabUnlessEventSearch('Delisted');
        }
      }
      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to delist tickets');
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // Delist requested (from Listed/Verified) — email modal + delistTickets API
    if (targetStatus === 'DelistInProgress' || targetStatus === 'DelistRequested') {
      openBulkDelistEmailModal(tpIds);
      return;
    }

    // Listing Request Cancelled (ToBeVerified only) — use dedicated cancel API, not updateTicketStatus
    if (targetStatus === 'ListingRequestCancelled' || targetStatus === 'Invalid') {
      const notEligible = bulkSelectedTickets.filter(
        t => normalizeStatusValue(t.tp_status || '') !== 'ToBeVerified',
      );
      if (notEligible.length) {
        showError('Only tickets in To Be Verified status can be marked as Listing Request Cancelled.');
        return;
      }

      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket => callCancelListingRequestByAdminRest(ticket.tp_id)),
      );
      const successIds = bulkSelectedTickets
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(ticket => ticket.tp_id);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'ListingRequestCancelled');
        success(
          `Cancelled ${successIds.length} listing request(s)${failedCount ? `, ${failedCount} failed` : ''}. Find them under the Listing Request Cancelled tab.`,
        );
        if (activeTab !== 'Listing Request Cancelled') {
          switchTabUnlessEventSearch('Listing Request Cancelled');
        }
        setFilterStatus('All Listing Request Cancelled');
      }

      if (failedCount > 0 && successIds.length === 0) {
        const firstError = results.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        );
        showError(getErrorMessage(firstError?.reason, 'Failed to cancel listing requests'));
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // ListingRequestCancelled -> ToBeVerified: Hasura restore (no SES / updateTicketStatus)
    if (targetStatus === 'ToBeVerified') {
      const notEligible = bulkSelectedTickets.filter(
        t => normalizeStatusValue(t.tp_status || '') !== 'ListingRequestCancelled',
      );
      if (notEligible.length) {
        showError('Only tickets in Listing Request Cancelled status can be restored to To Be Verified in bulk.');
        return;
      }

      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket =>
          restoreCancelledListingToVerification({ variables: { tp_id: ticket.tp_id } }).then(
            result => {
              const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
              if (updated <= 0) {
                throw new Error('Unable to restore ticket to To Be Verified');
              }
              return ticket.tp_id;
            },
          ),
        ),
      );
      const successIds = bulkSelectedTickets
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(ticket => ticket.tp_id);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'ToBeVerified');
        success(
          `Restored ${successIds.length} ticket(s) to To Be Verified${failedCount ? `, ${failedCount} failed` : ''}`,
        );
        if (activeTab !== 'Verification') {
          switchTabUnlessEventSearch('Verification');
        }
        setFilterStatus('ToBeVerified');
      }

      if (failedCount > 0 && successIds.length === 0) {
        const firstError = results.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        );
        showError(getErrorMessage(firstError?.reason, 'Failed to restore tickets to To Be Verified'));
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // Deleted - bulk delete tickets
    if (targetStatus === 'Deleted') {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket => {
          return updateTicketStatus({
            variables: {
              tp_id: ticket.tp_id,
              status: 'Deleted'
            }
          });
        })
      );
      
      const successIds = bulkSelectedTickets
        .filter((_, index) => results[index].status === 'fulfilled')
        .map(ticket => ticket.tp_id);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        // Remove deleted tickets from current tab
        setFilteredTickets((prevTickets) =>
          prevTickets.filter((t) => !successIds.includes(t.tp_id))
        );
        setStatusFilterSourceTickets((prevTickets) =>
          prevTickets.filter((t) => !successIds.includes(t.tp_id))
        );

        // Update tab counts
        setTabCounts((prev) => {
          const updated = { ...prev };
          successIds.forEach(() => {
            updated.Deleted = (updated.Deleted || 0) + 1;
            updated[activeTab] = Math.max(0, (updated[activeTab] || 0) - 1);
          });
          return updated;
        });

        updateLocalTicketStatus(successIds, 'Deleted');
        success(
          `Deleted ${successIds.length} ticket(s)${failedCount ? `, ${failedCount} failed` : ''}`
        );

        // Switch to Deleted tab to see the deleted tickets
        if (activeTab !== 'Deleted') {
          switchTabUnlessEventSearch('Deleted');
        }
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to delete selected tickets');
      }

      setBulkSelectedTicketIds(new Set());
      setBulkSelectedTickets([]);
      closeBulkStatusModal();
      await syncTicketsAfterStatusChange();
      return;
    }

    // Generic fallback: call REST endpoint in batch
    await callUpdateTicketStatusRest(tpIds, targetStatus === 'Verified', false);
    updateLocalTicketStatus(tpIds, bulkNewStatus);
    success(`Updated ${tpIds.length} ticket(s) to ${getStatusLabel(bulkNewStatus)}`);
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setShowBulkStatusModal(false);
    setBulkNewStatus('');
    await syncTicketsAfterStatusChange();
    return;
  } catch (error: any) {
    console.error('Bulk update failed', error);
    showError(getErrorMessage(error, `Failed to update selected tickets`));
    await syncTicketsAfterStatusChange();
  } finally {
    suppressListPublishChainToastsRef.current = false;
    setBulkStatusChangeLoading(false);
    endStatusTableBusy();
  }
};

// Bulk delist with email modal
const [showBulkDelistEmailModal, setShowBulkDelistEmailModal] = useState(false);
const [pendingBulkDelistTpIds, setPendingBulkDelistTpIds] = useState<string[]>([]);

function openBulkDelistEmailModal(tpIds: string[]) {
  setPendingBulkDelistTpIds(tpIds);
  const defaultEmail = getDefaultDelistEmail(tpIds);
  resetDelistEmail({
    email: defaultEmail,
    confirmEmail: defaultEmail
  });
  setShowBulkDelistEmailModal(true);
  setShowBulkStatusModal(false);
}

const onBulkDelistEmailSubmit = async (data: { email: string; confirmEmail: string }) => {
  const requestedTpIds = [...pendingBulkDelistTpIds];
  if (!requestedTpIds.length) return;

  setShowBulkDelistEmailModal(false);
  beginStatusTableBusy('Submitting delist request…');
  try {
    await executeDelistWithFallback(requestedTpIds, data.email);
    updateLocalTicketStatus(requestedTpIds, 'DelistInProgress');
    success(`Delist request submitted for ${requestedTpIds.length} ticket(s)`);
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    if (activeTab !== 'Delisted') {
      switchTabUnlessEventSearch('Delisted');
    }
    setFilterStatus('DelistInProgress');
    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to request delist'));
    await syncTicketsAfterStatusChange();
  } finally {
    endStatusTableBusy();
    setPendingBulkDelistTpIds([]);
    resetDelistEmail();
  }
};

// Handle checkbox selection
const handleBulkSelectTicket = (ticketId: string, checked: boolean) => {
  setBulkSelectedTicketIds(prev => {
    const newSet = new Set(prev);
    if (checked) {
      newSet.add(ticketId);
    } else {
      newSet.delete(ticketId);
    }
    return newSet;
  });
};

// Handle select all on current page / visible rows
const handleBulkSelectAllPage = (checked: boolean) => {
  if (checked) {
    const allIds = ticketRowsForTable.map(row => row.ticket.tp_id);
    setBulkSelectedTicketIds(new Set(allIds));
  } else {
    setBulkSelectedTicketIds(new Set());
  }
};

// Open bulk status change modal
const openBulkStatusModal = (ticketIds = Array.from(bulkSelectedTicketIds)) => {
  const selectedTicketsList = filteredTickets.filter(t => ticketIds.includes(t.tp_id));
  const validation = canBulkUpdateTickets(ticketIds);
  
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot perform bulk action on selected tickets');
    return;
  }
  
  setBulkSelectedTickets(selectedTicketsList);
  setShowBulkStatusModal(true);
  setBulkNewStatus('');
  setBulkStatusListingPrices({});
  setBulkStatusListingCommonPrice('');
};

const openBulkListModal = () => {
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected for bulk List action.');
    return;
  }

  setBulkListCommonPrice('');
  setBulkListedPrices(
    Object.fromEntries(
      bulkSelectedTickets.map(t => [t.tp_id, t.tp_list_price != null ? String(t.tp_list_price) : ''])
    )
  );
  setShowBulkStatusModal(false);
  setShowBulkListModal(true);
};

const closeBulkListModal = () => {
  setShowBulkListModal(false);
  setBulkListedPrices({});
  setBulkListCommonPrice('');
  setBulkListLoading(false);
};

const openBulkSoldModal = async (tickets?: Ticket[]) => {
  const selectedTicketsList = tickets?.length
    ? tickets
    : filteredTickets.filter(t => bulkSelectedTicketIds.has(t.tp_id));

  if (!selectedTicketsList.length) {
    showError('No tickets selected for bulk Sold action.');
    return;
  }

  if ((!marketplacesList.length && !marketplacesLoading) || marketplacesError) {
    try {
      await refetchMarketplaces();
    } catch (err: any) {
      console.error('Failed to refetch marketplaces for bulk sold modal:', err);
    }
  }

  setBulkSelectedTickets(selectedTicketsList);
  setBulkSoldMarketplaceId('');
  setBulkSoldCommonLogitixAmount('');
  setShowBulkStatusModal(false);
  setShowBulkSoldModal(true);
  await initBulkSoldFeeConfig();
};

const closeBulkSoldModal = () => {
  setShowBulkSoldModal(false);
  resetBulkSoldState();
};

const openSoldModalForTicket = async (ticket: Ticket) => {
  if (!ticket) return;

  if ((!marketplacesList.length && !marketplacesLoading) || marketplacesError) {
    try {
      await refetchMarketplaces();
    } catch (err: any) {
      console.error('Failed to refetch marketplaces for sold modal:', err);
    }
  }

  setSoldModalTicket(ticket);
  setSoldMarketplaceId('');
setSoldLogitixAmount('0');
  setSoldFeeConfigReady(false);
  const initialClientType = getResolvedClientType(ticket);
  if (initialClientType === 'self-pricing') {
    setSoldFeeMode('super');
    setSoldSuperClientPercent(SELF_PRICING_FEE_FALLBACK);
  } else {
    setSoldFeeMode('default');
    setSoldSuperClientPercent(null);
  }
  setShowSoldModal(true);

  if (ticket.u_id) {
    const currentType = getResolvedClientType(ticket);
    if (currentType === 'self-pricing') {
      const percent = await resolveSelfPricingClientPercent(ticket.u_id);
      setSoldFeeMode('super');
      setSoldSuperClientPercent(percent);
    } else {
      const conciergePercent = await fetchUserFeePercent(ticket.u_id);
      if (conciergePercent != null) {
        setSoldFeeMode('super');
        setSoldSuperClientPercent(conciergePercent);
      } else {
        setSoldFeeMode('default');
        setSoldSuperClientPercent(null);
      }
    }
  }

  setSoldFeeConfigReady(true);
};

// Get common status badge class
const getCtBadgeClassForStatus = (status: string): string => {
  const s = (status || '').toLowerCase().replace(/\s/g, '');
  if (s === 'verified') return styles.ctStatusBadgeVerified;
  if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
  if (s === 'sold') return styles.ctStatusBadgeSold;
  if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
  if (s === 'invalid') return styles.ctStatusBadgeInvalid;
  return styles.ctStatusBadgeDefault;
};
  // Filtered status counts for dropdown
  const [filteredStatusCounts, setFilteredStatusCounts] = useState<Record<string, number>>({});

  // Sync Listing Expired tab count whenever trackingCounts updates
  useEffect(() => {
    if (isMarketingUser) return;
    setTabCounts(prev => ({ ...prev, 'Listing Expired': trackingCounts.listingExpired }));
  }, [trackingCounts.listingExpired, isMarketingUser]);

  // Delist email modal states
  const [showDelistEmailModal, setShowDelistEmailModal] = useState(false);
  const [pendingDelistTpIds, setPendingDelistTpIds] = useState<string[]>([]);
  const { register: registerDelistEmail, handleSubmit: handleDelistEmailSubmit, formState: { errors: delistEmailErrors }, reset: resetDelistEmail, watch: watchDelistEmail } = useForm<{ email: string; confirmEmail: string }>({
    defaultValues: { email: '', confirmEmail: '' },
    mode: 'onTouched'
  });

  // ==================== HELPER FUNCTIONS FOR FILTER COUNTS ====================
  
const calculateFilteredCounts = (tickets: Ticket[], currentTab: string) => {
  const counts: Record<string, number> = {};
  
  switch (currentTab) {
    case 'Verification':
      const toBeVerifiedCount = tickets.filter(t => t.tp_status === 'ToBeVerified').length;
      const verifiedCount = tickets.filter(t => t.tp_status === 'Verified').length;
      counts['All Verification'] = toBeVerifiedCount + verifiedCount;
      counts['ToBeVerified'] = toBeVerifiedCount;
      counts['Verified'] = verifiedCount;
      break;
    case 'Verified':
      // When viewing the Verified tab directly, the tickets array is already scoped to Verified
      counts['Verified'] = tickets.length;
      break;
    case 'Listed':
      counts['All Listed'] = tickets.length;
      counts['List'] = tickets.filter(t => t.tp_status === 'List').length;
      break;
    case 'Sold':
      counts['All Sold'] = tickets.length;
      counts['Sold'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Sold').length;
      counts['Settled'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Settled').length;
      counts['SettlementInProgress'] = tickets.filter(
        t => getSoldStatusFilterValue(t) === 'SettlementInProgress'
      ).length;
      counts['Failed'] = tickets.filter(t => getSoldStatusFilterValue(t) === 'Failed').length;
      break;
    case 'Delisted':
      counts['All Delisted'] = tickets.length;
      counts['Delist'] = tickets.filter(t => t.tp_status === 'Delist').length;
      counts['DelistInProgress'] = tickets.filter(
        t => t.tp_status === 'DelistInProgress' || t.tp_status === 'DelistRequested'
      ).length;
      counts['Return'] = tickets.filter(t => t.tp_status === 'Return').length;
      break;
    case 'Deleted':
      counts['All Deleted'] = tickets.length;
      counts['Deleted'] = tickets.length;
      break;
    case 'Listing Request Cancelled':
      counts['All Listing Request Cancelled'] = tickets.length;
      counts['ListingRequestCancelled'] = tickets.filter(t => t.tp_status === 'ListingRequestCancelled').length;
      break;
    case 'Returned':
      // When viewing Returned tab directly, tickets array should be scoped to returned tickets
      counts['Return'] = tickets.length;
      break;
    case 'Listing Expired':
      counts['All Expired'] = tickets.length;
      counts['Closed'] = tickets.filter(t => t.tp_status === 'Closed').length;
      break;
    case 'Unsold':
      counts['All Unsold'] = tickets.length;
      counts['Unsold'] = tickets.filter(t => t.tp_status === 'Unsold').length;
      break;
    default:
      break;
  }
  
  return counts;
};
  // ==================== FETCH TRACKING COUNTS ====================

  const { refetch: refetchExpired } = useQuery(GET_EXPIRED_TICKETS, {
    fetchPolicy: 'network-only',
    skip: true,
    onError: () => {
      setTrackingCounts(prev => ({ ...prev, listingExpired: 0 }));
    }
  });

  const { refetch: refetchFailed } = useQuery(GET_FAILED_TRANSFER_TICKETS, {
    fetchPolicy: 'network-only',
    skip: true,
    onError: () => {
      setTrackingCounts(prev => ({ ...prev, failedTransfer: 0 }));
    }
  });

  const getFailedTransferCutoffDate = () => {
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return cutoff.toISOString();
  };

  const filterFailedTransferByCutoff = (tickets: Ticket[], cutoffIso: string): Ticket[] => {
    const cutoffTs = Date.parse(cutoffIso);
    if (!Number.isFinite(cutoffTs)) return tickets;
    return tickets.filter((ticket: any) => {
      const updatedAtTs = Date.parse(ticket?.tp_updated_at || '');
      if (!Number.isFinite(updatedAtTs)) {
        // Keep rows with missing timestamps for operational visibility.
        return true;
      }
      return updatedAtTs <= cutoffTs;
    });
  };

  const [verificationCutoffDate] = useState(() => {
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return cutoff.toISOString();
  });

  const parseEventDateValue = (value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }

    const asString = String(value).trim();
    if (!asString) return null;

    // First try native parsing for ISO/timestamptz values.
    const nativeParsed = new Date(asString);
    if (!Number.isNaN(nativeParsed.getTime())) {
      return nativeParsed;
    }

    // Fallback: parse DD/MM/YYYY (and DD/MM/YYYY HH:mm[:ss]) explicitly.
    const datePart = asString.split(' ')[0];
    const [ddRaw, mmRaw, yyyyRaw] = datePart.split('/');
    const dd = Number(ddRaw);
    const mm = Number(mmRaw);
    const yyyy = Number(yyyyRaw);
    if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yyyy)) {
      return null;
    }
    const parsed = new Date(yyyy, mm - 1, dd);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return parsed;
  };

  const isUnparseableEventTimezoneLabel = (value: any): boolean => {
    const asString = String(value ?? '').trim();
    if (!asString) return true;
    if (asString.toLowerCase() === 'invalid date') return true;
    // Human labels like "Monday ~ Oct 06 ~ 7:00 pm CDT" are display-only.
    return asString.includes('~');
  };

  const getAutoExpireEventFields = (ticket: any) => ({
    e_date: ticket?.e_date ?? ticket?.ticket?.event?.e_date,
    e_date_time_zone: ticket?.e_date_time_zone ?? ticket?.ticket?.event?.e_date_time_zone,
  });

  const isEventExpired = (ticket: any, nowTs: number) => {
    const { e_date, e_date_time_zone } = getAutoExpireEventFields(ticket);

    // Primary: event start timestamp from e_date (matches DB e.e_date < NOW()).
    const eventDate = parseEventDateValue(e_date);
    if (eventDate && eventDate.getTime() <= nowTs) {
      return true;
    }

    // Fallback: only parse machine-readable timezone values.
    if (!isUnparseableEventTimezoneLabel(e_date_time_zone)) {
      const tzDate = parseEventDateValue(e_date_time_zone);
      if (tzDate && tzDate.getTime() <= nowTs) {
        return true;
      }
    }

    return false;
  };

  const mergeAutoExpireCandidates = (expireCandidatesData: any): any[] => {
    const fromManageView = expireCandidatesData?.filtermanagetickets || [];
    const fromPlacement = expireCandidatesData?.ticket_placement || [];
    const seen = new Set<string>();
    const merged: any[] = [];

    [...fromManageView, ...fromPlacement].forEach((ticket: any) => {
      const tpId = ticket?.tp_id;
      if (!tpId || seen.has(tpId)) return;
      seen.add(tpId);
      merged.push(ticket);
    });

    return merged;
  };

  const mapPlacementToExpiredTicket = (row: any): Ticket => {
    const user = row?.ticket?.user;
    const event = row?.ticket?.event;
    const fullName = [user?.u_first_name, user?.u_last_name].filter(Boolean).join(' ').trim();
    return {
      tp_id: row.tp_id,
      t_id: row.t_id || row?.ticket?.t_id,
      tp_section: row.tp_section,
      tp_row: row.tp_row,
      tp_seat_no: row.tp_seat_no,
      tp_list_price: row.tp_list_price,
      tp_status: row.tp_status,
      tp_updated_at: row.tp_updated_at,
      tp_delist_requested_email: row.tp_delist_requested_email,
      tp_is_support_vanderbilt_nil_fund: row.tp_is_support_vanderbilt_nil_fund,
      e_id: event?.e_id,
      e_name: event?.e_name || '',
      e_date: event?.e_date || '',
      e_address: event?.e_address || '',
      e_brand_name: event?.e_brand_name,
      e_status: event?.e_status,
      e_time_zone: event?.e_time_zone,
      e_date_time_zone: event?.e_date_time_zone,
      u_id: user?.u_id || row?.ticket?.u_id || '',
      u_first_name: user?.u_first_name,
      u_last_name: user?.u_last_name,
      u_email_id: user?.u_email_id,
      u_original_email: user?.u_original_email,
      u_pricing_mode: user?.u_pricing_mode,
      u_full_name: fullName,
      l_name: '',
    } as Ticket;
  };

  const mergeExpiredTicketsResponse = (responseData: any): Ticket[] => {
    const fromView = (responseData?.filterreturntickets || []) as Ticket[];
    const viewIds = new Set(fromView.map((ticket) => ticket.tp_id));
    const orphans = (responseData?.closed_orphan_placements || [])
      .filter((row: any) => row?.tp_id && !viewIds.has(row.tp_id))
      .map(mapPlacementToExpiredTicket);
    return [...fromView, ...orphans];
  };

  const getExpiredTicketsCount = (responseData: any): number =>
    responseData?.closed_placement_aggregate?.aggregate?.count ??
    responseData?.filterreturntickets_aggregate?.aggregate?.count ??
    0;

  const extractTicketsFromActiveTabQuery = (
    responseData: any,
    tab: string,
  ): { tickets: Ticket[]; totalCountValue: number } | null => {
    if (!responseData) return null;

    switch (tab) {
      case 'Verification':
      case 'Verified': {
        const tickets = Array.isArray(responseData.filtermanagetickets)
          ? responseData.filtermanagetickets
          : [];
        const totalCountValue = responseData.filtermanagetickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Listed': {
        const tickets = Array.isArray(responseData.filterlisttickets)
          ? responseData.filterlisttickets
          : [];
        const totalCountValue = responseData.filterlisttickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Sold': {
        const tickets = Array.isArray(responseData.FilterSoldTickets)
          ? responseData.FilterSoldTickets
          : [];
        const totalCountValue = responseData.FilterSoldTickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Delisted': {
        const tickets = Array.isArray(responseData.filterreturntickets)
          ? responseData.filterreturntickets
          : [];
        const totalCountValue = responseData.filterreturntickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Listing Request Cancelled': {
        const tickets = Array.isArray(responseData.filterListingRequestCancelledTickets)
          ? responseData.filterListingRequestCancelledTickets
          : [];
        const totalCountValue =
          responseData.filterListingRequestCancelledTickets_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Deleted': {
        const tickets = mapDeletedPlacementRows(responseData.ticket_placement);
        const totalCountValue = responseData.ticket_placement_aggregate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      case 'Listing Expired':
        return {
          tickets: mergeExpiredTicketsResponse(responseData),
          totalCountValue: getExpiredTicketsCount(responseData),
        };
      case 'Unsold': {
        const tickets = Array.isArray(responseData.filterUnsoldTickets)
          ? responseData.filterUnsoldTickets
          : [];
        const totalCountValue = responseData.filterUnsoldTickets_aggreagate?.aggregate?.count;
        if (totalCountValue == null && tickets.length === 0) return null;
        return {
          tickets,
          totalCountValue: totalCountValue ?? tickets.length,
        };
      }
      default:
        return null;
    }
  };

  const normalizeCardDetailTrackingRows = (tickets: any[] = []): Ticket[] =>
    tickets.map((ticket: any) => ({
      ...ticket,
      e_name: ticket?.e_name || ticket?.ticket?.event?.e_name || '',
      e_date: ticket?.e_date || ticket?.ticket?.event?.e_date || '',
      e_address: ticket?.e_address || ticket?.ticket?.event?.e_address || '',
      u_id: ticket?.u_id || ticket?.ticket?.user?.u_id || '',
      u_full_name:
        ticket?.u_full_name ||
        ticket?.full_name ||
        ticket?.ticket?.user?.u_full_name ||
        '',
      u_first_name: ticket?.u_first_name || ticket?.ticket?.user?.u_first_name || '',
      u_last_name: ticket?.u_last_name || ticket?.ticket?.user?.u_last_name || '',
      u_email_id: ticket?.u_email_id || ticket?.ticket?.user?.u_email_id || ''
    })) as Ticket[];

  async function fetchTrackingCounts() {
    // Auto-move verification tickets to Listing Expired once event date-time has passed.
    const now = Date.now();
    let autoExpiredCount = 0;
    if (!autoExpireInFlightRef.current) {
      autoExpireInFlightRef.current = true;
      try {
        const { data: expireCandidatesData, error: expireQueryError, errors: expireQueryErrors } = await fetchTicketsToAutoExpire({
          variables: {
            pricingMode: '%'
          }
        });
        if (expireQueryError || expireQueryErrors?.length) {
          console.error('[ManageTickets] Auto-expire candidate query failed:', expireQueryError || expireQueryErrors);
        } else {
          const tpIdsToExpire: string[] = mergeAutoExpireCandidates(expireCandidatesData)
            .filter((ticket: any) => isEventExpired(ticket, now))
            .map((ticket: any) => ticket?.tp_id)
            .filter(Boolean);

          console.log('[ManageTickets] Auto-expire scan:', {
            candidatesFetched: mergeAutoExpireCandidates(expireCandidatesData).length,
            pastEventMatches: tpIdsToExpire.length
          });

          if (tpIdsToExpire.length > 0) {
            const expireResult = await bulkExpireTickets({ variables: { tp_ids: tpIdsToExpire } });
            if (expireResult.errors?.length) {
              console.error('[ManageTickets] Auto-expire bulk errors:', expireResult.errors);
            }
            let bulkAffectedRows = expireResult?.data?.update_ticket_placement?.affected_rows || 0;

            // Fallback: if bulk update is blocked by role/permission constraints,
            // try per-ticket updates using the existing single status mutation path.
            if (bulkAffectedRows === 0) {
              let singleUpdatedCount = 0;
              for (const tpId of tpIdsToExpire) {
                try {
                  const singleRes = await updateTicketStatusSilent({
                    variables: {
                      tp_id: tpId,
                      status: 'Closed'
                    }
                  });
                  if (singleRes.errors?.length) {
                    console.error('[ManageTickets] Auto-expire single update errors:', { tpId, errors: singleRes.errors });
                  }
                  const updated = singleRes?.data?.update_ticket_placement?.returning?.length || 0;
                  if (updated > 0) {
                    singleUpdatedCount += 1;
                  }
                } catch (singleUpdateError) {
                  console.error('[ManageTickets] Auto-expire single update failed:', { tpId, singleUpdateError });
                }
              }
              bulkAffectedRows = singleUpdatedCount;
              console.log('[ManageTickets] Auto-expire fallback result:', {
                candidates: tpIdsToExpire.length,
                singleUpdatedCount
              });
            }

            autoExpiredCount = bulkAffectedRows;
            console.log('[ManageTickets] Auto-expired tickets:', {
              candidates: tpIdsToExpire.length,
              affectedRows: bulkAffectedRows
            });
          }
        }
      } catch (autoExpireError) {
        console.error('[ManageTickets] Auto-expire failed:', autoExpireError);
      } finally {
        autoExpireInFlightRef.current = false;
      }
    }

    if (autoExpiredCount > 0) {
      await refetch();
    }

    const getTrackingCount = (responseData: any): number => {
      if (responseData?.closed_placement_aggregate?.aggregate?.count != null) {
        return responseData.closed_placement_aggregate.aggregate.count;
      }
      return (
        responseData?.ticket_placement_aggregate?.aggregate?.count ||
        responseData?.filterreturntickets_aggregate?.aggregate?.count ||
        responseData?.filtermanagetickets_aggregate?.aggregate?.count ||
        0
      );
    };

    

    const getTrackingTickets = (responseData: any): Ticket[] => {
      if (responseData?.closed_orphan_placements || responseData?.filterreturntickets) {
        return mergeExpiredTicketsResponse(responseData);
      }
      if (Array.isArray(responseData?.ticket_placement)) return responseData.ticket_placement as Ticket[];
      if (Array.isArray(responseData?.filtermanagetickets)) return responseData.filtermanagetickets as Ticket[];
      if (Array.isArray(responseData?.filterreturntickets)) return responseData.filterreturntickets as Ticket[];
      return [];
    };

    try {
      const trackingVariables = {
        ...getManageTicketsTrackingVariables(
          eventVenueSearch ? `%${eventVenueSearch}%` : '%',
          clientSearch ? `%${clientSearch}%` : '%',
        ),
        ...getQueryVariables(),
      };
      if (startDate) {
        const range = getLocalDayRangeUTC(startDate);
        (trackingVariables as Record<string, unknown>).startdate = range?.startUTC ?? startDate;
      } else {
        (trackingVariables as Record<string, unknown>).startdate = '1970-01-01';
      }
      if (endDate) {
        const range = getLocalDayRangeUTC(endDate);
        (trackingVariables as Record<string, unknown>).enddate = range?.endUTC ?? endDate;
      } else {
        (trackingVariables as Record<string, unknown>).enddate = '9999-12-31';
      }

      const expiredResult = await refetchExpired(trackingVariables);
      const cutoffDate = getFailedTransferCutoffDate();
      const failedResult = await refetchFailed(trackingVariables);

 const expiredTickets = mergeExpiredTicketsResponse(expiredResult?.data);
      const failedTickets = filterFailedTransferByCutoff(
        getTrackingTickets(failedResult?.data),
        cutoffDate
      );

      // Keep tracking cards in the same user scope as table data for marketing users.
      const scopedExpiredTickets = isMarketingUser
        ? expiredTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : expiredTickets;
      const scopedFailedTickets = isMarketingUser
        ? failedTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : failedTickets;

      setTrackingCounts({
        listingExpired: isMarketingUser ? scopedExpiredTickets.length : getTrackingCount(expiredResult?.data),
        failedTransfer: scopedFailedTickets.length
      });
      const ids = new Set<string>(
        (isMarketingUser
          ? scopedFailedTickets
          : filterFailedTransferByCutoff((failedResult?.data?.filtermanagetickets || []) as Ticket[], cutoffDate))
          .map((t: any) => t.tp_id as string)
      );
      setFailedTransferTicketIds(ids);

      const modalOpen = showCardDetailModalRef.current;
      if (modalOpen === 'FAILED_TRANSFER') {
        setCardDetailTickets(sortManageTickets(normalizeCardDetailTrackingRows(scopedFailedTickets as any[])));
      } else if (modalOpen === 'LISTING_EXPIRED') {
        setCardDetailTickets(sortManageTickets(normalizeCardDetailTrackingRows(scopedExpiredTickets as any[])));
      }
    } catch {
      // silent
    }
  };

  // ==================== MUTATIONS ====================

  // Function to update local state with new price
  const updateLocalTicketPrice = (tpIds: string[], newPrice: number) => {
    const now = new Date().toISOString();
    const updateTicket = (ticket: Ticket) =>
      tpIds.includes(ticket.tp_id)
        ? {
            ...ticket,
            tp_list_price: newPrice,
            tp_updated_at: now,
          }
        : ticket;

    setFilteredTickets(prevTickets =>
      sortManageTickets(prevTickets.map(updateTicket))
    );

    if (hasActiveEventSearch(eventVenueSearch)) {
      setEventGroupedByStatus(prev => {
        const next = createEmptyEventGroupedTickets();

        for (const section of EVENT_TICKET_VIEW_SECTIONS) {
          for (const ticket of prev[section.key] ?? []) {
            next[section.key].push(updateTicket(ticket));
          }
        }

        for (const key of EVENT_TICKET_VIEW_SECTIONS.map(s => s.key)) {
          next[key] = sortManageTickets(next[key]);
        }

        return next;
      });
    }
  };

  // Price update mutation
  const [editTicketPrice, { loading: priceLoading }] = useMutation(EDIT_TICKET_PRICE_BY_SUPER_USER, {
    onCompleted: () => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      success('Listing Price Updated Successfully');
      handlePriceUpdateSuccess();
    },
    onError: (err) => {
      const price = pendingFinalPriceRef.current;
      if (selectedTpIds.length && !isNaN(price)) {
        updatePriceDirect({
          variables: {
            tp_ids: selectedTpIds,
            tp_list_price: price
          }
        }).catch(() => {
          handlePriceUpdateSuccess();
        });
      } else {
        handlePriceUpdateSuccess();
      }
    }
  });

  const [updatePriceDirect] = useMutation(UPDATE_PRICE_DIRECT, {
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      if (data?.update_ticket_placement?.returning?.length > 0) {
        const price = pendingFinalPriceRef.current;
        if (!isNaN(price)) {
          updateLocalTicketPrice(selectedTpIds, price);
        }
        success('Listing Price Updated Successfully');
        handlePriceUpdateSuccess();
      }
    },
    onError: (err) => {
      showError('Failed to update price. Please try again.');
      handlePriceUpdateSuccess();
    }
  });

  const [validateUpdate] = useMutation(VALIDATE_UPDATE, {
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      const message = (data?.updateTicketStatus?.message || '').trim();
      const normalized = message.toLowerCase();

      const hasSuccess = normalized.includes('success');
      const hasNotFound = normalized.includes('not found');
      const hasFailure =
        normalized.includes('failed') ||
        normalized.includes('error') ||
        normalized.includes("couldn't");
      const hasZeroUpdated =
        normalized.includes('0 ticket updated successfully') ||
        normalized.includes('0 tickets updated successfully');

      if ((hasNotFound && hasZeroUpdated) || hasFailure) {
        showError(message || 'Ticket verification failed');
      } else if (hasSuccess && hasNotFound) {
        info(message || 'Ticket verification processed with partial success');
      } else if (hasSuccess) {
        success(message || 'Status updated successfully');
        // Auto-switch to Verification tab when tickets are verified
        if (activeTab !== 'Verification') {
          setTimeout(() => switchTabUnlessEventSearch('Verification'), 300);
        }
      } else {
        info(message || 'Status update processed');
      }
      refetch();
      refetchExpired();
      refetchFailed();
      syncDashboardNotifications();
      handleBulkActionComplete();
    },
    onError: (err) => {
      showError(getErrorMessage(err, 'Failed to update ticket status'));
      handleBulkActionComplete();
    }
  });

  const [publishTickets] = useMutation(PUBLISH_TICKETS, {
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      const message = data?.publishTickets?.message || '';
      const type = resolveActionMessageType(message);
      if (type === 'error') showError(message || 'Failed to publish tickets');
      else if (type === 'success') {
        success(message || 'Ticket(s) published successfully');
        // Auto-switch to Listed tab when tickets are published
        if (activeTab !== 'Listed') {
          setTimeout(() => switchTabUnlessEventSearch('Listed'), 300);
        }
      }
      else info(message || 'Publish request processed');
      refetch();
      refetchExpired();
      refetchFailed();
      syncDashboardNotifications();
      handleBulkActionComplete();
    },
    onError: (err) => {
      showError(getErrorMessage(err, 'Failed to publish tickets'));
      handleBulkActionComplete();
    }
  });

  // Update Ticket Details Mutation
  const [updateTicketDetails] = useMutation(UPDATE_TICKET_DETAILS, {
    onError: () => {
      // Error handled by executeEditDetailsWithFallback
    }
  });

  const [updateTicketStatus] = useMutation(UPDATE_TICKET_STATUS, {
    onCompleted: (data) => {
      if (suppressListPublishChainToastsRef.current) {
        return;
      }
      if (data?.update_ticket_placement?.returning?.length > 0) {
        success('Ticket status updated successfully!');
        setShowStatusModal(false);
        const updatedTicket = data.update_ticket_placement.returning[0];
        setFilteredTickets(prevTickets =>
          sortManageTickets(prevTickets.map(ticket =>
            ticket.tp_id === updatedTicket.tp_id
              ? { ...ticket, tp_status: updatedTicket.tp_status, tp_updated_at: new Date().toISOString() }
              : ticket
          ))
        );
        localStatusOverridesRef.current[updatedTicket.tp_id] = updatedTicket.tp_status;
        // Auto-switch to the tab for the new status
        const targetTab = getTabForStatus(updatedTicket.tp_status);
        if (targetTab !== activeTab) {
          switchTabUnlessEventSearch(targetTab);
        }
        refetch();
        refetchExpired();
        refetchFailed();
        syncDashboardNotifications();
      } else {
        info('No changes were made');
        setShowStatusModal(false);
      }
      setStatusChangeLoading(false);
    },
    onError: (err) => {
      showError(getErrorMessage(err, 'Failed to update ticket status'));
      setShowStatusModal(false);
      setStatusChangeLoading(false);
    }
  });
const [unfilteredTabTickets, setUnfilteredTabTickets] = useState<Ticket[]>([]);
  const [updateTicketStatusSilent] = useMutation(UPDATE_TICKET_STATUS);
  const [restoreCancelledListingToVerification] = useMutation(RESTORE_CANCELLED_LISTING_TO_VERIFICATION);
  const [bulkExpireTickets] = useMutation(BULK_EXPIRE_TICKETS);
  const [fetchTicketsToAutoExpire] = useLazyQuery(GET_TICKETS_TO_AUTO_EXPIRE, { fetchPolicy: 'network-only' });
  const autoExpireInFlightRef = useRef(false);

  const [getSuperUserForSold] = useLazyQuery(GET_SUPER_USER_BY_UID, { fetchPolicy: 'no-cache' });
  const [getUserFeePercentById] = useLazyQuery(GET_USER_FEE_PERCENT_BY_ID, { fetchPolicy: 'no-cache' });

  /** Matches User Details: super_user.percentage → user.u_fee_percentage → 15 */
  const fetchUserFeePercent = async (uId: string): Promise<number | null> => {
    try {
      const { data } = await getUserFeePercentById({ variables: { u_id: uId } });
      const fee = Number(data?.user_by_pk?.u_fee_percentage);
      return Number.isFinite(fee) && fee > 0 ? fee : null;
    } catch (err) {
      console.error('Failed to fetch user fee percent:', err);
      return null;
    }
  };

  const resolveSelfPricingClientPercent = async (uId: string): Promise<number> => {
    try {
      const { data } = await getSuperUserForSold({ variables: { u_id: uId } });
      const superUserPercent = Number(data?.super_user?.[0]?.percentage);
      if (Number.isFinite(superUserPercent) && superUserPercent > 0) {
        return superUserPercent;
      }
    } catch (err) {
      console.error('Failed to fetch super user percent:', err);
    }
    const profileFee = await fetchUserFeePercent(uId);
    if (profileFee != null) return profileFee;
    return SELF_PRICING_FEE_FALLBACK;
  };

  const [refreshNotifications] = useLazyQuery(GET_NOTIFICATIONS, {
    fetchPolicy: 'network-only',
    onCompleted: (notificationData) => {
      handleGlobalState((prev: typeof globalState) =>
        mergeNotificationQueryIntoGlobalState(prev, notificationData),
      );
    }
  });

  const syncDashboardNotifications = () => {
    if (!globalState?.profile?.id) return;
    const adminId = globalState.profile.id;
    const runSync = () => {
      refreshNotifications({
        variables: getNotificationQueryVariables(adminId),
      });
    };
    scheduleNotificationSyncRetries(runSync);
  };

  async function callUpdateTicketStatusRest(ticketPlacementId: string[], isValid: boolean, isUndoRequest = false) {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/updateTicketStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementId,
          isValid,
          isUndoRequest
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to update ticket status';
      throw new Error(message);
    }
    return responseData;
  };

  const callDelistTicketByAdminRest = async (ticketPlacementId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/delistTicketBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to delist ticket';
      throw new Error(message);
    }
    return responseData;
  };

  const callCancelListingRequestByAdminRest = async (ticketPlacementId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/cancelListingRequestBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to cancel listing request';
      throw new Error(message);
    }
    return responseData;
  };

  const callUndoDelistRequestToListRest = async (tpId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/undoDelistRequestToList`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { tpId }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to undo delist request';
      throw new Error(message);
    }
    return responseData;
  };

  const callDelistTicketsRest = async (ticketPlacementIds: string[], requestedEmailId: string) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/delistTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          delistTicketsInput: {
            ticketPlacementIds,
            requestedEmailId
          }
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to request delist';
      throw new Error(message);
    }
    return responseData;
  };

  const getDelistRequestEmail = () =>
    loggedInEmail || String(globalState?.profile?.mailId || '').trim();

  const assertDelistTicketsRestSuccess = (data: any, fallbackMessage: string) => {
    const message = String(data?.message || '').trim();
    const status = String(data?.status || '').toLowerCase();
    const normalized = message.toLowerCase();
    if (
      status === 'failure' ||
      (normalized.includes('fail') && !normalized.includes('successfully'))
    ) {
      throw new Error(message || fallbackMessage);
    }
    return message;
  };

  /** Listed tickets: remove from Logitix/Victory Live, then mark Delisted. */
  const runListedTicketDelist = async (ticketPlacementIds: string[]) => {
    if (!ticketPlacementIds.length) return;
    beginStatusTableBusy(
      ticketPlacementIds.length > 1 ? 'Delisting tickets…' : 'Delisting ticket…',
    );
    setStatusChangeLoading(true);
    try {
      const { successIds, message } = await executeAdminOrSuperUserDelistRest(ticketPlacementIds);
      const failedCount = ticketPlacementIds.filter((id) => !successIds.includes(id)).length;
      if (!successIds.length) {
        throw new Error('Failed to delist ticket(s)');
      }
      updateLocalTicketStatus(successIds, 'Delist');
      if (failedCount > 0) {
        showError(`${failedCount} ticket(s) could not be delisted`);
      } else {
        success(
          message ||
            (successIds.length > 1
              ? `Delisted ${successIds.length} tickets successfully`
              : 'Ticket delisted successfully'),
        );
      }
      if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to delist ticket(s)'));
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
  };

  /** One Logitix BulkUpdate per selection (same as mobile Delist All) — keeps VL groups intact. */
  const executeAdminOrSuperUserDelistRest = async (ticketPlacementIds: string[]) => {
    if (!ticketPlacementIds.length) {
      return { successIds: [] as string[], message: '' };
    }
    const data = await callDelistTicketsRest(ticketPlacementIds, getDelistRequestEmail());
    const message = assertDelistTicketsRestSuccess(
      data,
      "We couldn't complete the delist request. Please try again.",
    );
    const successIds: string[] =
      Array.isArray(data?.successTpIds) && data.successTpIds.length > 0
        ? data.successTpIds
        : ticketPlacementIds;
    return { successIds, message: message || 'Ticket delisted successfully' };
  };

  async function callReturnTicketsRest(ticketPlacementId: string[]) {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/returnTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementId },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to return tickets';
      throw new Error(message);
    }
    return responseData;
  };

  async function executeReturnTicketsRest(ticketPlacementId: string[]) {
    const response = await callReturnTicketsRest(ticketPlacementId);
    const message = String(response?.message || '');
    const type = resolveActionMessageType(message);
    if (type === 'error') throw new Error(message || 'Failed to return tickets');
    return { message, type };
  };

  const callMarkTicketsUnsoldRest = async (ticketPlacementIds: string[]) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const response = await fetch(`${nodeApiBaseUrl}/api/markTicketsUnsold`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { ticketPlacementIds }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to mark ticket as Unsold';
      throw new Error(message);
    }
    return responseData;
  };
const callDeleteTicketRest = async (ticketPlacementId: string) => {
  const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
  const hasuraRole = localStorage.getItem('x-hasura-user-role');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

  const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
  if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

  const response = await fetch(`${nodeApiBaseUrl}/api/deleteTicket`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      input: { ticketPlacementId }
    })
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to delete ticket';
    throw new Error(message);
  }
  return responseData;
};

  const callPublishTicketsRest = async (ticketPlacementIds: string[], listPrice: number) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/publishTickets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          publishTicketInput: {
            ticketPlacementIds,
            listPrice
          }
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to publish tickets';
      throw new Error(message);
    }
    return responseData;
  };

  const callMarkAsSoldRest = async (
    ticketPlacementId: string,
    soldAmount: number,
    logitixAmount: number,
    marketplaceName: string
  ) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/markAsSold`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementId,
          soldAmount,
          logitixAmount,
          marketplaceName
        }
      })
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to mark ticket as sold';
      throw new Error(message);
    }
    return responseData;
  };



  // ---------- Marketplace persistence (same as User Details) ----------
const getMarketplaceCache = (userId: string): Record<string, string> => {
  try {
    const cached = localStorage.getItem(`marketplace_cache_${userId}`);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

const saveMarketplaceCache = (userId: string, tpId: string, marketplace: string) => {
  try {
    const cache = getMarketplaceCache(userId);
    cache[tpId] = marketplace;
    localStorage.setItem(`marketplace_cache_${userId}`, JSON.stringify(cache));
    console.log(`✅ CACHED marketplace for ${tpId} (user ${userId}): ${marketplace}`);
  } catch (err) {
    console.warn('Failed to cache marketplace:', err);
  }
};

  const callUpdateSettlementPaymentStatusRest = async (
    ticketPlacementIds: string[],
    paymentStatus: string | null,
  ) => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    const response = await fetch(`${nodeApiBaseUrl}/api/updateSettlementPaymentStatus`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: {
          ticketPlacementIds,
          paymentStatus,
        },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to update settlement status';
      throw new Error(message);
    }
    return responseData;
  };

  const { data: usersListData } = useQuery(GET_USERS_LIST, {
    skip: !shouldLoadUsersList,
    variables: {
      pageSize: 2000,
      pageOffset: 0,
      searchText: "%",
      order_by: [{ u_created_at: "desc" }]
    },
    fetchPolicy: 'cache-first',
    onError: (err) => {
      console.error('Failed to load users list for client type mapping:', err);
    }
  });

  const getClientDisplayName = (ticket: Ticket | null | undefined): string => {
    if (!ticket) return 'N/A';
    const explicitName = ticket.u_full_name?.trim();
    if (explicitName) return explicitName;

    const splitName = `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim();
    if (splitName) return splitName;

    return ticket.u_email_id?.trim() || 'N/A';
  };

  const getResolvedClientType = (ticket: any): 'self-pricing' | 'concierge' | '' => {
    const ticketType = String(ticket?.u_pricing_mode || '').trim().toLowerCase();
    if (ticketType === 'self-pricing' || ticketType === 'concierge') return ticketType;
    const usersListType = String(
      (usersListData?.user || []).find((u: any) => u?.u_id === ticket?.u_id)?.u_pricing_mode || ''
    )
      .trim()
      .toLowerCase();
    if (usersListType === 'self-pricing' || usersListType === 'concierge') return usersListType as 'self-pricing' | 'concierge';
    const byUserId = String(resolvedClientTypeByUserId?.[ticket?.u_id] || '').trim().toLowerCase();
    if (byUserId === 'self-pricing' || byUserId === 'concierge') return byUserId as 'self-pricing' | 'concierge';
    return '';
  };

  const resolveSoldFeeConfigForTicket = async (ticket: Ticket): Promise<SoldFeeConfig> => {
    const clientType = getResolvedClientType(ticket);

    if (clientType === 'self-pricing') {
      const percent = ticket.u_id
        ? await resolveSelfPricingClientPercent(ticket.u_id)
        : SELF_PRICING_FEE_FALLBACK;
      return { feeMode: 'super', superClientPercent: percent };
    }

    if (ticket.u_id) {
      const conciergePercent = await fetchUserFeePercent(ticket.u_id);
      if (conciergePercent != null) {
        return { feeMode: 'super', superClientPercent: conciergePercent };
      }
    }

    return DEFAULT_SOLD_FEE_CONFIG;
  };

  async function loadBulkSoldFeeConfigs(tickets: Ticket[]): Promise<Record<string, SoldFeeConfig>> {
    if (!tickets.length) return {};
    const configByUserKey = new Map<string, SoldFeeConfig>();
    const configs: Record<string, SoldFeeConfig> = {};

    await Promise.all(
      tickets.map(async (ticket) => {
        const userKey = ticket.u_id || ticket.tp_id;
        if (!configByUserKey.has(userKey)) {
          configByUserKey.set(userKey, await resolveSoldFeeConfigForTicket(ticket));
        }
        configs[ticket.tp_id] = configByUserKey.get(userKey)!;
      })
    );

    return configs;
  };

const getTargetPricingMode = (): string => {
  if (clientType === 'Self Pricing') return 'self-pricing';
  if (clientType === 'Concierge') return 'concierge';
  return '';
};

  useEffect(() => {
    const userIdsToResolve = Array.from(
      new Set(
        (filteredTickets || [])
          .map((ticket: any) => ticket?.u_id)
          .filter((uId: string | undefined) => !!uId && !resolvedClientTypeByUserId[uId])
      )
    );
    if (!userIdsToResolve.length) return;

    let cancelled = false;
    (async () => {
      const resolvedMap: Record<string, string> = {};
      for (const uId of userIdsToResolve) {
        try {
          const { data: userFeeData } = await getUserFeePercentById({ variables: { u_id: uId } });
          const feePercent = Number(userFeeData?.user_by_pk?.u_fee_percentage);
          if (Number.isFinite(feePercent) && feePercent > 0) {
            // Existing business rule: self-pricing users are configured at 15%.
            resolvedMap[uId] = feePercent <= 15 ? 'self-pricing' : 'concierge';
          }
        } catch (err) {
          console.error('Failed resolving client type from fee percent:', err);
        }
      }
      if (!cancelled && Object.keys(resolvedMap).length) {
        setResolvedClientTypeByUserId((prev) => ({ ...prev, ...resolvedMap }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filteredTickets, resolvedClientTypeByUserId, getUserFeePercentById]);

  const [markAsSoldMutation, { loading: markAsSoldLoading }] = useMutation(MARK_AS_SOLD, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (markAsSoldUiHandledRef.current) return;
      const rawMsg = (data?.markAsSold?.message || '').toString();
      const normalized = rawMsg.toLowerCase();
      if (!normalized.includes('success')) {
        showError(rawMsg || 'Failed to mark ticket as sold');
        markAsSoldPendingTpIdRef.current = null;
        return;
      }
      markAsSoldUiHandledRef.current = true;
      const tpId = markAsSoldPendingTpIdRef.current;
      if (tpId) {
        updateLocalTicketStatus([tpId], 'Sold');
      }
      success(rawMsg || 'Ticket marked as sold successfully');
      closeSoldModal();
      // Auto-switch to Sold tab when ticket is marked as sold
      if (activeTab !== 'Sold') {
        setTimeout(() => switchTabUnlessEventSearch('Sold'), 300);
      }
      void refetch();
      void refetchExpired();
      void refetchFailed();
      markAsSoldPendingTpIdRef.current = null;
      globalThis.setTimeout(() => {
        markAsSoldUiHandledRef.current = false;
      }, 0);
    },
  });

  // Mark as Sold
const markTicketsAsSold = async (tpIds: string[]) => {
  let updatedCount = 0;
  let failedCount = 0;
  const updatedIds: string[] = [];

  await withSuppressedBulkMutationToasts(async () => {
    for (const tpId of tpIds) {
      try {
        const res = await updateTicketStatus({
          variables: {
            tp_id: tpId,
            status: 'Sold'
          }
        });
        const affected = res?.data?.update_ticket_placement?.returning?.length || 0;
        if (affected > 0) {
          updatedCount += 1;
          updatedIds.push(tpId);
        } else {
          failedCount += 1;
        }
      } catch {
        failedCount += 1;
      }
    }
  });

  if (updatedIds.length > 0) {
    updateLocalTicketStatus(updatedIds, 'Sold');
  }

  if (updatedCount > 0 && failedCount === 0) {
    success(`${updatedCount} ticket(s) marked as sold successfully`);
    if (activeTab !== 'Sold') {
        setTimeout(() => switchTabUnlessEventSearch('Sold'), 300);
    }
  } else if (updatedCount > 0) {
    info(`${updatedCount} ticket(s) marked as sold. ${failedCount} failed.`);
    if (activeTab !== 'Sold') {
        setTimeout(() => switchTabUnlessEventSearch('Sold'), 300);
    }
  } else {
    showError('Failed to mark selected tickets as sold');
  }

  refetch();
  refetchExpired();
  refetchFailed();
  handleBulkActionComplete();
};

const handleBulkActionComplete = () => {
  setSelectedSeats({});
  setSelectAllSeats(false);
  setShowBulkActions(false);
  setSelectedTpIds([]);
  setBulkSelectedTicketIds(new Set());
  setBulkSelectedTickets([]);
};

const getLocalDayRangeUTC = (dateStr: string): { startUTC: string; endUTC: string; startTimestamp: string; endTimestamp: string } | null => {
  if (!dateStr) return null;
  
  // GraphQL parameters expect 'date' type (YYYY-MM-DD format) for date filters,
  // and timestamptz for timestamp-based event date comparisons.
  return {
    startUTC: dateStr,
    endUTC: dateStr,
    startTimestamp: `${dateStr}T00:00:00Z`,
    endTimestamp: `${dateStr}T23:59:59Z`,
  };
};
  // ==================== DATE FILTER HELPERS ====================
const getTodayStr = () => {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
const getTomorrowStr = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const day = String(tomorrow.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
  const getNext7DaysEndStr = () => {
    const next7 = new Date();
    next7.setDate(next7.getDate() + 6);  // +6 gives exactly 7 days (today through 6 days from now)
    return next7.toISOString().split('T')[0];
  };

  const applyDatePreset = (preset: 'today' | 'tomorrow' | 'next7days') => {
    setDatePreset(preset);
    setCustomRangeStart('');
    setCustomRangeEnd('');
    if (preset === 'today') {
      const todayStr = getTodayStr();
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === 'tomorrow') {
      const tomorrowStr = getTomorrowStr();
      setStartDate(tomorrowStr);
      setEndDate(tomorrowStr);
    } else if (preset === 'next7days') {
      const todayStr = getTodayStr();
      const endStr = getNext7DaysEndStr();
      setStartDate(todayStr);
      setEndDate(endStr);
    }
    setPageOffset(0);
  };

  const clearDateFilter = () => {
    setDatePreset(null);
    setCustomRangeStart('');
    setCustomRangeEnd('');
    setStartDate('');
    setEndDate('');
    setPageOffset(0);
  };

  const applyCustomRange = () => {
    if (customRangeStart && customRangeEnd) {
      setDatePreset(null);
      setStartDate(customRangeStart);
      setEndDate(customRangeEnd);
      setShowCustomRangeModal(false);
      setPageOffset(0);
    } else {
      info('Please select both start and end dates for custom range');
    }
  };

  const getActiveDateFilterLabel = () => {
    if (datePreset === 'today') return 'Today';
    if (datePreset === 'tomorrow') return 'Tomorrow';
    if (datePreset === 'next7days') return 'Next 7 days';
    if (startDate && endDate && (startDate !== '' || endDate !== '')) {
      if (startDate === endDate) return startDate;
      return `${startDate} → ${endDate}`;
    }
    return null;
  };

  // ==================== FILTER OPTIONS ====================

const getFilterStatusOptions = () => {
  const options = [];
  
  switch (activeTab) {
    case 'Verification':
      options.push(
        { value: 'All Verification', label: `All Verification (${filteredStatusCounts['All Verification'] || 0})` },
        { value: 'ToBeVerified', label: `To Be Verified (${filteredStatusCounts['ToBeVerified'] || 0})` },
        { value: 'Verified', label: `Verified (${filteredStatusCounts['Verified'] || 0})` }
      );
      break;
    case 'Listed':
      options.push(
        { value: 'All Listed', label: `All Listed (${filteredStatusCounts['All Listed'] || 0})` },
        { value: 'List', label: `Listed (${filteredStatusCounts['List'] || 0})` }
      );
      break;
    case 'Sold':
      options.push(
        { value: 'All Sold', label: `All Sold (${filteredStatusCounts['All Sold'] || 0})` },
        { value: 'Sold', label: `Sold (${filteredStatusCounts['Sold'] || 0})` },
        { value: 'Settled', label: `Settled (${filteredStatusCounts['Settled'] || 0})` },
        { value: 'SettlementInProgress', label: `Settlement In Progress (${filteredStatusCounts['SettlementInProgress'] || 0})` },
        { value: 'Failed', label: `Failed (${filteredStatusCounts['Failed'] || 0})` }
      );
      break;
    case 'Delisted':
      options.push(
        { value: 'All Delisted', label: `All Delisted (${filteredStatusCounts['All Delisted'] || 0})` },
        { value: 'DelistInProgress', label: `Delist Requested (${filteredStatusCounts['DelistInProgress'] || 0})` },
        { value: 'Delist', label: `Delisted (${filteredStatusCounts['Delist'] || 0})` },
        { value: 'Return', label: `Returned (${filteredStatusCounts['Return'] || 0})` }
      );
      break;
    case 'Listing Request Cancelled':
      options.push(
        { value: 'All Listing Request Cancelled', label: `All Listing Request Cancelled (${filteredStatusCounts['All Listing Request Cancelled'] || 0})` },
        { value: 'ListingRequestCancelled', label: `Listing Request Cancelled (${filteredStatusCounts['ListingRequestCancelled'] || 0})` }
      );
      break;
    case 'Listing Expired':
      options.push(
        { value: 'All Expired', label: `All Expired (${filteredStatusCounts['All Expired'] || 0})` },
        { value: 'Closed', label: `Listing Request Expired (${filteredStatusCounts['Closed'] || 0})` }
      );
      break;
    case 'Unsold':
      options.push(
        { value: 'All Unsold', label: `All Unsold (${filteredStatusCounts['All Unsold'] || 0})` },
        { value: 'Unsold', label: `Unsold (${filteredStatusCounts['Unsold'] || 0})` }
      );
      break;
    case 'Deleted':
      options.push(
        { value: 'All Deleted', label: `All Deleted (${filteredStatusCounts['All Deleted'] || 0})` },
        { value: 'Deleted', label: `Deleted (${filteredStatusCounts['Deleted'] || 0})` }
      );
      break;
    default:
      options.push(
        { value: 'All Verification', label: `All Verification (${filteredStatusCounts['All Verification'] || 0})` },
        { value: 'ToBeVerified', label: `To Be Verified (${filteredStatusCounts['ToBeVerified'] || 0})` },
        { value: 'Verified', label: `Verified (${filteredStatusCounts['Verified'] || 0})` }
      );
  }
  
  return options;
};

  const getDefaultStatusByTab = (tab: string) => {
    switch (tab) {
      case 'Verification': return 'All Verification';
      case 'Listed': return 'All Listed';
      case 'Sold': return 'All Sold';
      case 'Delisted': return 'All Delisted';
      case 'Listing Request Cancelled': return 'All Listing Request Cancelled';
      case 'Listing Expired': return 'All Expired';
      case 'Unsold': return 'All Unsold';
      case 'Deleted': return 'All Deleted';
      default: return isSuperUser ? 'All Listed' : 'All Verification';
    }
  };

  const getDefaultStatus = () => getDefaultStatusByTab(activeTab);

  useEffect(() => {
    if (!isSuperUser) return;
    if (!SUPER_USER_MANAGE_TABS.includes(activeTab as (typeof SUPER_USER_MANAGE_TABS)[number])) {
      setActiveTab("Listed");
    }
  }, [isSuperUser, activeTab]);

  useEffect(() => {
    if (!globalState?.error?.errorClicked) return;
    // When tp ids are present, resolve the tab from live ticket status (cross-tab fetch).
    if (getMailParserErrorTpIds(globalState.error)?.length) return;
    const tab = getManageTicketsTabFromErrorStatus(globalState.error.status || '');
    setActiveTab(tab);
    setFilterStatus(getDefaultStatusByTab(tab));
    setPageOffset(0);
  }, [
    globalState.error.errorClicked,
    globalState.error.status,
    globalState.error.errorSearchCancel,
    globalState.error.ticketPlacementId,
    globalState.error.approximateTpArray,
  ]);

  useEffect(() => {
    setFilterStatus(getDefaultStatus());
    setPageOffset(0);
  }, [activeTab]);

  const filterStatusOptions = getFilterStatusOptions();

  // ==================== DATA FETCHING ====================

  const { data: salesRepLookupData } = useQuery<SalesRepLookupResponse>(GET_SALES_REPS_BY_EMAIL, {
    skip: !isMarketingUser || !loggedInEmail,
    variables: { email: loggedInEmail },
    fetchPolicy: "network-only",
  });
  const scopedSalesRepId = salesRepLookupData?.sales_reps?.[0]?.sr_id || null;

  const { data: managedUsersByRepData } = useQuery<{ managedUsers: Array<{ u_id: string }> }>(GET_MANAGED_USERS_BY_SALES_REP, {
    skip: !isMarketingUser || !scopedSalesRepId,
    variables: { salesRepId: scopedSalesRepId },
    fetchPolicy: "network-only",
  });

  const managedUserIds = useMemo(
    () => new Set((managedUsersByRepData?.managedUsers || []).map((user) => user.u_id)),
    [managedUsersByRepData]
  );

  const trackingCountsDeferralReadyRef = useRef(false);

useEffect(() => {
  const delayMs = trackingCountsDeferralReadyRef.current ? 0 : 2500;
  trackingCountsDeferralReadyRef.current = true;
  const timer = window.setTimeout(() => {
    void fetchTrackingCounts();
  }, delayMs);
  return () => window.clearTimeout(timer);
}, [isMarketingUser, managedUserIds.size, startDate, endDate, eventVenueSearch, clientSearch]);

  // Users list is only needed as fallback when ticket rows lack u_pricing_mode.
  useEffect(() => {
    const timer = window.setTimeout(() => setShouldLoadUsersList(true), 4000);
    return () => window.clearTimeout(timer);
  }, []);

  const { data: leaguesData } = useQuery<{ leagues: League[] }>(GET_LEAGUES_LIST, {
    fetchPolicy: 'cache-first',
  });

  const { data: marketplacesData, loading: marketplacesLoading, error: marketplacesError, refetch: refetchMarketplaces } = useQuery(GET_MARKETPLACES_FOR_TICKETS, {
    fetchPolicy: 'cache-first',
  });

  const soldTicketTpIds = useMemo(() => {
    if (activeTab !== 'Sold') return [] as string[];
    return filteredTickets.map((ticket) => ticket.tp_id).filter(Boolean);
  }, [activeTab, filteredTickets]);

  const { data: soldMarketplaceData } = useQuery(GET_TICKET_MARKETPLACES_BY_TP_IDS, {
    variables: { tpIds: soldTicketTpIds },
    skip: activeTab !== 'Sold' || soldTicketTpIds.length === 0,
    fetchPolicy: 'cache-first',
  });

  const marketplaceByTpId = useMemo(() => {
    const map = new Map<string, string>();
    const rows = Array.isArray(soldMarketplaceData?.ticket_placement)
      ? soldMarketplaceData.ticket_placement
      : [];
    rows.forEach((row: { tp_id?: string; tp_marketplace_name?: string | null }) => {
      const tpId = String(row?.tp_id ?? '').trim();
      const name = typeof row?.tp_marketplace_name === 'string' ? row.tp_marketplace_name.trim() : '';
      if (tpId && name) map.set(tpId, name);
    });
    return map;
  }, [soldMarketplaceData]);

  const getTicketMarketplaceName = useCallback((ticket: Ticket): string => {
    const fromApi = marketplaceByTpId.get(ticket.tp_id);
    if (fromApi) return fromApi;
    if (ticket.u_id) {
      const cached = getMarketplaceCache(ticket.u_id)[ticket.tp_id];
      if (cached && String(cached).trim()) return String(cached).trim();
    }
    return '';
  }, [marketplaceByTpId]);

  const marketplacesList: MarketplaceOption[] = useMemo(() => {
    const dbList: MarketplaceOption[] = Array.isArray(marketplacesData?.marketplaces)
      ? marketplacesData.marketplaces
      : [];
    return dbList
      .filter((marketplace) => marketplace.active ?? true)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [marketplacesData]);

  const bulkSoldActiveInStatusModal =
    showBulkStatusModal && normalizeStatusValue(bulkNewStatus) === 'Sold';

  useEffect(() => {
    if (!bulkSoldActiveInStatusModal || !bulkSoldMarketplaceId || !bulkSoldFeeConfigReady) return;
    const mp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
    if (!mp || bulkSelectedTickets.length === 0) return;

    setBulkSoldLogitixAmounts(prev => {
      const next = { ...prev };
      bulkSelectedTickets.forEach(ticket => {
        const list = Number(ticket.tp_list_price) || 0;
        const expected = Math.round(list * (100 - mp.vl_fee_percentage)) / 100;
        next[ticket.tp_id] = String(Number(roundToFixed(expected, 2)));
      });
      return next;
    });
  }, [
    bulkSoldActiveInStatusModal,
    bulkSoldMarketplaceId,
    bulkSoldFeeConfigReady,
    bulkSelectedTickets,
    marketplacesList,
  ]);

  useEffect(() => {
    if (!showSoldModal || !soldModalTicket || !soldMarketplaceId || !soldFeeConfigReady) return;
    const mp = marketplacesList.find(m => m.id === soldMarketplaceId);
    if (!mp) return;
    const list = Number(soldModalTicket.tp_list_price) || 0;
    const expected = Math.round(list * (100 - mp.vl_fee_percentage)) / 100;
    setSoldLogitixAmount(String(Number(roundToFixed(expected, 2))));
  }, [
    showSoldModal,
    soldMarketplaceId,
    soldModalTicket?.tp_id,
    soldModalTicket?.tp_list_price,
    soldFeeConfigReady,
    marketplacesList,
  ]);

  const isMailParserErrorView = Boolean(globalState?.error?.errorClicked);
  const effectiveTab = isMailParserErrorView
    ? getManageTicketsTabFromErrorStatus(globalState.error.status || '')
    : activeTab;
  const mailParserErrorTpIds = isMailParserErrorView
    ? getMailParserErrorTpIds(globalState.error)
    : null;
  const isMailParserTpLookupView = isMailParserErrorView && Boolean(mailParserErrorTpIds?.length);

  useEffect(() => {
    effectiveTabRef.current = effectiveTab;
  }, [effectiveTab]);

  const ticketsQueryKey = JSON.stringify({
    tab: effectiveTab,
    mailParser: isMailParserErrorView
      ? {
          status: globalState.error.status,
          cancel: globalState.error.errorSearchCancel,
          tpIds: mailParserErrorTpIds,
          text: globalState.error.text,
        }
      : null,
    pageOffset,
    pageSize,
    filterStatus,
    clientType,
    selectedLeague,
    startDate,
    endDate,
    eventVenueSearch,
    clientSearch,
    cardFilter,
  });
  if (prevTicketsQueryKeyRef.current !== ticketsQueryKey) {
    prevTicketsQueryKeyRef.current = ticketsQueryKey;
    queryFetchIdRef.current += 1;
    hasAppliedActiveTabDataRef.current = false;
  }
  const ticketsQueryFetchId = queryFetchIdRef.current;

  const getActiveQuery = () => {
    switch (effectiveTab) {
      case 'Verification': return GET_MANAGE_TICKETS;
      case 'Verified': return GET_MANAGE_TICKETS;
      case 'Listed': return GET_LIST_TICKETS;
      case 'Sold': return GET_SOLD_TICKETS;
      case 'Delisted': return GET_DELIST_AND_RETURN_TICKETS;
      case 'Listing Request Cancelled': return GET_LISTING_REQUEST_CANCELLED_TICKETS;
      case 'Listing Expired': return GET_EXPIRED_TICKETS;
      case 'Unsold': return GET_DELIST_AND_UNSOLD_TICKETS;
      case 'Deleted': return GET_DELETED_TICKETS;
      default: return GET_LIST_TICKETS;
    }
  };

  const getQueryByTab = (tab: string) => {
    switch (tab) {
      case 'Verification': return GET_MANAGE_TICKETS;
      case 'Verified': return GET_MANAGE_TICKETS;
      case 'Listed': return GET_LIST_TICKETS;
      case 'Sold': return GET_SOLD_TICKETS;
      case 'Delisted': return GET_DELIST_AND_RETURN_TICKETS;
      case 'Listing Request Cancelled': return GET_LISTING_REQUEST_CANCELLED_TICKETS;
      case 'Listing Expired': return GET_EXPIRED_TICKETS;
      case 'Unsold': return GET_DELIST_AND_UNSOLD_TICKETS;
      case 'Deleted': return GET_DELETED_TICKETS;
      default: return GET_LIST_TICKETS;
    }
  };

  // Event view only needs row payloads — avoid heavy tab queries with sibling aggregates/notes.
  const getEventViewQueryByTab = (tab: string) => {
    switch (tab) {
      case 'Verification': return EVENT_VIEW_GET_MANAGE_TICKETS;
      case 'Listed': return EVENT_VIEW_GET_LIST_TICKETS;
      case 'Sold': return EVENT_VIEW_GET_SOLD_TICKETS;
      case 'Delisted': return EVENT_VIEW_GET_DELIST_AND_RETURN_TICKETS;
      case 'Listing Request Cancelled': return EVENT_VIEW_GET_LISTING_REQUEST_CANCELLED_TICKETS;
      case 'Deleted': return EVENT_VIEW_GET_DELETED_TICKETS;
      default: return EVENT_VIEW_GET_LIST_TICKETS;
    }
  };

  const getTicketStatus = (): string | null => {
    if (isMailParserErrorView) {
      return null;
    }

    // Verification loads full tab results and filters fail-to-transfer rows in the browser.
    if (effectiveTab === 'Verification') {
      return null;
    }

    switch (effectiveTab) {
      case 'Verified':
        return filterStatus === 'Verified' ? filterStatus : 'Verified';
      case 'Listed':
        return filterStatus === 'All Listed' ? null : filterStatus;
      case 'Sold':
        return filterStatus === 'All Sold' ? null : filterStatus;
      case 'Delisted':
        return filterStatus === 'All Delisted' ? null : filterStatus;
      case 'Listing Request Cancelled':
        return filterStatus === 'All Listing Request Cancelled' ? null : filterStatus;
      case 'Listing Expired':
        return filterStatus === 'All Expired' ? null : 'Closed';
      case 'Unsold':
        return filterStatus === 'All Unsold' ? null : filterStatus;
      case 'Listing Request Cancelled':
        return filterStatus === 'ListingRequestCancelled' ? filterStatus : 'ListingRequestCancelled';
      default:
        return null;
    }
  };

// Update the getQueryVariables function (around line 1260-1300) to include proper pricingMode filtering:
const [fetchDeletedCountQuery, { data: deletedCountData }] = useLazyQuery(GET_DELETED_TICKETS, {
  fetchPolicy: 'network-only',
});


const fetchDeletedCount = useCallback(() => {
  const vars: any = {
    pageSize: 1,
    pageOffset: 0,
    search_event: eventVenueSearch ? `%${eventVenueSearch}%` : '%',
    search_client: clientSearch ? `%${clientSearch}%` : '%',
    pricingMode: clientType === 'Self Pricing' ? 'self-pricing' 
                : clientType === 'Concierge' ? 'concierge' 
                : '%',
  };

  // --- FIX: Convert local dates to UTC ranges ---
  if (startDate) {
    const range = getLocalDayRangeUTC(startDate);
    if (range) vars.startdate = range.startUTC;
  } else {
    vars.startdate = '1970-01-01T00:00:00Z';
  }
  if (endDate) {
    const range = getLocalDayRangeUTC(endDate);
    if (range) vars.enddate = range.endUTC;
  } else {
    vars.enddate = '9999-12-31T23:59:59Z';
  }

  if (selectedLeague) vars.leagueId = selectedLeague;

  Object.keys(vars).forEach((k) => {
    const v = vars[k];
    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
      delete vars[k];
    }
  });

  fetchDeletedCountQuery({ variables: vars });
}, [
  eventVenueSearch,
  clientSearch,
  startDate,
  endDate,
  selectedLeague,
  clientType,
  fetchDeletedCountQuery,
]);

useEffect(() => {
  const count = deletedCountData?.ticket_placement_aggregate?.aggregate?.count;
  if (count !== undefined && count !== null) {
    setTabCounts((prev) => ({ ...prev, Deleted: count }));
  }
}, [deletedCountData]);
const getQueryVariables = () => {
  const useServerPagination = shouldPreferServerPagination({
    isEventGroupedView: hasActiveEventSearch(eventVenueSearch),
    isMailParserTpLookupView,
    isMailParserErrorView,
    isMarketingUser,
    effectiveTab,
  });
  const shouldLoadFullTabResults =
    !useServerPagination &&
    usesClientSidePaginationForTab(effectiveTab, {
      isMailParserErrorView,
      isMarketingUser,
    });
  const effectivePageSize = shouldLoadFullTabResults ? 5000 : pageSize;
  const effectivePageOffset = shouldLoadFullTabResults ? 0 : pageOffset;

  const baseVars: any = {
    pageSize: effectivePageSize,
    pageOffset: effectivePageOffset,
    search_event: eventVenueSearch ? `%${eventVenueSearch}%` : "%",
    search_client: clientSearch ? `%${clientSearch}%` : "%",
    verificationCutoff: verificationCutoffDate,
    order_by: effectiveTab === 'Deleted' ? [{ tp_updated_at: 'desc' }, { tp_id: 'asc' }] : MANAGE_TICKETS_ORDER_BY,
  };

  if (globalState?.error?.errorClicked) {
    if (globalState.error.text === "Error-filter" || globalState.error.text === "Success-filter") {
      baseVars.search_event = "%";
      baseVars.search_client = "%";
    }
    const tpFilter = getMailParserErrorTpIds(globalState.error);
    if (tpFilter) {
      baseVars.array_tpid = tpFilter;
    }
  } else {
    // --- FIX: Convert local dates to UTC ranges ---
    if (startDate) {
      const range = getLocalDayRangeUTC(startDate);
      if (range) {
        baseVars.startdate = range.startUTC;
        baseVars.startDateTime = range.startTimestamp;
      }
    } else {
      baseVars.startdate = '1970-01-01';
      baseVars.startDateTime = '1970-01-01T00:00:00Z';
    }
    if (endDate) {
      const range = getLocalDayRangeUTC(endDate);
      if (range) {
        baseVars.enddate = range.endUTC;
        baseVars.endDateTime = range.endTimestamp;
      }
    } else {
      baseVars.enddate = '9999-12-31';
      baseVars.endDateTime = '9999-12-31T23:59:59Z';
    }

    if (selectedLeague) baseVars.leagueId = selectedLeague;

    const status = getTicketStatus();
    if (status) baseVars.ticketStatus = status;
  }

  const pricingModeMap: Record<string, string> = {
    'Self Pricing': 'self-pricing',
    'Concierge': 'concierge',
  };
  baseVars.pricingMode = isMailParserErrorView || clientType === 'All'
    ? '%'
    : (pricingModeMap[clientType] ?? '%');

  if (cardFilter === "FAILED_TRANSFER") baseVars.transferStatus = "Failed";
  if (cardFilter === "RETURN_FAILED") baseVars.returnStatus = "Failed";

  if (effectiveTab === 'Sold') {
    baseVars.paymentStatus = getSoldPaymentStatusForQuery(filterStatus);
    baseVars.payoutType = null;
  }

  if (effectiveTab === 'Delisted') {
    baseVars.delistedStatuses = getDelistedStatusesForQuery(filterStatus);
  }

  Object.keys(baseVars).forEach((k) => {
    const v = (baseVars as any)[k];
    if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
      delete (baseVars as any)[k];
    }
  });

  return baseVars;
};

  const isEventGroupedView = hasActiveEventSearch(eventVenueSearch) &&
    isEventGroupedTab(activeTab) &&
    isDefaultLocalStatusFilter(activeTab, filterStatus);
  const serverPaginationActive = shouldPreferServerPagination({
    isEventGroupedView,
    isMailParserTpLookupView,
    isMailParserErrorView,
    isMarketingUser,
    effectiveTab,
  });
  const usesLocalStatusFiltering =
    !isEventGroupedView &&
    !isMailParserErrorView &&
    isLocalStatusFilterTab(effectiveTab) &&
    !serverPaginationActive;

  const usesClientSidePagination =
    !isEventGroupedView &&
    !serverPaginationActive &&
    (isMailParserTpLookupView ||
      usesClientSidePaginationForTab(effectiveTab, {
        isMailParserErrorView,
        isMarketingUser,
      }));

  const scopeTicketsForDisplay = useCallback(
    (tickets: Ticket[]): Ticket[] => {
      const scoped = isMarketingUser
        ? tickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : tickets;
      const targetPricingMode = getTargetPricingMode();
      const filtered = targetPricingMode
        ? scoped.filter((ticket) => getResolvedClientType(ticket) === targetPricingMode)
        : scoped;
      return sortManageTickets(filtered);
    },
    [isMarketingUser, managedUserIds, clientType],
  );

  const extractTabTicketsFromResponse = (tab: string, responseData: any): Ticket[] => {
    if (tab === 'Verification') return filterVerificationTickets(responseData?.filtermanagetickets || []);
    if (tab === 'Verified') return (responseData?.filtermanagetickets || []).filter((t: any) => t.tp_status === 'Verified');
    if (tab === 'Listed') return responseData?.filterlisttickets || [];
    if (tab === 'Sold') return responseData?.FilterSoldTickets || [];
    if (tab === 'Delisted') return responseData?.filterreturntickets || [];
    if (tab === 'Deleted') return mapDeletedPlacementRows(responseData?.ticket_placement);
    if (tab === 'Listing Request Cancelled') return responseData?.filterListingRequestCancelledTickets || [];
    if (tab === 'Unsold') return responseData?.filterUnsoldTickets || [];
    if (tab === 'Listing Expired') return mergeExpiredTicketsResponse(responseData);
    return [];
  };

  const fetchEventGroupedTickets = useCallback(async () => {
    if (!eventVenueSearch.trim() || eventVenueSearch.trim().length < 2) return;
    if (isMarketingUser && (!scopedSalesRepId || managedUserIds.size === 0)) {
      setEventGroupedByStatus(createEmptyEventGroupedTickets());
      setFilteredTickets([]);
      setTotalCount(0);
      return;
    }

    const fetchId = ++eventGroupedFetchIdRef.current;
    setEventGroupedLoading(true);
    try {
      const baseVars: Record<string, unknown> = {
        pageSize: 5000,
        pageOffset: 0,
        search_event: `%${eventVenueSearch.trim()}%`,
        search_client: clientSearch ? `%${clientSearch}%` : '%',
        verificationCutoff: verificationCutoffDate,
        order_by: MANAGE_TICKETS_ORDER_BY,
      };
      if (startDate) {
        const range = getLocalDayRangeUTC(startDate);
        baseVars.startdate = range?.startUTC ?? startDate;
      }
      if (endDate) {
        const range = getLocalDayRangeUTC(endDate);
        baseVars.enddate = range?.endUTC ?? endDate;
      }
      if (selectedLeague) baseVars.leagueId = selectedLeague;
      // Always fetch all client types (don't filter by pricingMode at backend)
      // We apply client type filter locally so we can calculate accurate counts
      baseVars.pricingMode = '%';

      // Clean up null/empty variables
      Object.keys(baseVars).forEach((k) => {
        const v = (baseVars as any)[k];
        if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
          delete (baseVars as any)[k];
        }
      });

      // Deleted uses ticket_placement + timestamptz dates + a different order_by type.
      const deletedVars: Record<string, unknown> = {
        pageSize: 5000,
        pageOffset: 0,
        search_event: `%${eventVenueSearch.trim()}%`,
        search_client: clientSearch ? `%${clientSearch}%` : '%',
        order_by: [{ tp_updated_at: 'desc' }, { tp_id: 'asc' }],
        startdate: startDate ? (getLocalDayRangeUTC(startDate)?.startUTC ?? `${startDate}T00:00:00Z`) : '1970-01-01T00:00:00Z',
        enddate: endDate ? (getLocalDayRangeUTC(endDate)?.endUTC ?? `${endDate}T23:59:59Z`) : '9999-12-31T23:59:59Z',
        pricingMode: '%',
      };

      const extractRows = (tab: string, responseData: Record<string, unknown>): Ticket[] => {
        if (tab === 'Verification') return filterVerificationTickets((responseData?.filtermanagetickets as Ticket[]) || []);
        if (tab === 'Listed') return (responseData?.filterlisttickets as Ticket[]) || [];
        if (tab === 'Sold') return (responseData?.FilterSoldTickets as Ticket[]) || [];
        if (tab === 'Delisted') return (responseData?.filterreturntickets as Ticket[]) || [];
        if (tab === 'Deleted') return mapDeletedPlacementRows(responseData?.ticket_placement as any[]);
        if (tab === 'Listing Request Cancelled') {
          return (responseData?.filterListingRequestCancelledTickets as Ticket[]) || [];
        }
        return [];
      };

      const tabsToFetch = [
        'Verification',
        'Listed',
        'Sold',
        'Delisted',
        'Listing Request Cancelled',
        'Deleted',
      ] as const;
      const settled = await Promise.allSettled(
        tabsToFetch.map(async (tab) => {
          const { data: responseData } = await apolloClient.query({
            query: getEventViewQueryByTab(tab),
            variables: tab === 'Deleted' ? deletedVars : baseVars,
            fetchPolicy: 'network-only',
          });
          return [tab, extractRows(tab, responseData)] as const;
        }),
      );

      // Ignore stale responses if the user changed search while requests were in-flight.
      if (fetchId !== eventGroupedFetchIdRef.current) return;

      const byTab: Record<string, Ticket[]> = {};
      for (const result of settled) {
        if (result.status === 'fulfilled') {
          const [tab, rows] = result.value;
          byTab[tab] = rows;
        } else {
          console.error('[ManageTickets] Event-group tab fetch failed:', result.reason);
        }
      }

      // Also fetch Unsold counts scoped to the event search using the full unsold query
      let unsoldCount: number | undefined = undefined;
      try {
        const { data: unsoldData } = await apolloClient.query({
          query: GET_DELIST_AND_UNSOLD_TICKETS,
          variables: baseVars,
          fetchPolicy: 'network-only',
        });
        unsoldCount = unsoldData?.filterUnsoldTickets_aggreagate?.aggregate?.count ??
          unsoldData?.filterUnsoldTickets_aggreagate?.aggregate?.count ??
          undefined;
      } catch (unsoldErr) {
        console.error('[ManageTickets] Event-group unsold fetch failed:', unsoldErr);
      }

      // Also fetch Listing Expired count scoped to the event search
      let expiredCount: number | undefined = undefined;
      try {
        const { data: expiredData } = await apolloClient.query({
          query: GET_EXPIRED_TICKETS,
          variables: baseVars,
          fetchPolicy: 'network-only',
        });
        expiredCount = expiredData?.closed_placement_aggregate?.aggregate?.count ?? expiredData?.filterreturntickets_aggregate?.aggregate?.count ?? undefined;
      } catch (unsoldErr) {
        console.error('[ManageTickets] Event-group unsold fetch failed:', unsoldErr);
      }
      const overrides = localStatusOverridesRef.current;
      const scopedTickets = [
        ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Verification || []), overrides),
        ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Listed || []), overrides),
        ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Sold || []), overrides),
        ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Delisted || []), overrides),
        ...applyStatusOverrides(scopeTicketsForDisplay(byTab['Listing Request Cancelled'] || []), overrides),
        ...applyStatusOverrides(scopeTicketsForDisplay(byTab.Deleted || []), overrides),
      ];
      const grouped = distributeEventGroupedTickets(scopedTickets);
      const visibleTickets = getEventGroupedTicketsForTab(activeTab, grouped);

      setEventGroupedByStatus(grouped);
      setFilteredTickets(sortManageTickets(visibleTickets));
      setTotalCount(visibleTickets.length);
      setEventGroupedUnsoldCount(typeof unsoldCount === 'number' ? unsoldCount : 0);
      setEventGroupedListingExpiredCount(typeof expiredCount === 'number' ? expiredCount : 0);
      // Update tab badges for event-grouped view. Include Unsold if fetched above.
      const delistedTickets = grouped.delisted ?? [];
      const listingRequestCancelledCount = delistedTickets.filter(
        (ticket) => ticket.tp_status === 'ListingRequestCancelled',
      ).length;
      const delistedCount = delistedTickets.length - listingRequestCancelledCount;
      setTabCounts((prev) => ({
        ...prev,
        Verification: grouped.pending.length,
        Listed: grouped.listed.length,
        Sold: grouped.sold.length,
        Delisted: delistedCount,
        'Listing Request Cancelled': listingRequestCancelledCount,
        Deleted: grouped.deleted.length,
        Unsold: (typeof unsoldCount === 'number' ? unsoldCount : prev.Unsold ?? 0),
      }));
    } catch (eventGroupError) {
      if (fetchId !== eventGroupedFetchIdRef.current) return;
      console.error('[ManageTickets] Failed to load event-grouped tickets:', eventGroupError);
      setEventGroupedByStatus(createEmptyEventGroupedTickets());
      setFilteredTickets([]);
      setTotalCount(0);
    } finally {
      if (fetchId === eventGroupedFetchIdRef.current) {
        setEventGroupedLoading(false);
      }
    }
  }, [
    activeTab,
    eventVenueSearch,
    clientSearch,
    startDate,
    endDate,
    selectedLeague,
    clientType,
    verificationCutoffDate,
    apolloClient,
    isMarketingUser,
    scopedSalesRepId,
    managedUserIds,
    scopeTicketsForDisplay,
  ]);

  const applyTicketsTableState = useCallback((
    tickets: Ticket[],
    totalCountValue: number,
    tabForScope: string,
    skipTabScope = false,
  ) => {
    let resolvedTickets = tickets;
    let resolvedTotalCount = totalCountValue;

    if (mailParserErrorTpIds?.length) {
      const idSet = new Set(mailParserErrorTpIds);
      resolvedTickets = resolvedTickets.filter((ticket) => idSet.has(ticket.tp_id));
      resolvedTotalCount = resolvedTickets.length;
    }

    const scopedTickets = isMarketingUser
      ? resolvedTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
      : resolvedTickets;
    const tabScopedTickets = skipTabScope
      ? scopedTickets
      : tabForScope === 'Verification'
      ? filterVerificationTickets(scopedTickets)
      : tabForScope === 'Verified'
      ? scopedTickets.filter((ticket) => ticket.tp_status === 'Verified')
      : scopedTickets;

    setUnfilteredTabTickets(tabScopedTickets);

    const targetPricingMode = isMailParserErrorView ? '' : getTargetPricingMode();
    const clientFilteredTickets = targetPricingMode
      ? tabScopedTickets.filter((ticket: any) => getResolvedClientType(ticket) === targetPricingMode)
      : tabScopedTickets;
    const scopedTotalCount = isMarketingUser
      ? tabScopedTickets.length
      : resolvedTotalCount;
      

    const mergedTickets = applyStatusOverrides(
      clientFilteredTickets,
      localStatusOverridesRef.current,
    );
    reconcileStatusOverrides(localStatusOverridesRef.current, clientFilteredTickets);
    const sortedMergedTickets = sortManageTickets(mergedTickets);
    setStatusFilterSourceTickets(sortedMergedTickets);

    const tabServerPaginationActive = shouldPreferServerPagination({
      isEventGroupedView,
      isMailParserTpLookupView,
      isMailParserErrorView,
      isMarketingUser,
      effectiveTab: tabForScope,
    });
    const activeLocalStatusFilters = getLocalStatusFiltersFromFilterStatus(tabForScope, filterStatus);
    const tabUsesLocalStatusFiltering =
      !isEventGroupedView && !isMailParserErrorView && isLocalStatusFilterTab(tabForScope) && !tabServerPaginationActive;

    const visibleTickets = tabUsesLocalStatusFiltering
      ? sortManageTickets(applyLocalStatusFilters(sortedMergedTickets, tabForScope, activeLocalStatusFilters))
      : sortedMergedTickets;

    const tabUsesClientSidePagination =
      !tabServerPaginationActive &&
      usesClientSidePaginationForTab(tabForScope, {
        isMailParserErrorView,
        isMarketingUser,
      });
    const displayCount = tabServerPaginationActive
      ? scopedTotalCount
      : tabUsesClientSidePagination || tabUsesLocalStatusFiltering
        ? visibleTickets.length
        : scopedTotalCount;

    setFilteredTickets(visibleTickets);
    setTotalCount(displayCount);

    const counts = (() => {
      const baseCounts = calculateFilteredCounts(sortedMergedTickets, tabForScope);
      if (tabUsesClientSidePagination || tabUsesLocalStatusFiltering) {
        return baseCounts;
      }
      return applyAggregateStatusCounts(baseCounts, tabForScope, scopedTotalCount);
    })();
    if (
      !(
        tabServerPaginationActive &&
        (tabForScope === 'Sold' || tabForScope === 'Delisted')
      )
    ) {
      setFilteredStatusCounts(counts);
    }

    const tabBadgeCount = displayCount;

    setTabCounts(prev => {
      if (prev[tabForScope] === tabBadgeCount) return prev;
      return { ...prev, [tabForScope]: tabBadgeCount };
    });

    if (tabForScope === effectiveTabRef.current) {
      hasAppliedActiveTabDataRef.current = true;
    }
  }, [
    mailParserErrorTpIds,
    isMarketingUser,
    managedUserIds,
    isMailParserErrorView,
    isMailParserTpLookupView,
    isEventGroupedView,
    filterStatus,
    clientType,
  ]);

  const processActiveTabQueryData = useCallback((responseData: any, tab: string) => {
    if (tab !== effectiveTabRef.current) return;
    const parsed = extractTicketsFromActiveTabQuery(responseData, tab);
    if (!parsed) return;

    applyTicketsTableState(parsed.tickets, parsed.totalCountValue, tab);

    if (tab === 'Sold' && responseData?.FilterSoldTickets_soldOpen_aggregate) {
      setFilteredStatusCounts(extractSoldStatusCountsFromResponse(responseData));
    }

    if (tab === 'Delisted' && responseData?.FilterDelisted_delist_aggregate) {
      setFilteredStatusCounts(extractDelistedStatusCountsFromResponse(responseData));
    }

    const siblingCounts = extractSiblingTabCountsFromResponse(responseData);
    if (Object.keys(siblingCounts).length > 0) {
      // Only refresh inactive tab badges; active tab count comes from applyTicketsTableState.
      setTabCounts((prev) => {
        const next = { ...prev };
        for (const [siblingTab, count] of Object.entries(siblingCounts)) {
          if (siblingTab !== tab) {
            next[siblingTab] = count;
          }
        }
        return next;
      });
    }
  }, [applyTicketsTableState]);

  useEffect(() => {
    processQueryDataForTableRef.current = processActiveTabQueryData;
  }, [processActiveTabQueryData]);

  const fetchMailParserErrorTickets = useCallback(async () => {
    if (!isMailParserTpLookupView || !mailParserErrorTpIds?.length) return;
    if (isMarketingUser && (!scopedSalesRepId || managedUserIds.size === 0)) {
      setFilteredTickets([]);
      setTotalCount(0);
      setUnfilteredTabTickets([]);
      return;
    }

    setMailParserErrorLoading(true);
    try {
      const idSet = new Set(mailParserErrorTpIds);
      const baseVars: Record<string, unknown> = {
        pageSize: 5000,
        pageOffset: 0,
        search_event: '%',
        search_client: '%',
        verificationCutoff: verificationCutoffDate,
        order_by: MANAGE_TICKETS_ORDER_BY,
        array_tpid: mailParserErrorTpIds,
        // omit leagueId/startdate/enddate/ticketStatus to avoid sending explicit nulls
        pricingMode: '%',
      };

      // Clean up null/empty variables
      Object.keys(baseVars).forEach((k) => {
        const v = (baseVars as any)[k];
        if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) {
          delete (baseVars as any)[k];
        }
      });

      const tabsToFetch = [
        'Verification',
        'Listed',
        'Sold',
        'Delisted',
        'Listing Request Cancelled',
        'Unsold',
        'Listing Expired',
      ] as const;

      const responses = await Promise.all(
        tabsToFetch.map(async (tab) => {
          const { data: responseData } = await apolloClient.query({
            query: getQueryByTab(tab),
            variables: baseVars,
            fetchPolicy: 'network-only',
          });
          return extractTabTicketsFromResponse(tab, responseData);
        }),
      );

      const matchingTickets = sortManageTickets(
        responses.flat().filter((ticket) => idSet.has(ticket.tp_id)),
      );
      const scopedTickets = isMarketingUser
        ? matchingTickets.filter((ticket) => managedUserIds.has(ticket.u_id))
        : matchingTickets;

      let resolvedTab = effectiveTab;
      if (scopedTickets.length > 0) {
        resolvedTab = getTabForStatus(scopedTickets[0].tp_status || '');
        setActiveTab(resolvedTab);
        setFilterStatus(getDefaultStatusByTab(resolvedTab));
      }

      applyTicketsTableState(scopedTickets, scopedTickets.length, resolvedTab, true);
    } catch (mailParserFetchError) {
      console.error('[ManageTickets] Failed to load mail parser tickets:', mailParserFetchError);
      setFilteredTickets([]);
      setTotalCount(0);
      setUnfilteredTabTickets([]);
    } finally {
      setMailParserErrorLoading(false);
    }
  }, [
    isMailParserTpLookupView,
    mailParserErrorTpIds,
    isMarketingUser,
    scopedSalesRepId,
    managedUserIds,
    verificationCutoffDate,
    apolloClient,
    effectiveTab,
    applyTicketsTableState,
  ]);

  useEffect(() => {
    if (!isMailParserTpLookupView) return;
    void fetchMailParserErrorTickets();
  }, [
    isMailParserTpLookupView,
    fetchMailParserErrorTickets,
    globalState?.error?.errorSearchCancel,
    globalState?.error?.ticketPlacementId,
    globalState?.error?.approximateTpArray,
  ]);

  useEffect(() => {
    if (!showClientDropdown) return;
    if (
      isEventGroupedView ||
      isMailParserTpLookupView ||
      isMarketingUser ||
      effectiveTab === 'Verification'
    ) {
      return;
    }
    if (lastClientTypeCountsQueryKeyRef.current === ticketsQueryKey) return;

    let isCancelled = false;

    const fetchClientTypeCounts = async () => {
      setClientTypeCountsLoading(true);
      try {
        const tab = effectiveTab;
        const query = getQueryByTab(tab);
        const sharedVars = {
          ...getQueryVariables(),
          pageSize: 1,
          pageOffset: 0,
        };

        const [allResult, selfResult, conciergeResult] = await Promise.all([
          apolloClient.query({ query, variables: { ...sharedVars, pricingMode: '%' }, fetchPolicy: 'network-only' }),
          apolloClient.query({ query, variables: { ...sharedVars, pricingMode: 'self-pricing' }, fetchPolicy: 'network-only' }),
          apolloClient.query({ query, variables: { ...sharedVars, pricingMode: 'concierge' }, fetchPolicy: 'network-only' }),
        ]);

        if (isCancelled) return;

        setClientTypeCounts({
          All: extractTicketsFromActiveTabQuery(allResult.data, tab)?.totalCountValue ?? 0,
          'Self Pricing': extractTicketsFromActiveTabQuery(selfResult.data, tab)?.totalCountValue ?? 0,
          Concierge: extractTicketsFromActiveTabQuery(conciergeResult.data, tab)?.totalCountValue ?? 0,
        });
        lastClientTypeCountsQueryKeyRef.current = ticketsQueryKey;
      } catch (countFetchError) {
        console.error('[ManageTickets] Failed to load client type counts:', countFetchError);
      } finally {
        if (!isCancelled) setClientTypeCountsLoading(false);
      }
    };

    void fetchClientTypeCounts();

    return () => {
      isCancelled = true;
    };
  }, [
    showClientDropdown,
    ticketsQueryKey,
    verificationCutoffDate,
    isEventGroupedView,
    isMailParserTpLookupView,
    isMarketingUser,
    apolloClient,
    effectiveTab,
  ]);

  useEffect(() => {
    if (
      effectiveTab !== 'Verification' ||
      isEventGroupedView ||
      isMailParserTpLookupView
    ) {
      return;
    }

    const sourceTickets = unfilteredTabTickets;
    const selfPricingTickets = sourceTickets.filter((t) => getResolvedClientType(t) === 'self-pricing');
    const conciergeTickets = sourceTickets.filter((t) => getResolvedClientType(t) === 'concierge');

    setClientTypeCounts({
      All: sourceTickets.length,
      'Self Pricing': selfPricingTickets.length,
      Concierge: conciergeTickets.length,
    });
  }, [
    effectiveTab,
    isEventGroupedView,
    isMailParserTpLookupView,
    unfilteredTabTickets,
    usersListData,
    resolvedClientTypeByUserId,
  ]);

useEffect(() => {
    if (!isEventGroupedView) {
      setEventGroupedByStatus(createEmptyEventGroupedTickets());
      return;
    }
    void fetchEventGroupedTickets();
  }, [isEventGroupedView, fetchEventGroupedTickets]);

  useEffect(() => {
    if (!isEventGroupedView) return;

    const delistedTickets = eventGroupedByStatus.delisted ?? [];
    const listingRequestCancelledCount = delistedTickets.filter(
      (ticket) => ticket.tp_status === 'ListingRequestCancelled',
    ).length;
    const delistedCount = delistedTickets.length - listingRequestCancelledCount;

    setTabCounts((prev) => ({
      ...prev,
      Verification: eventGroupedByStatus.pending.length,
      Listed: eventGroupedByStatus.listed.length,
      Sold: eventGroupedByStatus.sold.length,
      Delisted: delistedCount,
      'Listing Request Cancelled': listingRequestCancelledCount,
      Deleted: eventGroupedByStatus.deleted.length,
    }));

    const activeTabTickets = getEventGroupedTicketsForTab(activeTab, eventGroupedByStatus);
    setFilteredTickets(sortManageTickets(activeTabTickets));
    setTotalCount(activeTabTickets.length);
    setFilteredStatusCounts(calculateFilteredCounts(activeTabTickets, activeTab));
  }, [isEventGroupedView, eventGroupedByStatus, activeTab]);

  const { loading, error: queryError, data, refetch: refetchActiveTab } = useQuery<any>(getActiveQuery(), {
    variables: getQueryVariables(),
    skip: isEventGroupedView || isMailParserTpLookupView,
    fetchPolicy: 'network-only',
    onCompleted: (responseData) => {
      if (ticketsQueryFetchId !== queryFetchIdRef.current) return;
      processActiveTabQueryData(responseData, effectiveTabRef.current);
    },
  });

  useEffect(() => {
    if (isEventGroupedView || isMailParserTpLookupView || !data) return;
    if (ticketsQueryFetchId !== queryFetchIdRef.current) return;
    processActiveTabQueryData(data, effectiveTabRef.current);
  }, [
    data,
    ticketsQueryFetchId,
    isEventGroupedView,
    isMailParserTpLookupView,
    processActiveTabQueryData,
  ]);

  useEffect(() => {
    refreshTicketsTableRef.current = () => {
      if (isEventGroupedView) return fetchEventGroupedTickets();
      if (isMailParserTpLookupView) return fetchMailParserErrorTickets();
      return refetchActiveTab();
    };
  }, [isEventGroupedView, isMailParserTpLookupView, fetchEventGroupedTickets, fetchMailParserErrorTickets, refetchActiveTab]);

  // Debounce event/venue search
  useEffect(() => {
    const timer = setTimeout(() => {
      setEventVenueSearch(debouncedEventVenueSearch);
      setPageOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedEventVenueSearch]);

  // Debounce client search - FIXED: This sets clientSearch from debouncedClientSearch
  useEffect(() => {
    const timer = setTimeout(() => {
      setClientSearch(debouncedClientSearch);
      setPageOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [debouncedClientSearch]);

  // Reset page offset when filters change
  useEffect(() => {
    setPageOffset(0);
  }, [filterStatus, clientType, selectedLeague, startDate, endDate, activeTab, eventVenueSearch, clientSearch]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rawUntil = sessionStorage.getItem('manageTicketsBulkProcessingUntil');
    const refreshUntil = rawUntil ? Number(rawUntil) : 0;
    if (!Number.isFinite(refreshUntil) || refreshUntil <= Date.now()) {
      if (rawUntil) sessionStorage.removeItem('manageTicketsBulkProcessingUntil');
      return;
    }

    // Bulk add is queued asynchronously on backend; poll briefly so user sees rows appear.
    const intervalId = window.setInterval(() => {
      if (Date.now() >= refreshUntil) {
        sessionStorage.removeItem('manageTicketsBulkProcessingUntil');
        window.clearInterval(intervalId);
        return;
      }
      void refreshTicketsTableRef.current?.();
      void fetchTrackingCounts();
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (clientType === 'All') return;
    if (!usersListData?.user?.length) return;
    void refreshTicketsTableRef.current?.();
  }, [usersListData, clientType]);

  useEffect(() => {
    if (!isMarketingUser) return;
    if (!scopedSalesRepId) return;
    void refreshTicketsTableRef.current?.();
  }, [isMarketingUser, scopedSalesRepId, managedUserIds.size]);

  // Recalculate filtered counts when filteredTickets or activeTab changes (client-paginated tabs only).
  useEffect(() => {
    if (!usesLocalStatusFiltering) return;

    const tabUsesClientPagination = usesClientSidePaginationForTab(activeTab, {
      isMailParserErrorView,
      isMarketingUser,
    });
    if (!tabUsesClientPagination) return;

    const sourceTickets = statusFilterSourceTickets.length > 0 ? statusFilterSourceTickets : filteredTickets;
    if (sourceTickets.length > 0) {
      setFilteredStatusCounts(calculateFilteredCounts(sourceTickets, activeTab));
    } else {
      // Reset counts when no tickets
      const emptyCounts: Record<string, number> = {};
      switch (activeTab) {
        case 'Verification':
          emptyCounts['All Verification'] = 0;
          emptyCounts['ToBeVerified'] = 0;
          emptyCounts['Verified'] = 0;
          break;
        case 'Listed':
          emptyCounts['All Listed'] = 0;
          emptyCounts['List'] = 0;
          break;
        case 'Sold':
          emptyCounts['All Sold'] = 0;
          emptyCounts['Sold'] = 0;
          emptyCounts['Settled'] = 0;
          emptyCounts['SettlementInProgress'] = 0;
          emptyCounts['Failed'] = 0;
          break;
        case 'Delisted':
          emptyCounts['All Delisted'] = 0;
          emptyCounts['DelistInProgress'] = 0;
          emptyCounts['Delist'] = 0;
          emptyCounts['Return'] = 0;
          break;
        case 'Listing Request Cancelled':
          emptyCounts['All Listing Request Cancelled'] = 0;
          emptyCounts['ListingRequestCancelled'] = 0;
          break;
        case 'Listing Expired':
          emptyCounts['All Expired'] = 0;
          emptyCounts['Closed'] = 0;
          break;
        case 'Unsold':
          emptyCounts['All Unsold'] = 0;
          emptyCounts['Unsold'] = 0;
          break;
      }
      setFilteredStatusCounts(emptyCounts);
    }
  }, [filteredTickets, statusFilterSourceTickets, activeTab, usesLocalStatusFiltering, isMarketingUser, isMailParserErrorView]);

  // --- Fetch Deleted count on mount and whenever filters change ---
useEffect(() => {
  const timer = setTimeout(() => {
    void refreshTicketsTableRef.current?.();
    void fetchTrackingCounts();
    fetchDeletedCount();
  }, 300);
  return () => clearTimeout(timer);
}, [
  fetchDeletedCount,
  eventVenueSearch,
  clientSearch,
  startDate,
  endDate,
  selectedLeague,
  clientType,
]);

useEffect(() => {
  if (!isEventGroupedView) return;
  
  const allTickets = [
    ...eventGroupedByStatus.pending,
    ...eventGroupedByStatus.listed,
    ...eventGroupedByStatus.sold,
    ...eventGroupedByStatus.delisted,
    ...eventGroupedByStatus.deleted,
  ];
  
  const selfPricingTickets = allTickets.filter(t => getResolvedClientType(t) === 'self-pricing');
  const conciergeTickets = allTickets.filter(t => getResolvedClientType(t) === 'concierge');
  
  setClientTypeCounts({
    All: allTickets.length,
    'Self Pricing': selfPricingTickets.length,
    'Concierge': conciergeTickets.length
  });
}, [isEventGroupedView, eventGroupedByStatus, usersListData, resolvedClientTypeByUserId]);
// useEffect(() => {
//   // Use filteredTickets for accurate count based on all active filters
//   const sourceTickets = usesLocalStatusFiltering ? statusFilterSourceTickets : filteredTickets;
//   const selfPricingTickets = sourceTickets.filter(t => {
//     const clientType = getResolvedClientType(t);
//     return clientType === 'self-pricing';
//   });
//   const conciergeTickets = sourceTickets.filter(t => {
//     const clientType = getResolvedClientType(t);
//     return clientType === 'concierge';
//   });
//   const allCount = sourceTickets.length;
//   const selfPricingCount = selfPricingTickets.length;
//   const conciergeCount = conciergeTickets.length;

//   setClientTypeCounts({
//     'All': allCount,
//     'Self Pricing': selfPricingCount,
//     'Concierge': conciergeCount
//   });
// }, [statusFilterSourceTickets, filteredTickets, usesLocalStatusFiltering, usersListData, resolvedClientTypeByUserId]);
  const handleOpenCardDetailModal = async (modalType: string) => {
    const getModalTrackingTickets = (responseData: any): Ticket[] => {
      if (modalType === 'LISTING_EXPIRED') {
        return mergeExpiredTicketsResponse(responseData);
      }
      if (Array.isArray(responseData?.ticket_placement)) return responseData.ticket_placement as Ticket[];
      if (Array.isArray(responseData?.filterreturntickets)) return responseData.filterreturntickets as Ticket[];
      if (Array.isArray(responseData?.filtermanagetickets)) return responseData.filtermanagetickets as Ticket[];
      return [];
    };

    const getModalTrackingCount = (responseData: any): number => {
      if (modalType === 'LISTING_EXPIRED') {
        return getExpiredTicketsCount(responseData);
      }
      return (
        responseData?.ticket_placement_aggregate?.aggregate?.count ||
        responseData?.filterreturntickets_aggregate?.aggregate?.count ||
        responseData?.filtermanagetickets_aggregate?.aggregate?.count ||
        0
      );
    };

    cardDetailRequestIdRef.current += 1;
    const requestId = cardDetailRequestIdRef.current;

    setShowCardDetailModal(modalType);
    setCardDetailTickets([]);
    setCardDetailLoading(true);

    try {
      let ticketsToShow: Ticket[] = [];

      if (modalType === "LISTING_EXPIRED") {
        const trackingVariables = {
          ...getManageTicketsTrackingVariables(
            eventVenueSearch ? `%${eventVenueSearch}%` : '%',
            clientSearch ? `%${clientSearch}%` : '%',
          ),
          ...getQueryVariables(),
        };
        if (startDate) {
          const range = getLocalDayRangeUTC(startDate);
          (trackingVariables as Record<string, unknown>).startdate = range?.startUTC ?? startDate;
        } else {
          (trackingVariables as Record<string, unknown>).startdate = '1970-01-01T00:00:00Z';
        }
        if (endDate) {
          const range = getLocalDayRangeUTC(endDate);
          (trackingVariables as Record<string, unknown>).enddate = range?.endUTC ?? endDate;
        } else {
          (trackingVariables as Record<string, unknown>).enddate = '9999-12-31T23:59:59Z';
        }
        const { data } = await refetchExpired(trackingVariables);
        ticketsToShow = sortManageTickets(normalizeCardDetailTrackingRows(getModalTrackingTickets(data)));
        setTrackingCounts(prev => ({
          ...prev,
          listingExpired: getModalTrackingCount(data)
        }));
      } else if (modalType === "FAILED_TRANSFER") {
        const cutoffDate = getFailedTransferCutoffDate();
        const trackingVariables = getManageTicketsTrackingVariables(
          eventVenueSearch ? `%${eventVenueSearch}%` : '%',
          clientSearch ? `%${clientSearch}%` : '%',
        );
        if (startDate) {
          const range = getLocalDayRangeUTC(startDate);
          (trackingVariables as Record<string, unknown>).startdate = range?.startUTC ?? startDate;
        } else {
          (trackingVariables as Record<string, unknown>).startdate = '1970-01-01T00:00:00Z';
        }
        if (endDate) {
          const range = getLocalDayRangeUTC(endDate);
          (trackingVariables as Record<string, unknown>).enddate = range?.endUTC ?? endDate;
        } else {
          (trackingVariables as Record<string, unknown>).enddate = '9999-12-31T23:59:59Z';
        }
        const { data } = await refetchFailed(trackingVariables);
        ticketsToShow = sortManageTickets(normalizeCardDetailTrackingRows(
          filterFailedTransferByCutoff(getModalTrackingTickets(data), cutoffDate)
        ));
        setTrackingCounts(prev => ({
          ...prev,
          failedTransfer: ticketsToShow.length
        }));
      }

      if (requestId !== cardDetailRequestIdRef.current) {
        return;
      }

      setCardDetailTickets(ticketsToShow);
      setCardDetailLoading(false);

      if (ticketsToShow.length === 0) {
        info(`No ${modalType === 'LISTING_EXPIRED' ? 'expired' : 'failed transfer'} tickets found`);
      }
    } catch {
      if (requestId !== cardDetailRequestIdRef.current) {
        return;
      }

      setCardDetailLoading(false);
      showError('Failed to load ticket details');
    }
  };

  // ==================== EXPORT HANDLERS ====================
const handleExport = () => {
  if (filteredTickets.length === 0) {
    info('No tickets to export');
    return;
  }

  const formattedTickets = filteredTickets.map((ticket: any) => ({
    ...ticket,
    'Event Time': formatEventTimeForCsv(ticket)
  }));

  exportToCSV(formattedTickets, {
    filename: `${activeTab.toLowerCase()}_tickets`,
    includeHeaders: true,
    includeSeatDetails: false,
    includeReturnEmail: shouldIncludeReturnEmailInExport(activeTab),
    includeCommission: shouldIncludeCommissionInExport(activeTab),
    dateFormat: exportOptions.dateFormat,
    delimiter: exportOptions.delimiter
  });

  setShowExportDropdown(false);
  success('Export started successfully');
};

  const handleAdvancedExport = () => {
    if (filteredTickets.length === 0) {
      info('No tickets to export');
      return;
    }
    setShowExportModal(true);
    setShowExportDropdown(false);
  };

  const handleExportSelected = () => {
    if (selectedTpIds.length === 0) {
      info('No tickets selected');
      return;
    }
    const selectedTickets = filteredTickets.filter(ticket => selectedTpIds.includes(ticket.tp_id));
    exportToCSV(selectedTickets, {
      filename: `selected_tickets_${selectedTpIds.length}`,
      includeHeaders: true,
      includeSeatDetails: exportOptions.includeSeatDetails,
      includeReturnEmail: shouldIncludeReturnEmailInExport(activeTab),
      includeCommission: shouldIncludeCommissionInExport(activeTab),
      dateFormat: exportOptions.dateFormat,
      delimiter: exportOptions.delimiter
    });
    setShowExportDropdown(false);
    success(`Exported ${selectedTpIds.length} tickets`);
  };

  const confirmExport = () => {
    exportToCSV(filteredTickets, {
      filename: `${activeTab.toLowerCase()}_tickets_advanced`,
      ...exportOptions,
      includeReturnEmail: shouldIncludeReturnEmailInExport(activeTab),
      includeCommission: shouldIncludeCommissionInExport(activeTab),
    });
    setShowExportModal(false);
    success(`Exported ${filteredTickets.length} tickets`);
  };

  // ==================== HANDLERS ====================

  const handleTabChange = (tab: string) => {
    if (isSuperUser && tab === 'Unsold') {
      showError('Only admins can access unsold tickets.');
      return;
    }
    const nextTab =
      isSuperUser && !SUPER_USER_MANAGE_TABS.includes(tab as (typeof SUPER_USER_MANAGE_TABS)[number])
        ? "Listed"
        : tab;
    setActiveTab(nextTab);
    setFilterStatus(getDefaultStatusByTab(nextTab));
    setPageOffset(0);
    setSelectedSeats({});
    setSelectAllSeats(false);
    setShowBulkActions(false);
    setExpandedRows([]);
    setCardFilter(null);
  };

  function switchTabUnlessEventSearch(tab: string) {
    if (hasActiveEventSearch(eventVenueSearch)) return;
    handleTabChange(tab);
  }


  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      await fetchTrackingCounts();
      await refetch();
      await fetchDeletedCount();
      success('Data refreshed successfully');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newOffset: number) => {
    setPageOffset(newOffset);
    setSelectedSeats({});
    setSelectAllSeats(false);
    setShowBulkActions(false);
    setExpandedRows([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageOffset(0);
    setSelectedSeats({});
    setSelectAllSeats(false);
    setShowBulkActions(false);
    setExpandedRows([]);
  };

  const clearAllFilters = () => {
    setFilterStatus(getDefaultStatus());
    setClientType('All');
    setSelectedLeague('');
    setDebouncedEventVenueSearch("");
    setEventVenueSearch("");
    setDebouncedClientSearch("");
    setClientSearch("");
    clearDateFilter();
    setCardFilter(null);
    setPageOffset(0);
    success('Filters cleared');
  };

  // ==================== SEAT UTILITIES ====================

  const parseSeatNumbers = (seatNo: string | number | null | undefined): string[] =>
    parseAndSortSeatNumbers(seatNo);

  const generateSeats = (ticket: Ticket): Seat[] => {
    const seatNumbers = parseSeatNumbers(ticket.tp_seat_no);
    const soldAmount = Number(ticket.tp_sold_amount ?? 0);
    const listAmount = Number(ticket.tp_list_price ?? 0);
    const seatPrice =
      normalizeStatusValue(ticket.tp_status || '') === 'Sold' && Number.isFinite(soldAmount) && soldAmount > 0
        ? soldAmount
        : Number.isFinite(listAmount)
          ? listAmount
          : 0;
    return seatNumbers.map((seatNum, index) => ({
      id: `${ticket.tp_id}-seat-${index}`,
      ticketId: ticket.tp_id,
      number: seatNum,
      section: ticket.tp_section || 'N/A',
      row: ticket.tp_row || 'N/A',
      status: ticket.tp_status || 'Unknown',
      price: seatPrice,
      isDelisted: ['Delist', 'DelistInProgress', 'DelistRequested', 'Return'].includes(ticket.tp_status || ''),
      // isDelisted: ['Delist', 'DelistInProgress', 'DelistRequested', 'Return'].includes(ticket.tp_status || ''),
      isMarked: false,
      tp_id: ticket.tp_id
    }));
  };

  const getAllSeats = (): Seat[] => {
    return currentPageTickets.flatMap(ticket =>
      expandedRows.includes(ticket.tp_id) ? generateSeats(ticket) : []
    );
  };

  // ==================== SELECTION HANDLERS ====================

  const handleSelectAllSeats = () => {
    const newSelectAll = !selectAllSeats;
    setSelectAllSeats(newSelectAll);
    const newSelectedSeats: { [key: string]: boolean } = {};
    if (newSelectAll) {
      getAllSeats().forEach(seat => newSelectedSeats[seat.id] = true);
    }
    setSelectedSeats(newSelectedSeats);
    setShowBulkActions(newSelectAll);
    const tpIdSet = new Set<string>();
    Object.keys(newSelectedSeats).forEach(id => {
      const seat = getAllSeats().find(s => s.id === id);
      if (seat?.tp_id) tpIdSet.add(seat.tp_id);
    });
    setSelectedTpIds(Array.from(tpIdSet));
  };

  const handleSelectSeat = (seatId: string) => {
    setSelectedSeats(prev => {
      const newSelected = { ...prev };
      if (newSelected[seatId]) {
        delete newSelected[seatId];
      } else {
        newSelected[seatId] = true;
      }
      const totalSeats = getAllSeats().length;
      const selectedCount = Object.keys(newSelected).length;
      setSelectAllSeats(selectedCount === totalSeats && totalSeats > 0);
      setShowBulkActions(selectedCount > 0);
      const tpIdSet = new Set<string>();
      Object.keys(newSelected).forEach(id => {
        const seat = getAllSeats().find(s => s.id === id);
        if (seat?.tp_id) tpIdSet.add(seat.tp_id);
      });
      setSelectedTpIds(Array.from(tpIdSet));
      return newSelected;
    });
  };

  const handlePriceUpdateSuccess = () => {
    handleBulkActionComplete();
    setShowPriceModal(false);
    setTimeout(() => {
      refetch();
      fetchTrackingCounts();
    }, 100);
  };

  const getDefaultDelistEmail = (tpIds: string[]): string => {
    const firstMatch = filteredTickets.find((ticket) => tpIds.includes(ticket.tp_id));
    return (firstMatch?.u_email_id || firstMatch?.u_original_email || '').trim();
  };

  const openDelistEmailModal = (tpIds: string[]) => {
    setPendingDelistTpIds(tpIds);
    const defaultEmail = getDefaultDelistEmail(tpIds);
    resetDelistEmail({
      email: defaultEmail,
      confirmEmail: defaultEmail
    });
    setShowDelistEmailModal(true);
  };

  const onDelistEmailSubmit = async (data: { email: string; confirmEmail: string }) => {
    const requestedTpIds = [...pendingDelistTpIds];
    if (!requestedTpIds.length) return;

    const hadPreVerificationTickets = requestedTpIds.some((tpId) => {
      const ticket = filteredTickets.find((row) => row.tp_id === tpId);
      return normalizeStatusValue(ticket?.tp_status || '') === 'ToBeVerified';
    });
    const nextStatus = hadPreVerificationTickets ? 'ListingRequestCancelled' : 'DelistInProgress';

    setShowDelistEmailModal(false);
    beginStatusTableBusy('Submitting delist request…');
    try {
      await executeDelistWithFallback(requestedTpIds, data.email);
      updateLocalTicketStatus(requestedTpIds, nextStatus);
      if (hadPreVerificationTickets) {
        success('Listing request cancelled. Find the ticket under the Listing Request Cancelled tab.');
        setFilterStatus('All Listing Request Cancelled');
      } else {
        success('Delist request submitted. Find the ticket under the Delisted tab as Delist Requested.');
        setFilterStatus('DelistInProgress');
      }
      if (hadPreVerificationTickets) {
        if (activeTab !== 'Listing Request Cancelled') {
          switchTabUnlessEventSearch('Listing Request Cancelled');
        }
      } else if (activeTab !== 'Delisted') {
        switchTabUnlessEventSearch('Delisted');
      }
      await syncTicketsAfterStatusChange();
      await refetchExpired();
      await refetchFailed();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to request delist'));
      await syncTicketsAfterStatusChange();
    } finally {
      endStatusTableBusy();
      setPendingDelistTpIds([]);
      resetDelistEmail();
    }
  };

  const closeDelistEmailModal = () => {
    setShowDelistEmailModal(false);
    setPendingDelistTpIds([]);
    resetDelistEmail();
  };

  const isDelistWebhookUnavailable = (error: any): boolean => {
    const graphQlMsg = error?.graphQLErrors?.[0]?.message || '';
    const message = (error?.message || graphQlMsg || '').toLowerCase();
    return message.includes('not a valid json response from webhook') || message.includes('cannot post /api/delisttickets');
  };

  const setDelistWebhookBypass = (value: boolean) => {
    localStorage.setItem('delistWebhookUnavailable', value ? 'true' : 'false');
  };

  const executeDelistWithFallback = async (ticketPlacementIds: string[], requestedEmailId: string) => {
    // Always try primary delist request first; this is the path that creates notifications.
    try {
      await callDelistTicketsRest(ticketPlacementIds, requestedEmailId);
      setDelistWebhookBypass(false);
      return;
    } catch (error: any) {
      if (isDelistWebhookUnavailable(error)) {
        setDelistWebhookBypass(true);
      }
      throw error;
    }
  };

  const callRelistTicketBySuperUserRest = async (ticketPlacementIds: string | string[]) => {
    const ids = (Array.isArray(ticketPlacementIds) ? ticketPlacementIds : [ticketPlacementIds]).filter(
      Boolean,
    );
    if (!ids.length) {
      throw new Error('No tickets selected for relist');
    }
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes('idToken')) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : '';
    const hasuraRole = localStorage.getItem('x-hasura-user-role');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;

    const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || '').replace(/\/+$/, '');
    if (!nodeApiBaseUrl) throw new Error('Backend API URL not configured');

    const input =
      ids.length === 1
        ? { ticketPlacementId: ids[0] }
        : { ticketPlacementIds: ids };

    const response = await fetch(`${nodeApiBaseUrl}/api/relistTicketBySuperUser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ input }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message =
        responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to relist ticket';
      throw new Error(message);
    }
    return responseData;
  };

  const assertRelistRestSuccess = (response: any) => {
    const message = String(response?.message || '').toLowerCase();
    if (message.includes('fail') || message.includes('error')) {
      throw new Error(response?.message || 'Failed to relist ticket');
    }
  };

  /** One Logitix BulkUpdate per selection — keeps VL groups intact on bulk undo. */
  const executeBatchedUndoDelistWithFallback = async (tpIds: string[]): Promise<void> => {
    if (!tpIds.length) return;
    const response = await callRelistTicketBySuperUserRest(tpIds);
    assertRelistRestSuccess(response);
  };

  const executeUndoDelistWithFallback = async (tpId: string, _currentStatus?: string): Promise<void> => {
    await executeBatchedUndoDelistWithFallback([tpId]);
  };
  type EditDetailsResult = {
    source: 'webhook';
    requiresConfirmation: boolean;
    message: string;
    successFlag: boolean;
  };

  const executeEditDetailsWithFallback = async (
    editTicketsInput: any
  ): Promise<EditDetailsResult> => {
    try {
      const response = await updateTicketDetails({
        variables: {
          editTicketsInput: editTicketsInput
        }
      });
      const editResponse = response?.data?.editTickets;
      const requiresConfirmation = Boolean(editResponse?.requiresConfirmation);

      if (requiresConfirmation) {
        return {
          source: 'webhook',
          requiresConfirmation: true,
          message: editResponse?.message || '',
          successFlag: false
        };
      }

      const message = editResponse?.message || '';
      const normalizedMessage = message.toLowerCase();
      const hasSuccessKeyword = normalizedMessage.includes('success');
      const hasErrorKeyword =
        normalizedMessage.includes('fail') ||
        normalizedMessage.includes('error') ||
        normalizedMessage.includes('already exist') ||
        normalizedMessage.includes('already exists') ||
        normalizedMessage.includes('invalid') ||
        normalizedMessage.includes('unauthorized');
      const successFlag = !requiresConfirmation && hasSuccessKeyword && !hasErrorKeyword;
      return {
        source: 'webhook',
        requiresConfirmation: false,
        message,
        successFlag
      };
    } catch (error: any) {
      throw error;
    }
  };

  const handleBulkAction = async (action: string) => {
    if (selectedTpIds.length === 0) {
      info('No tickets selected');
      return;
    }

    // Add verification conditions for bulk verification
    if (action === 'Validate' || action === 'validate' || action === 'verify' || action === 'Verification') {
      // Only allow tickets that are ToBeVerified and have required fields.
      // Backend performs the authoritative Logitix/date validity checks.
      const invalidTickets = selectedTpIds
        .map(tpId => filteredTickets.find(t => t.tp_id === tpId))
        .filter(ticket => {
          if (!ticket) return true;
          // Only allow ToBeVerified
          if (ticket.tp_status !== 'ToBeVerified') return true;
          // Required fields
          if (
            !ticket.tp_section ||
            !ticket.tp_row ||
            ticket.tp_seat_no === null ||
            ticket.tp_seat_no === undefined ||
            String(ticket.tp_seat_no).trim() === '' ||
            ticket.tp_list_price === null ||
            ticket.tp_list_price === undefined
          ) return true;
          return false;
        });
      if (invalidTickets.length > 0) {
        showError('Some selected tickets cannot be verified. Only "To Be Verified" tickets with section, row, seat and price can be verified.');
        return;
      }
    }

    try {
      switch (action) {
     case 'markSold': {
  if (selectedTpIds.length === 0) {
    info('No tickets selected');
    return;
  }
  const validation = canBulkUpdateTickets(selectedTpIds);
  if (!validation.allowed) {
    showError(validation.reason || 'Cannot perform bulk Sold action on selected tickets');
    return;
  }
  const selectedTickets = filteredTickets.filter(t => selectedTpIds.includes(t.tp_id));
  if (selectedTickets.length === 1) {
    try {
      await openSoldModalForTicket(selectedTickets[0]);
    } catch (err) {
      console.error('Failed to open sold modal for single ticket', err);
      // still try to show modal with defaults
      setSoldModalTicket(selectedTickets[0]);
      setShowSoldModal(true);
    }
    return;  // important: stop here
  }
  setBulkSelectedTickets(selectedTickets);
  await openBulkSoldModal(selectedTickets);
  return;
}

        case 'delist': {
          const listedTpIds = selectedTpIds.filter((tpId) => {
            const row = filteredTickets.find((t) => t.tp_id === tpId);
            return normalizeStatusValue(row?.tp_status || '') === 'List';
          });
          if (listedTpIds.length === selectedTpIds.length) {
            await runListedTicketDelist(listedTpIds);
            return;
          }
          openDelistEmailModal(selectedTpIds);
          return;
        }
        case 'changePrice': {
          if (selectedTpIds.length > 1) {
            openBulkPriceModal(selectedTpIds);
            return;
          }
          const priceValidation = canBulkUpdateTickets(selectedTpIds);
          if (!priceValidation.allowed) {
            showError(priceValidation.reason || 'Cannot change price for selected tickets');
            return;
          }
          setSelectedMarketplaceId('');
          setShowPriceModal(true);
          if (selectedTpIds.length > 0) {
            const firstTicket = filteredTickets.find(t => t.tp_id === selectedTpIds[0]);
            if (firstTicket) {
              setSelectedTicketForPrice(firstTicket);
              setNewPrice(firstTicket.tp_list_price?.toString() || '0');
            }
          }
          return;
        }

        case 'Validate':
          // Bulk verify in one request so response message reflects actual results
          await validateUpdate({
            variables: { ticketPlacementId: selectedTpIds, isValid: true, isUndoRequest: false }
          });
          break;

        default:
          break;
      }

    } catch (error: any) {
      showError(getErrorMessage(error, `Failed to ${action}`));
      handleBulkActionComplete();
    }
  };

  const handleAction = async (action: string, ticket: Ticket, e: React.MouseEvent) => {
    e.stopPropagation();

    const statusActionsNeedingBusy = new Set(['Validate', 'Publish', 'Undo Delist', 'Return']);
    if (statusActionsNeedingBusy.has(action)) {
      beginStatusTableBusy(
        action === 'Validate'
          ? 'Verifying ticket…'
          : action === 'Publish'
            ? 'Publishing to Listed…'
            : action === 'Undo Delist'
              ? 'Moving ticket to Listed…'
              : 'Updating ticket status…',
      );
    }

    try {
      switch (action) {
        case 'Validate': {
          // Add verification conditions for single ticket
          if (ticket.tp_status !== 'ToBeVerified') {
            showError('Only tickets in "To Be Verified" status can be verified.');
            return;
          }
          if (
            !ticket.tp_section ||
            !ticket.tp_row ||
            ticket.tp_seat_no === null ||
            ticket.tp_seat_no === undefined ||
            String(ticket.tp_seat_no).trim() === '' ||
            ticket.tp_list_price === null ||
            ticket.tp_list_price === undefined
          ) {
            showError('Ticket is missing required fields (section, row, seat, or price).');
            return;
          }
          const verifyResult = await validateUpdate({
            variables: { ticketPlacementId: [ticket.tp_id], isValid: true, isUndoRequest: false }
          });
          const verifyMessage = (verifyResult?.data?.updateTicketStatus?.message || '').toLowerCase();
          const isVerifySuccess =
            verifyMessage.includes('success') &&
            !verifyMessage.includes('0 ticket updated successfully') &&
            !verifyMessage.includes('0 tickets updated successfully') &&
            !verifyMessage.includes('not found') &&
            !verifyMessage.includes('failed') &&
            !verifyMessage.includes('error');

          if (isVerifySuccess) {
            updateLocalTicketStatus([ticket.tp_id], 'Verified');
          }
          break;
        }
        case 'Invalidate':
          await validateUpdate({
            variables: { ticketPlacementId: [ticket.tp_id], isValid: false, isUndoRequest: false }
          });
          break;
        case 'Delist':
          setOpenActionMenu(null);
          if (normalizeStatusValue(ticket.tp_status || '') === 'List') {
            await runListedTicketDelist([ticket.tp_id]);
            return;
          }
          openDelistEmailModal([ticket.tp_id]);
          return;
        case 'Publish':
          if (!(Number(ticket.tp_list_price) > 0)) {
            showError('Please provide a valid price greater than 0 before listing.');
            return;
          }
          await callPublishTicketsRest([ticket.tp_id], Number(ticket.tp_list_price));
          updateLocalTicketStatus([ticket.tp_id], 'List', Number(ticket.tp_list_price));
          break;
        case 'Return': {
          const { message: returnMessage, type: returnType } = await executeReturnTicketsRest([ticket.tp_id]);
          updateLocalTicketStatus([ticket.tp_id], 'Return');
          if (returnType === 'success') success(returnMessage || 'Return request successful');
          else info(returnMessage || 'Return request processed');
          if (activeTab !== 'Delisted') {
            setTimeout(() => switchTabUnlessEventSearch('Delisted'), 300);
          }
          break;
        }
        case 'Undo Delist':
          await executeUndoDelistWithFallback(ticket.tp_id, ticket.tp_status);
          updateLocalTicketStatus([ticket.tp_id], 'List');
          if (activeTab !== 'Listed') {
            switchTabUnlessEventSearch('Listed');
          }
          break;
        case 'Change Price':
          setSelectedTicketForPrice(ticket);
          setSelectedTpIds([ticket.tp_id]);
          setNewPrice(ticket.tp_list_price?.toString() || '0');
          setSelectedMarketplaceId('');
          setShowPriceModal(true);
          break;
        case 'Edit Details':
          setSelectedTicketForEdit(ticket);
          setEditFormData({
            section: ticket.tp_section || '',
            row: ticket.tp_row || '',
            seatNumbers: ticket.tp_seat_no?.toString() || ''
          });
          setShowEditModal(true);
          break;
        case 'Change Status': {
          const presMenu = getManageTicketsStatusPresentation(ticket);
          const pk = (presMenu.iconStatus || '').toLowerCase();
          if (pk === 'settled' || pk === 'settlementinprogress') {
            info(
              pk === 'settled'
                ? 'This ticket is settled. Its status cannot be changed here.'
                : soldPaymentStartedMessage,
            );
            break;
          }
          setSelectedTicketForStatus(ticket);
          setNewStatus(normalizeStatusValue(ticket.tp_status || 'ToBeVerified') || 'ToBeVerified');
          setShowStatusModal(true);
          break;
        }
        case 'View Details':
          setSelectedTicketForView(ticket);
          setShowViewModal(true);
          break;
        case 'View Client Profile':
          navigate(`/user/${ticket.u_id}?tab=tickets`);
          break;
        case 'View Invoice':
          navigate(`/invoice/${ticket.tp_id}`);
          break;
        default:
          break;
      }

    } catch (error: any) {
      showError(getErrorMessage(error, `Failed to ${action}`));
    } finally {
      if (statusActionsNeedingBusy.has(action)) {
        await syncTicketsAfterStatusChange();
        endStatusTableBusy();
      }
    }

    setOpenActionMenu(null);
  };

  const handleEditDetailsConfirm = async () => {
    if (!selectedTicketForEdit) return;

    if (!editFormData.section.trim() && !editFormData.row.trim() && !editFormData.seatNumbers.trim()) {
      info('Please fill at least one field to update');
      return;
    }

    try {
      const parsePrimarySeatNumber = (value: string | number | null | undefined): number | null => {
        if (value === null || value === undefined) return null;
        const raw = String(value).trim();
        if (!raw) return null;
        if (raw.includes('-')) {
          const first = parseInt(raw.split('-')[0], 10);
          return isNaN(first) ? null : first;
        }
        if (raw.includes(',')) {
          const first = parseInt(raw.split(',')[0].trim(), 10);
          return isNaN(first) ? null : first;
        }
        const single = parseInt(raw, 10);
        return isNaN(single) ? null : single;
      };

      let seatNumberInt: number | null = null;
      const seatNumStr = editFormData.seatNumbers.trim();

      if (seatNumStr) {
        seatNumberInt = parsePrimarySeatNumber(seatNumStr);
        if (seatNumberInt === null) {
          info('Please enter valid seat numbers');
          return;
        }
      }

      const fallbackSeat = parsePrimarySeatNumber(selectedTicketForEdit.tp_seat_no);
      const effectiveSeat = seatNumberInt ?? fallbackSeat;
      if (effectiveSeat === null) {
        info('Seat number is required');
        return;
      }

      const preparedSection = editFormData.section.trim() || selectedTicketForEdit.tp_section || '';
      const preparedRow = editFormData.row.trim() || selectedTicketForEdit.tp_row || '';

      const currentSection = (selectedTicketForEdit.tp_section || '').trim().toLowerCase();
      const currentRow = (selectedTicketForEdit.tp_row || '').trim().toLowerCase();
      const currentSeat = parsePrimarySeatNumber(selectedTicketForEdit.tp_seat_no);
      const isNoActualChange =
        preparedSection.trim().toLowerCase() === currentSection &&
        preparedRow.trim().toLowerCase() === currentRow &&
        effectiveSeat === currentSeat;

      if (isNoActualChange) {
        info('No changes detected. Please modify section, row, or seat number.');
        return;
      }

      const normalizedSection = preparedSection.toLowerCase();
      const normalizedRow = preparedRow.toLowerCase();
      const inactiveStatuses = new Set(['Delist', 'Return', 'Unsold']);
      const conflictingTicket = filteredTickets.find(ticket =>
        ticket.tp_id !== selectedTicketForEdit.tp_id &&
        ticket.e_id === selectedTicketForEdit.e_id &&
        (ticket.tp_section || '').trim().toLowerCase() === normalizedSection &&
        (ticket.tp_row || '').trim().toLowerCase() === normalizedRow &&
        Number(ticket.tp_seat_no) === effectiveSeat &&
        !inactiveStatuses.has(ticket.tp_status || '')
      );

      if (conflictingTicket) {
        showError('Another ticket already exists with the same section, row, and seat for this event.');
        return;
      }

      const editTicketsInput = {
        e_id: selectedTicketForEdit.e_id,
        tp_id: selectedTicketForEdit.tp_id,
        tp_section: preparedSection,
        tp_row: preparedRow,
        tp_seat_no: effectiveSeat
      };

      const editResult = await executeEditDetailsWithFallback(editTicketsInput);

      let finalResult = editResult;
      if (editResult?.requiresConfirmation) {
        const shouldProceed = window.confirm(
          editResult?.message || 'Matching ticket details exist in delisted/returned tickets. Do you want to proceed?'
        );
        if (!shouldProceed) {
          return;
        }

        finalResult = await executeEditDetailsWithFallback(
          {
            ...editTicketsInput,
            forceUpdate: true
          }
        );
      }

      const finalMessage = (finalResult?.message || '').toLowerCase();
      const hasErrorMessage = finalMessage.includes('fail') || finalMessage.includes('error');
      if (finalResult?.successFlag !== true || hasErrorMessage) {
        showError(finalResult?.message || 'Failed to update ticket details');
        return;
      }

      updateLocalTicketDetails([selectedTicketForEdit.tp_id], {
        tp_section: preparedSection,
        tp_row: preparedRow,
        tp_seat_no: effectiveSeat,
      });

      closeEditModal();
      success('Ticket details updated successfully!');

    } catch (error: any) {
      console.error('Edit details error:', error);
      showError(getErrorMessage(error, 'Failed to update ticket details'));
    }
  };

  const handlePriceChangeConfirm = async () => {
    if (priceLoading) return;
    if (!selectedTpIds.length || !newPrice) return;

    const ticketPrice = parseFloat(newPrice);
    if (isNaN(ticketPrice) || ticketPrice < 0) {
      info('Please enter a valid price');
      return;
    }

    // If a marketplace is selected, apply its fee and save the settled (user receives) amount
    const selectedMp = marketplacesList.find(m => m.id === selectedMarketplaceId);
    const finalPrice = selectedMp
      ? Math.max(0, ticketPrice - (ticketPrice * selectedMp.vl_fee_percentage) / 100)
      : ticketPrice;

    pendingFinalPriceRef.current = finalPrice;

    try {
      await editTicketPrice({
        variables: {
          tp_ids: selectedTpIds,
          tp_list_price: finalPrice
        }
      });

      updateLocalTicketPrice(selectedTpIds, finalPrice);

    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update price'));
      handlePriceUpdateSuccess();
    }
  };
const handleStatusChangeConfirm = async (ticket: Ticket, status: string) => {
  if (!ticket || !status) return;
  if (statusChangeLoading || statusTableBusyCount > 0) return;
  
  const sanitizedStatus = normalizeStatusValue(status);
  if (!sanitizedStatus) {
    showError('That status could not be applied. Reload the page and try again, or contact support if it keeps happening.');
    return;
  }

  // Handle Deleted status - redirect to delete confirmation
  if (sanitizedStatus === 'Deleted') {
    setShowStatusModal(false);
    setSelectedTicketForDelete(ticket);
    setShowDeleteConfirmModal(true);
    return;
  }

  // Check if this is a Verified -> Listed transition
  const currentStatus = normalizeStatusValue(ticket.tp_status || '');

  if (
    currentStatus === 'Sold' &&
    (sanitizedStatus === 'Sold' || sanitizedStatus === 'Settled' || sanitizedStatus === 'SettlementInProgress')
  ) {
    if (!canOverrideSettlementStatus) {
      info(soldPaymentStartedMessage);
      setShowStatusModal(false);
      setOpenStatusDropdown(null);
      return;
    }
    const paymentStatus = getPaymentStatusForSettlementOverride(sanitizedStatus);
    if (paymentStatus === undefined) {
      showError('That settlement status could not be applied.');
      return;
    }
    beginStatusTableBusy('Updating settlement status…');
    setStatusChangeLoading(true);
    try {
      const result = await callUpdateSettlementPaymentStatusRest([ticket.tp_id], paymentStatus);
      const updatedIds: string[] = Array.isArray(result?.updatedTpIds) ? result.updatedTpIds : [ticket.tp_id];
      if (!updatedIds.length) {
        showError('No sold ticket was updated.');
        return;
      }
      updateLocalTicketPaymentStatus(updatedIds, paymentStatus);
      success(result?.message || 'Settlement status updated successfully.');
      setShowStatusModal(false);
      setOpenStatusDropdown(null);
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update settlement status'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Verification tab: Hasura allowlists include the custom action `updateTicketStatus` (VALIDATE_UPDATE),
  // not the direct `update_ticket_placement` mutation used by UPDATE_TICKET_STATUS.
  if (currentStatus === 'ToBeVerified') {
    if (sanitizedStatus === 'Verified') {
      if (
        !ticket.tp_section ||
        !ticket.tp_row ||
        ticket.tp_seat_no === null ||
        ticket.tp_seat_no === undefined ||
        String(ticket.tp_seat_no).trim() === '' ||
        ticket.tp_list_price === null ||
        ticket.tp_list_price === undefined
      ) {
        showError('Ticket is missing required fields (section, row, seat, or price).');
        return;
      }
      patchFailTransferModalRow(ticket.tp_id, {
        tp_status: 'Verified',
        tp_updated_at: new Date().toISOString(),
      });
      beginStatusTableBusy('Verifying ticket…');
      setStatusChangeLoading(true);
      try {
        const restData = await callUpdateTicketStatusRest([ticket.tp_id], true, false);
        const rawMsg = String(restData?.message || '').trim();
        const verifyMessage = rawMsg.toLowerCase();
        const isVerifySuccess =
          verifyMessage.includes('success') &&
          !verifyMessage.includes('0 ticket updated successfully') &&
          !verifyMessage.includes('0 tickets updated successfully') &&
          !verifyMessage.includes('not found') &&
          !verifyMessage.includes('failed') &&
          !verifyMessage.includes('error');

        if (isVerifySuccess) {
          updateLocalTicketStatus([ticket.tp_id], 'Verified');
          success(rawMsg || 'Status updated successfully');
          removeTicketFromFailTransferModal(ticket.tp_id);
          setShowStatusModal(false);
          if (activeTab !== 'Verification') {
            setTimeout(() => switchTabUnlessEventSearch('Verification'), 300);
          }
          await syncTicketsAfterStatusChange();
          syncDashboardNotifications();
        } else {
          const msgType = resolveActionMessageType(rawMsg);
          if (msgType === 'error') {
            showError(rawMsg || 'Ticket verification failed');
            patchFailTransferModalRow(ticket.tp_id, { tp_status: 'ToBeVerified' });
          } else if (msgType === 'info') {
            info(rawMsg || 'Ticket verification processed with partial success');
          } else {
            success(rawMsg || 'Status updated successfully');
          }
          setShowStatusModal(false);
          await refetch();
          await fetchTrackingCounts();
        }
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to update ticket status'));
        patchFailTransferModalRow(ticket.tp_id, { tp_status: 'ToBeVerified' });
        setShowStatusModal(false);
      } finally {
        setStatusChangeLoading(false);
        endStatusTableBusy();
      }
      return;
    }

    if (sanitizedStatus === 'Invalid' || sanitizedStatus === 'ListingRequestCancelled') {
      patchFailTransferModalRow(ticket.tp_id, {
        tp_status: 'ListingRequestCancelled',
        tp_updated_at: new Date().toISOString(),
      });
      beginStatusTableBusy('Cancelling listing request…');
      setStatusChangeLoading(true);
      try {
        await callCancelListingRequestByAdminRest(ticket.tp_id);
        success('Ticket marked as Listing Request Cancelled');
        updateLocalTicketStatus([ticket.tp_id], 'ListingRequestCancelled');
        removeTicketFromFailTransferModal(ticket.tp_id);
        setShowStatusModal(false);
        setFilterStatus('All Listing Request Cancelled');
        if (activeTab !== 'Listing Request Cancelled') {
          switchTabUnlessEventSearch('Listing Request Cancelled');
        }
        await refetch();
        await fetchTrackingCounts();
      } catch (error: any) {
        showError(getErrorMessage(error, 'Failed to cancel listing request'));
        patchFailTransferModalRow(ticket.tp_id, { tp_status: 'ToBeVerified' });
        setShowStatusModal(false);
      } finally {
        setStatusChangeLoading(false);
        endStatusTableBusy();
      }
      return;
    }
  }
  
  if (currentStatus === 'Verified' && sanitizedStatus === 'List') {
    // Close status modal first
    setShowStatusModal(false);
    // Show Listed modal with price input
    setListedModalTicket(ticket);
    setListedPrice(ticket.tp_list_price?.toString() || '');
    setShowListedModal(true);
    return;
  }
  
  if (currentStatus === 'List' && sanitizedStatus === 'Sold') {
    setShowStatusModal(false);
    await openSoldModalForTicket(ticket);
    return;
  }

  // List -> Unsold should directly set Unsold status (not go through boolean REST flow).
  if (currentStatus === 'List' && sanitizedStatus === 'Unsold') {
    beginStatusTableBusy('Marking ticket as Unsold…');
    setStatusChangeLoading(true);
    try {
      const response = await callMarkTicketsUnsoldRest([ticket.tp_id]);
      success(response?.message || 'Ticket marked as Unsold successfully');
      updateLocalTicketStatus([ticket.tp_id], 'Unsold');
      setShowStatusModal(false);
      if (activeTab !== 'Unsold') switchTabUnlessEventSearch('Unsold');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to update ticket status'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Listed -> Delist: remove from Logitix/Victory Live via delistTicketByAdmin (admin + super user).
  if (currentStatus === 'List' && sanitizedStatus === 'Delist') {
    beginStatusTableBusy('Delisting ticket…');
    setStatusChangeLoading(true);
    try {
      const { successIds, message } = await executeAdminOrSuperUserDelistRest([ticket.tp_id]);
      if (!successIds.includes(ticket.tp_id)) {
        throw new Error('Failed to delist ticket');
      }
      success(message || 'Ticket delisted successfully');
      updateLocalTicketStatus([ticket.tp_id], 'Delist');
      setShowStatusModal(false);
      if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to delist ticket'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // ListingRequestCancelled -> ToBeVerified: restore pre-transfer listing request
  if (currentStatus === 'ListingRequestCancelled' && sanitizedStatus === 'ToBeVerified') {
    setStatusChangeLoading(true);
    try {
      const result = await restoreCancelledListingToVerification({
        variables: { tp_id: ticket.tp_id }
      });
      const updated = result?.data?.update_ticket_placement?.returning?.length || 0;
      if (updated <= 0) {
        throw new Error('Unable to restore ticket to To Be Verified');
      }
      success('Ticket restored to To Be Verified');
      updateLocalTicketStatus([ticket.tp_id], 'ToBeVerified');
      setShowStatusModal(false);
      if (activeTab !== 'Verification') {
        switchTabUnlessEventSearch('Verification');
      }
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to restore ticket to To Be Verified'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // DelistInProgress -> Delist: Confirm delist request (finalize delisting)
  if (currentStatus === 'DelistInProgress' && sanitizedStatus === 'Delist') {
    beginStatusTableBusy('Confirming delist…');
    setStatusChangeLoading(true);
    try {
      const { successIds, message } = await executeAdminOrSuperUserDelistRest([ticket.tp_id]);
      if (!successIds.includes(ticket.tp_id)) {
        throw new Error('Failed to delist ticket');
      }
      success(message || 'Ticket delisted successfully');
      updateLocalTicketStatus([ticket.tp_id], 'Delist');
      setShowStatusModal(false);
      if (activeTab !== 'Delisted') switchTabUnlessEventSearch('Delisted');
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to delist ticket'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }


  // Delist -> List: Move delisted ticket back to listed
if (currentStatus === 'Delist' && sanitizedStatus === 'List') {
  beginStatusTableBusy('Moving ticket to Listed…');
  setStatusChangeLoading(true);
  try {
    await executeUndoDelistWithFallback(ticket.tp_id, ticket.tp_status);
    updateLocalTicketStatus([ticket.tp_id], 'List');
    setShowStatusModal(false);
    if (activeTab !== 'Listed') {
      switchTabUnlessEventSearch('Listed');
    }
    success('Ticket moved to Listed successfully');
    await syncTicketsAfterStatusChange();
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to move ticket to Listed'));
    setShowStatusModal(false);
    await syncTicketsAfterStatusChange();
  } finally {
    setStatusChangeLoading(false);
    endStatusTableBusy();
  }
  return;
}

  // Delist -> DelistInProgress: Move delisted ticket back to delist requested
  if (currentStatus === 'Delist' && sanitizedStatus === 'DelistInProgress') {
    beginStatusTableBusy('Updating ticket status…');
    setStatusChangeLoading(true);
    try {
      await updateTicketStatusSilent({
        variables: {
          tp_id: ticket.tp_id,
          status: 'DelistInProgress'
        }
      });
      success('Ticket moved to Delist Requested');
      updateLocalTicketStatus([ticket.tp_id], 'DelistInProgress');
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
      syncDashboardNotifications();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to move ticket to Delist Requested'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Undo delist must use dedicated backend mutation to move DelistInProgress -> List.
  if (
    (currentStatus === 'DelistInProgress' || currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'List'
  ) {
    beginStatusTableBusy('Moving ticket to Listed…');
    setStatusChangeLoading(true);
    try {
      await executeUndoDelistWithFallback(ticket.tp_id, ticket.tp_status);
      updateLocalTicketStatus([ticket.tp_id], 'List');
      setShowStatusModal(false);
      if (activeTab !== 'Listed') {
        switchTabUnlessEventSearch('Listed');
      }
      success('Ticket moved to Listed successfully');
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to undo delist request'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  if (
    (currentStatus === 'Delist' ||
      currentStatus === 'DelistInProgress' ||
      currentStatus === 'DelistRequested') &&
    sanitizedStatus === 'Return'
  ) {
    beginStatusTableBusy('Returning ticket…');
    setStatusChangeLoading(true);
    try {
      setShowStatusModal(false);
      const { message: returnMessage, type: returnType } = await executeReturnTicketsRest([ticket.tp_id]);
      updateLocalTicketStatus([ticket.tp_id], 'Return');
      if (returnType === 'success') success(returnMessage || 'Return request successful');
      else if (returnType === 'info') info(returnMessage || 'Return request processed');
      await syncTicketsAfterStatusChange();
    } catch (error: any) {
      showError(getErrorMessage(error, 'Failed to return ticket'));
      setShowStatusModal(false);
      await syncTicketsAfterStatusChange();
    } finally {
      setStatusChangeLoading(false);
      endStatusTableBusy();
    }
    return;
  }

  // Ensure "Delist Requested" uses enum-supported status and bypasses boolean REST status flow.
  if (sanitizedStatus === 'DelistRequested' || sanitizedStatus === 'DelistInProgress') {
    // Route through delist API flow to create feed notifications.
    setShowStatusModal(false);
    openDelistEmailModal([ticket.tp_id]);
    return;
  }
  
  // For all other status transitions, use backend status API (avoid direct Hasura mutation).
  beginStatusTableBusy('Saving status change…');
  setStatusChangeLoading(true);
  
  try {
    const isValidForBackend = sanitizedStatus === 'Verified';
    const restData = await callUpdateTicketStatusRest([ticket.tp_id], isValidForBackend, false);
    const rawMsg = String(restData?.message || '').trim();
    const msgType = resolveActionMessageType(rawMsg);
    if (msgType === 'error') {
      showError(rawMsg || 'Failed to update ticket status');
      await syncTicketsAfterStatusChange();
    } else {
      updateLocalTicketStatus([ticket.tp_id], sanitizedStatus);
      if (msgType === 'info') info(rawMsg || 'Status update processed');
      else success(rawMsg || `Ticket status updated to ${getStatusLabel(sanitizedStatus)} successfully!`);
      await syncTicketsAfterStatusChange();
    }
    setShowStatusModal(false);
    
  } catch (error: any) {
    showError(getErrorMessage(error, 'Failed to update ticket status'));
    setShowStatusModal(false);
    await syncTicketsAfterStatusChange();
  } finally {
    setStatusChangeLoading(false);
    endStatusTableBusy();
  }
};

const handleDeleteConfirm = async () => {
  if (!selectedTicketForDelete) return;
  if (deleteLoading || statusTableBusyCount > 0) return;

  beginStatusTableBusy('Deleting ticket…');
  setDeleteLoading(true);

  try {
    // Use GraphQL mutation directly instead of REST API
    const result = await updateTicketStatus({
      variables: {
        tp_id: selectedTicketForDelete.tp_id,
        status: 'Deleted'  // This should work if enum exists
      }
    });
    
    const affected = result?.data?.update_ticket_placement?.returning?.length || 0;
    
    if (affected > 0) {
      // Update local state
      setFilteredTickets((prevTickets) =>
        prevTickets.filter((t) => t.tp_id !== selectedTicketForDelete.tp_id)
      );
      
      setStatusFilterSourceTickets((prevTickets) =>
        prevTickets.filter((t) => t.tp_id !== selectedTicketForDelete.tp_id)
      );
      
      // Update counts
      setTotalCount((prev) => Math.max(0, prev - 1));
      setTabCounts((prev) => ({
        ...prev,
        Deleted: (prev.Deleted || 0) + 1,
        [activeTab]: Math.max(0, (prev[activeTab] || 0) - 1)
      }));

      success('Ticket deleted successfully');
      setShowDeleteConfirmModal(false);
      setSelectedTicketForDelete(null);
      await syncTicketsAfterStatusChange();
    } else {
      showError('Failed to delete ticket. Please try again.');
    }
  } catch (error: any) {
    // If GraphQL mutation fails, try REST API as fallback
    try {
      await callDeleteTicketRest(selectedTicketForDelete.tp_id);
      
      setFilteredTickets((prevTickets) =>
        prevTickets.filter((t) => t.tp_id !== selectedTicketForDelete.tp_id)
      );
      // ... rest of the update logic
      success('Ticket deleted successfully');
    } catch (fallbackError: any) {
      showError(getErrorMessage(fallbackError, 'Failed to delete ticket'));
    }
    setShowDeleteConfirmModal(false);
  } finally {
    setDeleteLoading(false);
    endStatusTableBusy();
  }
};

const handleListedModalConfirm = async () => {
  if (listedModalLoading) return;
  if (!listedModalTicket) {
    showError('No ticket selected');
    return;
  }
  
  if (!listedPrice) {
    showError('Please enter a valid listing price.');
    return;
  }
  
  const priceNum = parseFloat(listedPrice);
  if (isNaN(priceNum) || priceNum <= 0) {
    showError('Please enter a valid price greater than 0.');
    return;
  }
  
  beginStatusTableBusy('Publishing to Listed…');
  setListedModalLoading(true);
  try {
    // Use allowlisted custom action for list transition.
    const restData = await callPublishTicketsRest([listedModalTicket.tp_id], priceNum);
    const message = String(restData?.message || '');
    const normalized = message.toLowerCase();
    const isSuccess = normalized.includes('success') || normalized.includes('listed');

    if (!isSuccess) {
      showError(message || 'Failed to list ticket');
      setShowListedModal(false);
      setListedModalTicket(null);
      setListedPrice('');
      return;
    }

    updateLocalTicketStatus([listedModalTicket.tp_id], 'List', priceNum);
    success(message || `Ticket listed successfully at $${priceNum}`);
    setShowListedModal(false);
    setListedModalTicket(null);
    setListedPrice('');
    if (activeTab !== 'Listed') {
      switchTabUnlessEventSearch('Listed');
    }
    await syncTicketsAfterStatusChange();
    syncDashboardNotifications();
    
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to list ticket'));
    setShowListedModal(false);
    setListedModalTicket(null);
    setListedPrice('');
    await syncTicketsAfterStatusChange();
  } finally {
    setListedModalLoading(false);
    endStatusTableBusy();
  }

};

const handleBulkListConfirm = async () => {
  if (bulkListLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const value = String(bulkListedPrices[ticket.tp_id] || '').trim();
    const num = parseFloat(value);
    return !value || isNaN(num) || num <= 0;
  });

  if (invalidTicket) {
    showError('All selected tickets must have a valid listing price greater than 0.');
    return;
  }

  beginStatusTableBusy('Publishing to Listed…');
  setBulkListLoading(true);

  try {
    await withSuppressedBulkMutationToasts(async () => {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket => {
          const priceNum = Number(parseFloat(String(bulkListedPrices[ticket.tp_id] || '').trim()));
          return callPublishTicketsRest([ticket.tp_id], priceNum).then(() => ticket.tp_id);
        })
      );

      const successIds = results
        .filter(result => result.status === 'fulfilled')
        .map((result: any) => result.value);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        const priceMap: Record<string, number> = {};
        successIds.forEach(id => {
          priceMap[id] = Number(parseFloat(String(bulkListedPrices[id] || '').trim()));
        });
        updateLocalTicketStatusWithPriceMap(successIds, 'List', priceMap);
        success(`Published ${successIds.length} ticket(s) to Listed${failedCount ? `, ${failedCount} failed` : ''}`);
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to publish selected tickets');
      }
    });
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to publish selected tickets'));
  } finally {
    closeBulkListModal();
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setBulkNewStatus('');
    setBulkListLoading(false);
    endStatusTableBusy();
    await syncTicketsAfterStatusChange();
  }
};

const handleApplyBulkListPrice = () => {
  const value = String(bulkListCommonPrice).trim();
  const priceNum = parseFloat(value);
  if (!value || isNaN(priceNum) || priceNum <= 0) {
    showError('Enter a valid price to apply to all tickets');
    return;
  }
  setBulkListedPrices(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(priceNum)]))
  );
};

async function handleBulkSoldConfirm() {
  if (bulkSoldLoading) return;
  if (!bulkSelectedTickets.length) {
    showError('No tickets selected');
    return;
  }
  if (!bulkSoldMarketplaceId) {
    showError('Please select a marketplace.');
    return;
  }

  const mp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
  if (!mp) {
    showError('Selected marketplace not found.');
    return;
  }

  const invalidTicket = bulkSelectedTickets.find(ticket => {
    const rawValue = bulkSoldLogitixAmounts[ticket.tp_id];
    const value = String(rawValue ?? '').trim();
    if (!value) {
      const selectedMp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
      const listPrice = Number(ticket.tp_list_price) || 0;
      const expectedLogitix = selectedMp ? Math.round(listPrice * (100 - selectedMp.vl_fee_percentage)) / 100 : 0;
      return expectedLogitix <= 0;
    }
    const num = parseFloat(value);
    return isNaN(num) || num <= 0;
  });

  if (invalidTicket) {
    showError('Enter a valid amount received from Logitix for each ticket.');
    return;
  }

  beginStatusTableBusy('Marking tickets as sold…');
  setBulkSoldLoading(true);

  try {
    await withSuppressedBulkMutationToasts(async () => {
      const results = await Promise.allSettled(
        bulkSelectedTickets.map(ticket => {
          const listPrice = Number(ticket.tp_list_price) || 0;
          const logitixValue = Number(parseFloat(String(bulkSoldLogitixAmounts[ticket.tp_id] || '').trim()));
          const feeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, bulkSoldFeeConfigByTicketId);
          const quickPct = effectiveSoldQuickCutPercent(
            mp.vl_fee_percentage,
            feeConfig.feeMode,
            feeConfig.superClientPercent,
          );
          const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
          const settlement = Math.round((logitixValue - quickCutDollars) * 100) / 100;
         return callMarkAsSoldRest(ticket.tp_id, settlement, logitixValue, mp.name)
  .then(() => {
    saveMarketplaceCache(ticket.u_id, ticket.tp_id, mp.name);
    return ticket.tp_id;
  });
        })
      );

      const successIds = results
        .filter(result => result.status === 'fulfilled')
        .map((result: any) => result.value);
      const failedCount = results.filter(result => result.status === 'rejected').length;

      if (successIds.length > 0) {
        updateLocalTicketStatus(successIds, 'Sold');
        success(`${successIds.length} ticket(s) marked as sold${failedCount ? `, ${failedCount} failed` : ''}`);
        if (activeTab !== 'Sold') {
          handleTabChange('Sold');
        }
      }

      if (failedCount > 0 && successIds.length === 0) {
        showError('Failed to mark selected tickets as sold');
      }
    });
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to mark selected tickets as sold'));
  } finally {
    closeBulkSoldModal();
    setBulkSelectedTicketIds(new Set());
    setBulkSelectedTickets([]);
    setBulkSoldLoading(false);
    endStatusTableBusy();
    await syncTicketsAfterStatusChange();
  }
};

const handleApplyBulkSoldAmount = () => {
  const value = String(bulkSoldCommonLogitixAmount).trim();
  const num = parseFloat(value);
  if (!value || isNaN(num) || num <= 0) {
    showError('Enter a valid logitix amount to apply to all tickets');
    return;
  }
  setBulkSoldLogitixAmounts(
    Object.fromEntries(bulkSelectedTickets.map(ticket => [ticket.tp_id, String(num)]))
  );
};

const closeSoldModal = () => {
    setShowSoldModal(false);
    setSoldModalTicket(null);
    setSoldMarketplaceId('');
    setSoldLogitixAmount('');
    setSoldFeeConfigReady(false);
    setSoldFeeMode('default');
    setSoldSuperClientPercent(null);
    setStatusChangeLoading(false);
  };

  // Confirm handler for Sold modal (same backend path as List Tickets → Mark as Sold)
const handleSoldModalConfirm = async () => {
  if (!soldModalTicket) {
    showError('No ticket selected');
    return;
  }
  
  if (!soldMarketplaceId) {
    showError('Please select a marketplace.');
    return;
  }
  
  const mp = marketplacesList.find(m => m.id === soldMarketplaceId);
  if (!mp) {
    showError('Selected marketplace not found.');
    return;
  }
  
  const listPrice = Number(soldModalTicket.tp_list_price) || 0;
  const logitixNum = parseFloat(String(soldLogitixAmount || '').trim());
  
  if (isNaN(logitixNum) || logitixNum <= 0) {
    showError('Please enter the amount received from Logitix.');
    return;
  }
  
  const quickPct = effectiveSoldQuickCutPercent(
    mp.vl_fee_percentage,
    soldFeeMode,
    soldSuperClientPercent,
  );
  const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
  const settlement = Math.round((logitixNum - quickCutDollars) * 100) / 100;
  
  if (settlement <= 0) {
    showError('Amount settled to user must be greater than zero.');
    return;
  }
  
  beginStatusTableBusy('Marking ticket as sold…');
  setStatusChangeLoading(true);
  
  try {
    const restData = await callMarkAsSoldRest(
      soldModalTicket.tp_id,
      settlement,
      logitixNum,
      mp.name
    );
    const rawMsg = String(restData?.message || '');
    const normalized = rawMsg.toLowerCase();
    if (!normalized.includes('success')) {
      showError(rawMsg || 'Failed to mark ticket as sold');
      return;
    }

    saveMarketplaceCache(soldModalTicket.u_id, soldModalTicket.tp_id, mp.name);
    updateLocalTicketStatus([soldModalTicket.tp_id], 'Sold');
    success(rawMsg || 'Ticket marked as sold successfully');
    if (activeTab !== 'Sold') {
      switchTabUnlessEventSearch('Sold');
    }
    closeSoldModal();
    await syncTicketsAfterStatusChange();
    await refetchExpired();
    await refetchFailed();
  } catch (err: any) {
    showError(getErrorMessage(err, 'Failed to mark ticket as sold'));
    await syncTicketsAfterStatusChange();
  } finally {
    setStatusChangeLoading(false);
    endStatusTableBusy();
  }
};

  // ==================== UTILITY FUNCTIONS ====================

  const toggleRow = (ticketId: string) => {
    setExpandedRows(prev =>
      prev.includes(ticketId) ? prev.filter(id => id !== ticketId) : [...prev, ticketId]
    );
    setSelectedSeats({});
    setSelectAllSeats(false);
    setShowBulkActions(false);
    setSelectedTpIds([]);
  };

  const toggleActionMenu = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenActionMenu(openActionMenu === ticketId ? null : ticketId);
  };

  const getStatusColor = (status: string): string => {
    if (!status) return styles.statusDefault;
    const s = status.toLowerCase();
    if (s === "verified" || s === "list") return styles.statusSuccess;
    if (s === "tobeverified") return styles.statusWarning;
    if (s === "invalid") return styles.statusError;
    if (s === "settled") return styles.statusVerified;
    if (s === "settlementinprogress") return styles.statusSuccess;
    if (s === "failed") return styles.statusError;
    if (s === "sold") return styles.statusSold;
    if (s === "delist" || s === "delistinprogress" || s === "delistrequested" || s === "listingrequestcancelled" || s === "return") return styles.statusDelist;
    if (s === "unsold") return styles.statusUnsold;
    if (s === "closed" || s === "listing expired" || s === "listing request expired") return styles.statusExpired;
    if (s === "deleted") return styles.statusDeleted;
    return styles.statusDefault;
  };

  function getStatusLabel(status: string): string {
  if (!status) return 'Unknown';
  const normalizedStatus = status.toLowerCase();

  if (normalizedStatus === 'list') return 'Listed';
  if (normalizedStatus === 'delistinprogress' || normalizedStatus === 'delistrequested') return 'Delist Requested';
  if (normalizedStatus === 'listingrequestcancelled') return 'Listing Request Cancelled';
  if (normalizedStatus === 'closed') return 'Listing Request Expired';
  if (normalizedStatus === 'settled') return 'Settled';
  if (normalizedStatus === 'settlementinprogress') return 'Settlement In Progress';
  if (normalizedStatus === 'return') return 'Returned';
  if (normalizedStatus === 'tobeverified') return 'To Be Verified';
  if (normalizedStatus === 'verified') return 'Verified';
  if (normalizedStatus === 'invalid') return 'Invalid';
  if (normalizedStatus === 'sold') return 'Sold';
  if (normalizedStatus === 'unsold') return 'Unsold';
  if (normalizedStatus === 'delist') return 'Delisted';
  if (normalizedStatus === 'deleted') return 'Deleted';
  return status;
};

  /** Sold tab: surface payout state (matches list fail-to-transfer / DB view intent). */
  const getManageTicketsStatusPresentation = (ticket: Ticket): { label: string; iconStatus: string } => {
    const placementStatus = (ticket.tp_status || '').trim();
    if ((placementStatus || '').toLowerCase() !== 'sold') {
      return { label: getStatusLabel(placementStatus), iconStatus: placementStatus };
    }
    const pay = ticket.tp_payment_status;
    if (pay === 'Success') {
      return { label: 'Settled', iconStatus: 'settled' };
    }
    if (pay === 'Inprogress') {
      return { label: 'Settlement In Progress', iconStatus: 'settlementinprogress' };
    }
    if (pay === 'Failed') {
      return { label: 'Failed', iconStatus: 'failed' };
    }
    if (pay == null || pay === '') {
      const payout = ticket.tp_payout_status;
      if (payout && payout !== 'Unvoided_Payout') {
        return { label: String(payout).replace(/_/g, ' '), iconStatus: 'sold' };
      }
      return { label: 'Sold', iconStatus: 'sold' };
    }
    return { label: pay, iconStatus: 'sold' };
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: '2-digit', day: '2-digit', year: 'numeric'
    });
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit'
    });
  };

  const hasActiveFilters = (): boolean => {
    return filterStatus !== getDefaultStatus() ||
      clientType !== 'All' ||
      selectedLeague !== '' ||
      eventVenueSearch !== '' ||
      clientSearch !== '' ||
      startDate !== '' ||
      endDate !== '' ||
      cardFilter !== null;
  };

  const currentPageTickets = useMemo(() => {
    if (isEventGroupedView) return filteredTickets;
    if (usesClientSidePagination) {
      return filteredTickets.slice(pageOffset, pageOffset + pageSize);
    }
    return filteredTickets;
  }, [isEventGroupedView, usesClientSidePagination, filteredTickets, pageOffset, pageSize]);

  const displayTotalCount = Math.max(filteredTickets.length, totalCount);
  const effectiveTotalCount = usesClientSidePagination
    ? filteredTickets.length
    : displayTotalCount;
  const totalPages = Math.ceil(effectiveTotalCount / pageSize);
  const currentPage = effectiveTotalCount === 0 ? 0 : Math.floor(pageOffset / pageSize) + 1;
  const startIndex =
    effectiveTotalCount === 0 || currentPageTickets.length === 0 ? 0 : pageOffset + 1;
  const endIndex =
    effectiveTotalCount === 0 || currentPageTickets.length === 0
      ? 0
      : Math.min(pageOffset + currentPageTickets.length, effectiveTotalCount);

  const showPriceColumn =
    isEventGroupedView ||
    activeTab === 'Listed' ||
    activeTab === 'Sold';

  const showExpandedCommission = activeTab === 'Sold';

  const eventGroupedTotalCount = useMemo(() => {
    if (!isEventGroupedView) return 0;
    return EVENT_TICKET_VIEW_SECTIONS.reduce(
      (sum, section) => sum + (eventGroupedByStatus[section.key]?.length ?? 0),
      0,
    );
  }, [isEventGroupedView, eventGroupedByStatus]);

  type ManageTicketTableRow =
    | { kind: 'section'; sectionKey: string; label: string; count: number }
    | { kind: 'ticket'; ticket: Ticket; rowTab: string };

  const manageTicketTableRows: ManageTicketTableRow[] = useMemo(() => {
    if (isEventGroupedView) {
      const tickets = getEventGroupedTicketsForTab(activeTab, eventGroupedByStatus);
      const header: ManageTicketTableRow = {
        kind: 'section',
        sectionKey: activeTab,
        label: activeTab,
        count: tickets.length,
      };
      const rows: ManageTicketTableRow[] = tickets.map((ticket) => ({
        kind: 'ticket',
        ticket,
        rowTab: activeTab,
      }));
      return [header, ...rows];
    }
    return sortManageTickets(currentPageTickets).map((ticket) => ({
      kind: 'ticket' as const,
      ticket,
      rowTab: activeTab,
    }));
  }, [isEventGroupedView, eventGroupedByStatus, currentPageTickets, activeTab]);

  const showDelistReturnEmailColumn =
    activeTab === 'Delisted' || activeTab === 'Listing Request Cancelled';

  const tableColSpan =
    (showPriceColumn ? 9 : 8) + (showDelistReturnEmailColumn ? 1 : 0);
  const ticketsTableLoading = isEventGroupedView
    ? eventGroupedLoading
    : isMailParserTpLookupView
    ? mailParserErrorLoading
    : loading || isLoading;
  const isStatusOperationInProgress =
    statusTableBusyCount > 0 ||
    bulkStatusChangeLoading ||
    bulkSoldLoading ||
    bulkListLoading ||
    statusChangeLoading ||
    listedModalLoading;
  const statusOperationMessage =
    statusTableBusyCount > 0 ? statusTableBusyMessage : 'Updating ticket status…';

  const isTicketRow = (row: ManageTicketTableRow): row is Extract<ManageTicketTableRow, { kind: 'ticket' }> =>
    row.kind === 'ticket';

  const ticketRowsForTable = manageTicketTableRows.filter(isTicketRow);
  const visibleTicketIds = useMemo(() => ticketRowsForTable.map((row) => row.ticket.tp_id), [ticketRowsForTable]);

  useEffect(() => {
    if (bulkSelectedTicketIds.size === 0) return;
    const visibleSet = new Set(visibleTicketIds);
    const nextSelection = new Set(Array.from(bulkSelectedTicketIds).filter(id => visibleSet.has(id)));
    if (nextSelection.size !== bulkSelectedTicketIds.size) {
      setBulkSelectedTicketIds(nextSelection);
    }
  }, [visibleTicketIds, bulkSelectedTicketIds]);

  const selectedTicketsList = useMemo(() => {
    return ticketRowsForTable
      .map((row) => row.ticket)
      .filter((ticket) => bulkSelectedTicketIds.has(ticket.tp_id));
  }, [ticketRowsForTable, bulkSelectedTicketIds]);

  const selectedSeatTicketsList = useMemo(() => {
    return filteredTickets.filter((ticket) => selectedTpIds.includes(ticket.tp_id));
  }, [filteredTickets, selectedTpIds]);

  const statusFilterButtonLabel = useMemo(() => {
    if (
      filterStatus === 'All Verification' ||
      filterStatus === 'All Listed' ||
      filterStatus === 'All Sold' ||
      filterStatus === 'All Delisted' ||
      filterStatus === 'All Listing Request Cancelled' ||
      filterStatus === 'All Expired' ||
      filterStatus === 'All Unsold'
    ) {
      return 'Filter Status';
    }
    return filterStatusOptions.find(opt => opt.value === filterStatus)?.label || 'Filter Status';
  }, [filterStatus, filterStatusOptions]);

  const handleUserClick = (userId: string) => {
    navigate(`/user/${userId}?tab=tickets`);
  };

  return (
    <div className={isDarkMode ? styles.dark : undefined}>
      {/* --- Listed Modal UI --- */}
      {showListedModal && listedModalTicket && (() => {
        const currentSt = listedModalTicket.tp_status || '';
        const getCtBadgeClass = (st: string) => {
          const s = (st || '').toLowerCase().replace(/\s/g, '');
          if (s === 'verified') return styles.ctStatusBadgeVerified;
          if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
          if (s === 'sold') return styles.ctStatusBadgeSold;
          if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
          if (s === 'invalid') return styles.ctStatusBadgeInvalid;
          return styles.ctStatusBadgeDefault;
        };
        return (
          <div className={styles.modalOverlay} onClick={() => setShowListedModal(false)}>
            <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
              <div className={styles.ctModalHeader}>
                <div className={styles.ctModalTitleRow}>
                  <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
                  <span className={styles.ctModalTitle}>Change Ticket Status</span>
                </div>
                <button className={styles.ctModalClose} onClick={() => setShowListedModal(false)}>×</button>
              </div>
              <div className={styles.ctModalBody}>
                <div className={styles.ctEventName}>{listedModalTicket.e_name}</div>
                <div className={styles.ctEventDetails}>
                  Section {listedModalTicket.tp_section || 'N/A'} • Row {listedModalTicket.tp_row || 'N/A'} • Seats {listedModalTicket.tp_seat_no || 'N/A'}
                </div>
                <div className={styles.ctEventDate}>
                  {formatDate(listedModalTicket.e_date)}{listedModalTicket.e_date_time_zone ? ` • ${formatTime(listedModalTicket.e_date_time_zone)}` : ''}
                </div>

                <div className={styles.ctCurrentStatusRow}>
                  <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                </div>

                <div className={styles.ctNewStatusTitle}>Select New Status:</div>
                <div className={styles.ctStatusOptions}>
                  <button className={`${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`}>
                    Listed
                  </button>
                </div>

                <div className={styles.ctTransitionVisual}>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                  <ArrowRight size={16} className={styles.ctTransArrow} />
                  <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                    {getStatusIcon('List')} Listed
                  </span>
                </div>

                <div className={styles.ctPriceLabel}>Listing Price (required):</div>
                <div className={styles.ctPriceInputWrapper}>
                  <span className={styles.ctPriceDollar}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.ctPriceInput}
                    value={listedPrice}
                    onChange={e => setListedPrice(sanitizePriceInput(e.target.value))}
                    placeholder="Enter listing price"
                  />
                </div>
              </div>
              <div className={styles.ctModalFooter}>
                <button className={styles.ctBackBtn} onClick={() => setShowListedModal(false)}>Back</button>
                <button
                  className={`${styles.ctConfirmBtn} ${(!listedPrice || listedModalLoading) ? styles.ctConfirmBtnDisabled : ''}`}
                  disabled={!listedPrice || listedModalLoading}
                  onClick={handleListedModalConfirm}
                >
                  {listedModalLoading ? 'Saving...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* --- Sold Modal UI (aligned with List Tickets → Sold Details / markAsSold) --- */}
      {showSoldModal && soldModalTicket && (() => {
        const currentSt = soldModalTicket.tp_status || '';
        const getCtBadgeClass = (st: string) => {
          const s = (st || '').toLowerCase().replace(/\s/g, '');
          if (s === 'verified') return styles.ctStatusBadgeVerified;
          if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
          if (s === 'sold') return styles.ctStatusBadgeSold;
          if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
          if (s === 'invalid') return styles.ctStatusBadgeInvalid;
          return styles.ctStatusBadgeDefault;
        };
        const listPriceNum = Number(soldModalTicket.tp_list_price) || 0;
        const selectedMp = marketplacesList.find(m => m.id === soldMarketplaceId);
        const logitixParsed = parseFloat(String(soldLogitixAmount || '').trim());
        const hasLogitixInput = !isNaN(logitixParsed) && logitixParsed > 0;
        const expectedFromLogitix =
          selectedMp && listPriceNum
            ? Math.round(listPriceNum * (100 - selectedMp.vl_fee_percentage)) / 100
            : null;
        const quickPct =
          selectedMp && soldFeeConfigReady
            ? effectiveSoldQuickCutPercent(
                selectedMp.vl_fee_percentage,
                soldFeeMode,
                soldSuperClientPercent,
              )
            : 0;
        const quickCutDollars =
          selectedMp && listPriceNum && soldFeeConfigReady
            ? Number(roundToFixed(listPriceNum * (quickPct / 100), 2))
            : 0;
        const receivedForSettlement =
          hasLogitixInput ? logitixParsed : expectedFromLogitix != null ? expectedFromLogitix : null;
        const usingEnteredLogitix = hasLogitixInput;
        const settlementAmount =
          receivedForSettlement != null &&
          selectedMp &&
          soldFeeConfigReady &&
          quickCutDollars != null
            ? Math.round((receivedForSettlement - quickCutDollars) * 100) / 100
            : null;
        const confirmDisabled =
          !soldFeeConfigReady ||
          markAsSoldLoading ||
          !soldMarketplaceId ||
          !String(soldLogitixAmount || '').trim() ||
          settlementAmount == null ||
          settlementAmount <= 0;
        return (
          <div className={styles.modalOverlay} onClick={closeSoldModal}>
            <div className={styles.ctModalContent} onClick={e => e.stopPropagation()}>
              <div className={styles.ctModalHeader}>
                <div className={styles.ctModalTitleRow}>
                  <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
                  <span className={styles.ctModalTitle}>Change Ticket Status</span>
                </div>
                <button type="button" className={styles.ctModalClose} onClick={closeSoldModal}>×</button>
              </div>
              <div className={styles.ctModalBody}>
                <div className={styles.ctEventName}>{soldModalTicket.e_name}</div>
                <div className={styles.ctEventDetails}>
                  Section {soldModalTicket.tp_section || 'N/A'} • Row {soldModalTicket.tp_row || 'N/A'} • Seats {soldModalTicket.tp_seat_no || 'N/A'}
                </div>
                <div className={styles.ctEventDate}>
                  {formatDate(soldModalTicket.e_date)}{soldModalTicket.e_date_time_zone ? ` • ${formatTime(soldModalTicket.e_date_time_zone)}` : ''}
                </div>

                <div className={styles.ctCurrentStatusRow} style={{ marginTop: 8 }}>
                  <span className={styles.ctCurrentStatusLabel}>List price:</span>
                  <b>${formatNumberWithCommas(listPriceNum)}</b>
                </div>

                <div className={styles.ctCurrentStatusRow}>
                  <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                </div>

                <div className={styles.ctNewStatusTitle}>Select New Status:</div>
                <div className={styles.ctStatusOptions}>
                  <button type="button" className={`${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`}>
                    Sold
                  </button>
                </div>

                {!soldFeeConfigReady ? (
                  <div style={{ margin: '12px 0', fontSize: 14 }}>Loading fee settings…</div>
                ) : null}

                <div style={{ marginBottom: 16 }}>
                  <div className={styles.ctPriceLabel} style={{ marginBottom: 8 }}>Marketplace:</div>
                  <select
                    className={styles.ctMarketplaceSelect}
                    value={soldMarketplaceId}
                    disabled={!soldFeeConfigReady || marketplacesList.length === 0}
                    onChange={e => setSoldMarketplaceId(e.target.value)}
                  >
                    <option value="">
                      {marketplacesList.length === 0 ? 'No active marketplaces available' : 'Choose a marketplace...'}
                    </option>
                    {marketplacesList.map(mp => (
                      <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <div className={styles.ctPriceLabel}>Amount received from Logitix</div>
                 
                  <div className={styles.cttPriceInputWrapper}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                    readOnly
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                     
                      value={soldLogitixAmount}
                     
                    />
                  </div>
                </div>

                <div className={styles.ctTransitionVisual}>
                  <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(currentSt)}`}>
                    {getStatusIcon(currentSt)} {getStatusLabel(currentSt)}
                  </span>
                  <ArrowRight size={16} className={styles.ctTransArrow} />
                  <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeSold}`}>
                    {getStatusIcon('Sold')} Sold
                  </span>
                </div>

                {selectedMp && soldFeeConfigReady ? (
                  <div className={styles.ctFeeBreakdown}>
                    <div><b>Sold details (preview)</b></div>
                    <div>Marketplace: {selectedMp.name} ({selectedMp.vl_fee_percentage}%)</div>
                    <div>
                      QuickAsyst fee{selectedMp ? ` (${quickPct}% of list)` : ''}:{' '}
                      <span style={{ color: '#b91c1c' }}>
                        {quickCutDollars != null ? `-$${formatNumberWithCommas(quickCutDollars)}` : '—'}
                      </span>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <span className={styles.ctCurrentStatusLabel}>Amount settled to user</span>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>
                      {settlementAmount != null ? `$${formatNumberWithCommas(settlementAmount)}` : '—'}
                    </div>
                    {receivedForSettlement != null && quickCutDollars != null && settlementAmount != null ? (
                      <div style={{ fontSize: 12, marginTop: 6 }}>
                        ${formatNumberWithCommas(receivedForSettlement)}{' '}
                        {usingEnteredLogitix ? 'received' : 'expected'} − $
                        {formatNumberWithCommas(quickCutDollars)} QuickAsyst fee
                        {usingEnteredLogitix ? '' : ' (preview)'}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className={styles.ctModalFooter}>
                <button type="button" className={styles.ctBackBtn} onClick={closeSoldModal}>Back</button>
                <button
                  type="button"
                  className={`${styles.ctConfirmBtn} ${confirmDisabled ? styles.ctConfirmBtnDisabled : ''}`}
                  disabled={confirmDisabled}
                  onClick={handleSoldModalConfirm}
                >
                  {markAsSoldLoading ? 'Saving…' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Main Component UI - Exact match to first image */}
      <div className={`${styles.container} ${isDarkMode ? styles.dark : ""}`}>
        <div className={styles.mainContent}>
          {/* Header Section */}
          <div className={styles.headerSection}>
            <div className={styles.headerLeft}>
              <p className={styles.subTitle}>All Tickets</p>
              <h1 className={styles.mainTitle}>Manage Tickets</h1>
            </div>
            <div className={styles.headerRight}>
              <button className={styles.iconButton} onClick={handleRefresh} title="Refresh">
                <RefreshRoundedIcon fontSize="small" />
              </button>
              <button className={styles.iconButton} onClick={() => setIsDarkMode(!isDarkMode)} title="Toggle theme">
                {isDarkMode ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            </div>
          </div>

          {/* Tracking Issues Cards (admin / sales rep ops — not shown for self-pricing super user) */}
          {!isSuperUser && (
          <div className={styles.trackingSection}>
            <h3 className={styles.trackingSectionTitle}>
              <AlertCircle size={16} style={{ color: '#f59e0b' }} /> Tracking Issues
            </h3>
            <div className={styles.trackingIssues}>
              <div className={styles.trackingCard}>
                <div className={styles.trackingCardContent}>
                  <div>
                    <div className={styles.trackingLabel}>Listing Request Expired</div>
                    <div className={styles.trackingNumber}>{trackingCounts.listingExpired}</div>
                    <div className={styles.trackingDesc}>Never transferred, event started</div>
                    <button
                      className={styles.trackingButton}
                      onClick={() => handleOpenCardDetailModal("LISTING_EXPIRED")}
                    >
                      View All <ExternalLink size={12} style={{ display: 'inline', marginLeft: 4 }} />
                    </button>
                  </div>
                  <Clock size={36} className={styles.trackingIconRedLarge} />
                </div>
              </div>

              <div className={styles.trackingCard}>
                <div className={styles.trackingCardContent}>
                  <div>
                    <div className={styles.trackingLabel}>Fail to Transfer</div>
                    <div className={styles.trackingNumber} style={{ color: '#f59e0b' }}>{trackingCounts.failedTransfer}</div>
                    <div className={styles.trackingDesc}>48+ hours in To Be Verified</div>
                    <button
                      className={styles.trackingButtonOrange}
                      onClick={() => handleOpenCardDetailModal("FAILED_TRANSFER")}
                    >
                      View Details <ExternalLink size={12} style={{ display: 'inline', marginLeft: 4 }} />
                    </button>
                  </div>
                  <AlertCircle size={36} className={styles.trackingIconOrangeLarge} />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Tabs + Filter Row — combined */}
          <div className={styles.tabsAndFilterRow}>
            <div className={styles.tabsGroup}>
              {!isSuperUser && (
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Verification' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Verification')}
              >
                Verification ({tabCounts.Verification})
              </button>
              )}
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Listed' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Listed')}
              >
                Listed ({tabCounts.Listed})
              </button>
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Sold' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Sold')}
              >
                Sold ({tabCounts.Sold})
              </button>
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Delisted' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Delisted')}
              >
                Delisted ({tabCounts.Delisted})
              </button>
              {!isSuperUser && (
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Listing Request Cancelled' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Listing Request Cancelled')}
              >
                Listing Request Cancelled ({tabCounts['Listing Request Cancelled']})
              </button>
              )}
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Unsold' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Unsold')}
              >
                Unsold ({tabCounts.Unsold})
              </button>
              {!isSuperUser && (
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Listing Expired' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Listing Expired')}
              >
                Listing Expired ({isMarketingUser ? (tabCounts['Listing Expired'] ?? 0) : trackingCounts.listingExpired})
              </button>
              )}
              <button
                className={`${styles.statusTabBtn} ${activeTab === 'Deleted' ? styles.activeTab : ''}`}
                onClick={() => handleTabChange('Deleted')}
              >
                Deleted ({tabCounts.Deleted})
              </button>
            </div>

            <div className={styles.filterGroup}>
              <div className={styles.filterDropdownWrapper}>
                <button
                  className={styles.filterStatusBtn}
                  type="button"
                  onClick={() => {
                    setShowFilterDropdown(!showFilterDropdown);
                    setShowClientDropdown(false);
                  }}
                >
                  <Filter size={13} style={{ marginRight: 4 }} /> 
                  {statusFilterButtonLabel}
                </button>
                {showFilterDropdown && (
                  <div className={styles.filterDropdown}>
                    {filterStatusOptions.map(option => (
                      <div
                        key={option.value}
                        className={`${styles.filterDropdownItem} ${filterStatus === option.value ? styles.filterDropdownItemActive : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterStatus(option.value);
                          setShowFilterDropdown(false);
                          setPageOffset(0);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.filterDropdownWrapper}>
                <button
                  className={styles.filterStatusBtn}
                  type="button"
                  onClick={() => {
                    setShowClientDropdown(!showClientDropdown);
                    setShowFilterDropdown(false);
                  }}
                >
                  {clientType === 'All'
                    ? 'Client Type'
                    : `${clientType} (${totalCount})`}
                </button>
                {showClientDropdown && (
                  <div className={styles.filterDropdown}>
                    {['All', 'Self Pricing', 'Concierge'].map(ct => {
                      const count = clientTypeCounts[ct as keyof typeof clientTypeCounts] ?? 0;
                      const countLabel = clientTypeCountsLoading && lastClientTypeCountsQueryKeyRef.current !== ticketsQueryKey
                        ? '...'
                        : count;
                      return (
                        <div
                          key={ct}
                          className={`${styles.filterDropdownItem} ${clientType === ct ? styles.filterDropdownItemActive : ''}`}
                          onClick={(e) => { e.stopPropagation(); setClientType(ct); setShowClientDropdown(false); }}
                        >
                          <span>{ct}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280' }}>
                            ({countLabel})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* --- NEW EVENT DATE FILTERS SECTION --- */}
              <div className={styles.dateFilterWrapper}>
                <button
                  className={`${styles.dateFilterBtn} ${datePreset === 'today' ? styles.activeDateFilter : ''}`}
                  onClick={() => applyDatePreset('today')}
                >
                  Today
                </button>
                <button
                  className={`${styles.dateFilterBtn} ${datePreset === 'tomorrow' ? styles.activeDateFilter : ''}`}
                  onClick={() => applyDatePreset('tomorrow')}
                >
                  Tomorrow
                </button>
                <button
                  className={`${styles.dateFilterBtn} ${datePreset === 'next7days' ? styles.activeDateFilter : ''}`}
                  onClick={() => applyDatePreset('next7days')}
                >
                  Next 7 days
                </button>
                <button
                  className={styles.dateFilterBtn}
                  onClick={() => {
                    setCustomRangeStart(startDate || getTodayStr());
                    setCustomRangeEnd(endDate || getTodayStr());
                    setShowCustomRangeModal(true);
                  }}
                >
                  <Calendar size={14} style={{ marginRight: 4 }} />
                  Custom
                </button>
                {getActiveDateFilterLabel() && (
                  <span className={styles.activeDatePill}>
                    {getActiveDateFilterLabel()}
                    <X
                      size={14}
                      style={{ marginLeft: 6, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDateFilter();
                      }}
                    />
                  </span>
                )}
              </div>

              <div className={styles.searchEventWrapper}>
                <SearchIcon size={15} className={styles.searchIconSmall} />
                <input
                  type="text"
                  placeholder="Search event, team, venue..."
                  className={styles.searchInputEvent}
                  value={debouncedEventVenueSearch}
                  onChange={(e) => setDebouncedEventVenueSearch(e.target.value)}
                />
              </div>

              <div className={styles.searchClientWrapper}>
                <SearchIcon size={15} className={styles.searchIconSmall} />
                <input
                  type="text"
                  placeholder="Search customer name or email..."
                  className={styles.searchInputEvent}
                  value={debouncedClientSearch}
                  onChange={(e) => setDebouncedClientSearch(e.target.value)}
                />
              </div>

              <button className={styles.exportButton} onClick={handleExport}>
                <Download size={16} /> Export CSV
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className={styles.resultsInfo}>
            {isEventGroupedView ? (
              <span className={styles.eventViewSummary}>
                Event view: {eventGroupedTotalCount} tickets — Pending ({eventGroupedByStatus.pending.length}),
                Listed ({eventGroupedByStatus.listed.length}), Sold ({eventGroupedByStatus.sold.length}),
                Delisted ({eventGroupedByStatus.delisted.filter(t => t.tp_status !== 'ListingRequestCancelled').length}),
                Listing Request Cancelled ({eventGroupedByStatus.delisted.filter(t => t.tp_status === 'ListingRequestCancelled').length}),
                Listing Request Expired ({eventGroupedListingExpiredCount}),
                Unsold ({eventGroupedUnsoldCount}), Deleted ({eventGroupedByStatus.deleted.length})
              </span>
            ) : (
              <span>
                {effectiveTotalCount === 0
                  ? 'Showing 0 of 0 tickets'
                  : `Showing ${startIndex} to ${endIndex} of ${effectiveTotalCount} tickets`}
              </span>
            )}
            {hasActiveFilters() && (
              <button className={styles.clearFiltersBtn} onClick={clearAllFilters}>
                Clear All Filters
              </button>
            )}
          </div>

          {/* Expanded-seat bulk actions */}
          {showBulkActions && (() => {
            const selectedStatuses: string[] = selectedSeatTicketsList.map((ticket) => normalizeStatusValue(ticket.tp_status || ''));
            const hasSelectedTickets = selectedSeatTicketsList.length > 0;
            const hasSoldSelected = selectedStatuses.includes('Sold');
            const canMarkSelectedSold =
              hasSelectedTickets && selectedStatuses.every((status) => status === 'List' || status === 'Unsold');
            const canDelistSelected =
              hasSelectedTickets && selectedStatuses.every((status) => status === 'List');
            const canChangeSelectedPrice =
              hasSelectedTickets && selectedStatuses.every((status) => status === 'List');
            const showChangeStatusAction =
              hasSelectedTickets && (!canMarkSelectedSold || hasSoldSelected);

            return (
              <div className={styles.bulkActionsBar}>
                <div className={styles.bulkActionsLeft}>
                  <CheckSquare size={16} />
                  <span>{selectedTpIds.length} ticket(s) selected</span>
                  <button className={styles.clearSelectionBtn} onClick={() => {
                    setSelectedSeats({});
                    setSelectAllSeats(false);
                    setShowBulkActions(false);
                    setSelectedTpIds([]);
                    setBulkSelectedTicketIds(new Set());
                  }}>
                    Clear
                  </button>
                </div>
                <div className={styles.bulkActionsRight}>
                  {canMarkSelectedSold && (
                    <button className={styles.bulkMarkSold} onClick={() => handleBulkAction('markSold')}>
                      <CheckCircle size={14} /> Mark as Sold
                    </button>
                  )}
                  {showChangeStatusAction && (
                    <button className={styles.bulkChangePrice} onClick={() => openBulkStatusModal(selectedTpIds)}>
                      <CheckCircle size={14} /> Change Status
                    </button>
                  )}
                  {canDelistSelected && (
                    <button className={styles.bulkDelist} onClick={() => handleBulkAction('delist')}>
                      <Package size={14} /> Delist
                    </button>
                  )}
                  {canChangeSelectedPrice && activeTab === 'Listed' && (
                    <button className={styles.bulkChangePrice} onClick={() => handleBulkAction('changePrice')}>
                      <DollarSign size={14} /> Change Price
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Loading State */}
          {ticketsTableLoading && (
            <div className={styles.loadingState}>
              <div className={styles.spinner}></div>
              <p>Loading tickets...</p>
            </div>
          )}

          {/* Error State */}
          {queryError && !isEventGroupedView && !ticketsTableLoading && (
            <div className={styles.errorState}>
              <AlertCircle size={48} />
              <h3>Error Loading Tickets</h3>
              <p>{queryError.message}</p>
              <button onClick={handleRefresh} className={styles.retryBtn}>Retry</button>
            </div>
          )}

          {/* Tickets Table */}
          {!ticketsTableLoading && !(queryError && !isEventGroupedView) && (
            <div className={styles.tableBusyWrap}>
              {isStatusOperationInProgress && (
                <div className={styles.tableBusyOverlay} role="status" aria-live="polite">
                  <div className={styles.spinner} />
                  <p>{statusOperationMessage}</p>
                </div>
              )}
            <>
              {/* Bulk selection actions bar */}
              {bulkSelectedTicketIds.size > 0 && (
                <div className={styles.bulkActionsBar}>
                  <div className={styles.bulkActionsLeft}>
                    <div><strong>{bulkSelectedTicketIds.size}</strong> ticket{bulkSelectedTicketIds.size > 1 ? 's' : ''} selected</div>
                    {selectedTicketsList.length > 0 && (
                      <div style={{ marginLeft: 8 }}>
                        Event: {selectedTicketsList[0].e_name || 'N/A'} • Section: {selectedTicketsList[0].tp_section || 'N/A'} • Row: {selectedTicketsList[0].tp_row || 'N/A'}
                      </div>
                    )}
                    <button className={styles.clearSelectionBtn} onClick={() => { setBulkSelectedTicketIds(new Set()); setBulkSelectedTickets([]); }}>Clear Selection</button>
                  </div>
               <div className={styles.bulkActionsRight}>
  {selectedTicketsList.length > 0 && selectedTicketsList.every(t => normalizeStatusValue(t.tp_status || '') === 'List') && (
    <button className={styles.bulkChangePrice} onClick={() => openBulkPriceModal()}>
      <DollarSign size={14} /> Change Price
    </button>
  )}
  <button className={styles.bulkChangePrice} onClick={() => openBulkStatusModal()}>
    Bulk Update Status
  </button>
</div>
                </div>
              )}
              <div className={styles.tableContainer}>
                <table className={styles.ticketsTable}>
                  <thead>
                    <tr>
                      <th className={styles.checkboxColumn}>
                        <input
                          type="checkbox"
                          className={styles.bulkSelectCheckbox}
                          checked={visibleTicketIds.length > 0 && visibleTicketIds.every(id => bulkSelectedTicketIds.has(id))}
                          onChange={(e) => handleBulkSelectAllPage(e.target.checked)}
                        />
                      </th>
                      <th className={styles.thEvent}>Event Name</th>
                      <th className={styles.thSection}>Section</th>
                      <th className={styles.thRow}>Row</th>
                      <th className={styles.thSeat}>Seat #s</th>
                      <th className={styles.thStatus}>Status</th>
                      <th className={styles.thClient}>Client Name</th>
                      {showDelistReturnEmailColumn && (
                        <th className={styles.thReturnEmail}>Return Email</th>
                      )}
                      <th className={styles.thClientType}>Client Type</th>
                      {showPriceColumn && (
                        <th className={styles.thPrice}>Price</th>
                      )}
                      <th className={styles.thActions}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketRowsForTable.length > 0 ? (
                      manageTicketTableRows.map((row) => {
                        if (row.kind === 'section') {
                          return (
                            <tr key={`event-section-${row.sectionKey}`} className={styles.eventGroupHeaderRow}>
                              <td colSpan={tableColSpan}>
                                <span className={styles.eventGroupHeaderLabel}>{row.label}</span>
                                <span className={styles.eventGroupHeaderCount}>({row.count})</span>
                              </td>
                            </tr>
                          );
                        }

                        const ticket = row.ticket;
                        const rowTab = row.rowTab;

                        return (
                        <React.Fragment key={ticket.tp_id}>
                          <tr
                            className={[
                              styles.ticketRow,
                              (ticket.tp_status || '').toLowerCase() === 'listing expired' ? styles.listingExpiredRow : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => toggleRow(ticket.tp_id)}
                          >
                             <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
    <input
      type="checkbox"
      className={styles.ticketCheckbox}
      checked={bulkSelectedTicketIds.has(ticket.tp_id)}
      onChange={(e) => handleBulkSelectTicket(ticket.tp_id, e.target.checked)}
    />
  </td>
                            <td className={styles.tdEvent}>
                              <div className={styles.eventName}>{ticket.e_name || '-'}</div>
                             <div className={styles.eventDate}>
  <Calendar size={10} /> 
  {formatDate(ticket.e_date)} • {formatTime(ticket.e_date)}
</div>
                            </td>
                            <td className={styles.tdSection}>{ticket.tp_section || 'N/A'}</td>
                            <td className={styles.tdRow}>{ticket.tp_row || 'N/A'}</td>
                            <td className={styles.tdSeat}>{ticket.tp_seat_no || 'N/A'}</td>
                         <td className={styles.tdStatus}>
  {(() => {
    const status = (ticket.tp_status || '').toLowerCase();
    const pres = getManageTicketsStatusPresentation(ticket);
    const paymentUiKey = (pres.iconStatus || '').toLowerCase();
    // Payment-driven Sold labels stay read-only unless an admin is applying a manual override.
    if (!canOverrideSettlementStatus && (paymentUiKey === 'settled' || paymentUiKey === 'settlementinprogress')) {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    // For terminal statuses, just show badge without dropdown
    if (status === 'return' || status === 'closed' || status === 'unsold' || status === 'invalid') {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    const transitions = getStatusTransitions(getTicketTransitionStatus(ticket));
    if (!transitions.length) {
      return (
        <span className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)}`}>
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
        </span>
      );
    }
    return (
      <div className={styles.statusDropdownWrapper}>
        <button
          className={`${styles.statusBadge} ${getStatusColor(pres.iconStatus)} ${styles.statusDropdownBtn}`}
          disabled={isStatusOperationInProgress}
          onClick={(e) => {
            e.stopPropagation();
            if (isStatusOperationInProgress) return;
            setOpenStatusDropdown(openStatusDropdown === ticket.tp_id ? null : ticket.tp_id);
          }}
        >
          {getStatusIcon(pres.iconStatus)}
          {pres.label}
          <ChevronDown size={12} style={{ marginLeft: 4 }} />
        </button>
        {openStatusDropdown === ticket.tp_id && (
          <div className={styles.statusDropdownList}>
            {transitions.map(opt => (
              <div
                key={opt.value}
                className={styles.statusDropdownListItem}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenStatusDropdown(null);
                  handleStatusChangeConfirm(ticket, opt.value);
                }}
              >
                {getStatusIcon(opt.value)}
                {opt.label}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  })()}
</td>
                            <td className={styles.tdClient}>
                              <div 
                                className={styles.clientName}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUserClick(ticket.u_id);
                                }}
                              >
                                {ticket.u_full_name ||
                                  `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() ||
                                  'N/A'}
                              </div>
                              <div className={styles.clientEmail}>{ticket.u_email_id || 'N/A'}</div>
                            </td>
                            {showDelistReturnEmailColumn && (
                              <td className={styles.tdReturnEmail}>
                                <div className={styles.clientEmail}>
                                  {ticket.tp_delist_requested_email?.trim() || '—'}
                                </div>
                              </td>
                            )}
                            <td className={styles.tdClientType}>
                              <span
                                className={
                                  styles.clientTypeBadge + ' ' +
                                  (getResolvedClientType(ticket) === 'self-pricing'
                                    ? styles.selfPricingBadge
                                    : getResolvedClientType(ticket) === 'concierge'
                                    ? styles.conciergeBadge
                                    : '')
                                }
                              >
                                {getResolvedClientType(ticket) === 'self-pricing'
                                  ? 'Self Pricing'
                                  : getResolvedClientType(ticket) === 'concierge'
                                  ? 'Concierge'
                                  : 'N/A'}
                              </span>
                            </td>
                            {showPriceColumn && (() => {
                              const soldAmount = Number(ticket.tp_sold_amount ?? 0);
                              const hasSoldAmount =
                                Number.isFinite(soldAmount) &&
                                soldAmount > 0;
                              const listAmount = Number(ticket.tp_list_price ?? 0);
                              const hasListPrice = Number.isFinite(listAmount) && listAmount > 0;
                              const display =
                                hasSoldAmount
                                  ? formatCurrency(ticket.tp_sold_amount ?? 0)
                                  : hasListPrice
                                    ? formatCurrency(ticket.tp_list_price ?? 0)
                                    : '—';
                              return <td className={styles.tdPrice}>{display}</td>
                            })()}
                            <td className={styles.tdActions}>
                              <div className={styles.actionMenuWrapper}>
                                <button
                                  className={styles.actionMenuBtn}
                                  onClick={(e) => toggleActionMenu(ticket.tp_id, e)}
                                >
                                  <MoreVertical size={16} />
                                </button>
                                {openActionMenu === ticket.tp_id && (
                                  <div className={styles.actionMenuList}>
                                    <button className={styles.actionMenuItem} onClick={(e) => handleAction('View Details', ticket, e)}>
                                      <Eye size={14} /> View Details
                                    </button>
                                {(ticket.tp_status !== 'Return' && rowTab !== 'Deleted') && (
  <button className={styles.actionMenuItem} onClick={(e) => handleAction('Change Status', ticket, e)}>
    <CheckCircle size={14} /> Change Status
  </button>
)}
                                    <button className={styles.actionMenuItem} onClick={(e) => handleAction('Edit Details', ticket, e)}>
                                      <Edit size={14} /> Edit Details
                                    </button>
                                    {rowTab === 'Listed' &&
                                      (getResolvedClientType(ticket) === 'self-pricing' ||
                                        getResolvedClientType(ticket) === 'concierge') && (
                                        <button
                                          className={styles.actionMenuItem}
                                          onClick={(e) => handleAction('Change Price', ticket, e)}
                                        >
                                          <DollarSign size={14} /> Change Price
                                        </button>
                                      )}
                                    {rowTab === 'Listed' && ticket.tp_status === 'List' && (
                                      <button className={styles.actionMenuItem} onClick={(e) => handleAction('Delist', ticket, e)}>
                                        <Package size={14} /> Delist
                                      </button>
                                    )}
                                 {(() => {
  const status = normalizeStatusValue(ticket.tp_status || '');
  const allowedStatuses = ['Verified', 'List', 'Delist','ToBeVerified'];
  if (allowedStatuses.includes(status)) {
    return (
      <button className={styles.actionMenuItem} onClick={(e) => {
        e.stopPropagation();
        setSelectedTicketForDelete(ticket);
        setShowDeleteConfirmModal(true);
        setOpenActionMenu(null);
      }}>
        <X size={14} /> Delete
      </button>
    );
  }
  return null;
})()}
                                    <button className={styles.actionMenuItem} onClick={(e) => handleAction('View Client Profile', ticket, e)}>
                                      <FileText size={14} /> View Client Profile
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* Expanded Row for Seats */}
                          {expandedRows.includes(ticket.tp_id) && (
                            <tr className={styles.expandedRow}>
                              <td colSpan={tableColSpan}>
                                <div className={styles.expandedContent}>
                                  {/* Venue / League / Email meta row */}
                                  <div className={styles.expandedMeta}>
                                    <span>
                                      <span className={styles.expandedMetaLabel}>Venue:</span>{' '}
                                      {ticket.e_address || 'N/A'}
                                    </span>
                                    <span className={styles.expandedMetaDivider}>|</span>
                                    <span>
                                      <span className={styles.expandedMetaLabel}>League:</span>{' '}
                                      {ticket.l_name || 'N/A'}
                                    </span>
                                    <span className={styles.expandedMetaDivider}>|</span>
                                    <span>
                                      <span className={styles.expandedMetaLabel}>Email:</span>{' '}
                                      {ticket.u_email_id || (ticket as any).u_original_email || 'N/A'}
                                    </span>
                                    {showExpandedCommission && (
                                      <>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Commission:</span>{' '}
                                          {getSalesRepCommission(ticket) > 0
                                            ? formatCurrency(getSalesRepCommission(ticket))
                                            : '—'}
                                        </span>
                                        <span className={styles.expandedMetaDivider}>|</span>
                                        <span>
                                          <span className={styles.expandedMetaLabel}>Marketplace:</span>{' '}
                                          {getTicketMarketplaceName(ticket) || '—'}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                  <div className={styles.expandedHeader}>
                                    <div>
                                      <h4>
                                        Individual Seats{' '}
                                        <span className={styles.seatCountLabel}>({generateSeats(ticket).length} seats)</span>
                                      </h4>
                                    </div>
                                    <div className={styles.selectAllContainer}>
                                      <button className={styles.selectAllBtn} onClick={(e) => {
                                        e.stopPropagation();
                                        const seats = generateSeats(ticket);
                                        const seatIds = seats.map(s => s.id);
                                        const allSelected = seatIds.every(id => selectedSeats[id]);
                                        if (allSelected) {
                                          const newSelected = { ...selectedSeats };
                                          seatIds.forEach(id => delete newSelected[id]);
                                          setSelectedSeats(newSelected);
                                          const tpIdSet = new Set<string>();
                                          Object.keys(newSelected).forEach(id => {
                                            const seat = getAllSeats().find(s => s.id === id);
                                            if (seat?.tp_id) tpIdSet.add(seat.tp_id);
                                          });
                                          setSelectedTpIds(Array.from(tpIdSet));
                                        } else {
                                          const newSelected = { ...selectedSeats };
                                          seatIds.forEach(id => newSelected[id] = true);
                                          setSelectedSeats(newSelected);
                                          const tpIdSet = new Set<string>();
                                          Object.keys(newSelected).forEach(id => {
                                            const seat = getAllSeats().find(s => s.id === id);
                                            if (seat?.tp_id) tpIdSet.add(seat.tp_id);
                                          });
                                          setSelectedTpIds(Array.from(tpIdSet));
                                        }
                                        const totalSeats = getAllSeats().length;
                                        const selectedCount = Object.keys(selectedSeats).length;
                                        setSelectAllSeats(selectedCount === totalSeats);
                                        setShowBulkActions(selectedCount > 0);
                                      }}>
                                        <CheckSquare size={14} />
                                        {generateSeats(ticket).every(s => selectedSeats[s.id]) ? 'Deselect All' : 'Select All'}
                                      </button>
                                    </div>
                                  </div>
                                  <div className={styles.seatsGrid}>
                                    {generateSeats(ticket).map((seat, idx) => (
                                      <div
                                        key={idx}
                                        className={`${styles.seatCard} ${selectedSeats[seat.id] ? styles.selectedSeat : ''}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSelectSeat(seat.id);
                                        }}
                                      >
                                        <div className={styles.seatCardHeader}>
                                          <div className={styles.seatSelection}>
                                            {selectedSeats[seat.id] ? 
                                              <CheckSquare size={14} className={styles.checkedIcon} /> : 
                                              <Square size={14} className={styles.uncheckedIcon} />
                                            }
                                            <span className={styles.seatNumber}>Seat {seat.number}</span>
                                          </div>
                                          <span className={`${styles.seatStatusBadge} ${styles[`status${seat.status}`] || styles.statusDefault}`}>
                                            {getStatusLabel(seat.status)}
                                          </span>
                                        </div>
                                        <div className={styles.seatDetails}>
                                          <span>Sec {seat.section} • Row {seat.row}</span>
                                          <span className={styles.seatPrice}>{formatCurrency(Number(seat.price) || 0)}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={tableColSpan} className={styles.noResults}>
                          {isEventGroupedView
                            ? 'No tickets found for this event'
                            : 'No tickets found matching the selected filters'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
{/* Bulk Status Change Modal */}
{showBulkStatusModal && bulkSelectedTickets.length > 0 && (() => {
  const isBulkListTransition = normalizeStatusValue(bulkNewStatus) === 'List';
  const isBulkSoldTransition = normalizeStatusValue(bulkNewStatus) === 'Sold';
  const bulkUndoToListTickets = bulkSelectedTickets.filter(ticket => {
    const status = normalizeStatusValue(ticket.tp_status || '');
    return status === 'Delist' || status === 'DelistInProgress' || status === 'DelistRequested';
  });
  const bulkPublishToListTickets = bulkSelectedTickets.filter(
    ticket => normalizeStatusValue(ticket.tp_status || '') === 'Verified',
  );
  const isBulkListUndoOnly =
    isBulkListTransition && bulkUndoToListTickets.length === bulkSelectedTickets.length;
  const isBulkListPublishOnly =
    isBulkListTransition && bulkPublishToListTickets.length === bulkSelectedTickets.length;
  const bulkListPricesValid = isBulkListTransition
    ? areAllBulkStatusListingPricesValid(bulkSelectedTickets, bulkStatusListingPrices)
    : true;
  const bulkSoldReady = isBulkSoldTransitionReady(
    bulkSelectedTickets,
    bulkSoldLogitixAmounts,
    bulkSoldMarketplaceId,
    bulkSoldFeeConfigReady,
    marketplacesList,
    bulkSoldFeeConfigByTicketId,
  );
  const bulkSelectedMp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
  const bulkSeatLabels = bulkSelectedTickets
    .map(t => t.tp_seat_no)
    .filter(seat => seat != null && String(seat).trim() !== '')
    .join(', ');
  const bulkConfirmDisabled =
    !bulkNewStatus ||
    bulkStatusChangeLoading ||
    bulkSoldLoading ||
    (isBulkListTransition && (!isBulkListUndoOnly && !isBulkListPublishOnly || !bulkListPricesValid)) ||
    (isBulkSoldTransition && !bulkSoldReady);

  return (
  <div className={styles.modalOverlay} onClick={closeBulkStatusModal}>
    <div
      className={styles.ctModalContent}
      onClick={(e) => e.stopPropagation()}
      style={{ maxWidth: isBulkListTransition || isBulkSoldTransition ? '720px' : '600px' }}
    >
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
          <span className={styles.ctModalTitle}>
            {isBulkSoldTransition ? 'Change Ticket Status' : `Bulk Status Change (${bulkSelectedTickets.length} tickets)`}
          </span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkStatusModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        {/* Show warning if tickets are from different groups */}
        {(() => {
          const validation = canBulkUpdateTickets(bulkSelectedTickets.map(t => t.tp_id));
          if (!validation.allowed && validation.reason) {
            return (
              <div className={styles.bulkActionWarning}>
                <AlertCircle size={16} />
                <span>{validation.reason}</span>
              </div>
            );
          }
          return null;
        })()}
        
        <div className={styles.ctEventName}>
          {bulkSelectedTickets[0]?.e_name}
          {bulkSelectedTickets.some(t => t.e_id !== bulkSelectedTickets[0]?.e_id) && ' (Multiple Events)'}
        </div>
        <div className={styles.ctEventDetails}>
          {isBulkSoldTransition ? (
            <>
              Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
              {bulkSeatLabels ? ` • Seats ${bulkSeatLabels}` : ''}
            </>
          ) : (
            <>
              Section: {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row: {bulkSelectedTickets[0]?.tp_row || 'N/A'}
              {bulkSelectedTickets.some(t => t.tp_section !== bulkSelectedTickets[0]?.tp_section ||
                                           t.tp_row !== bulkSelectedTickets[0]?.tp_row) &&
                ' (Multiple sections/rows - bulk update not allowed)'}
            </>
          )}
        </div>
        {isBulkSoldTransition && bulkSelectedTickets[0]?.e_date ? (
          <div className={styles.ctEventDate}>
            {formatDate(bulkSelectedTickets[0].e_date)}
            {bulkSelectedTickets[0].e_date_time_zone ? ` • ${formatTime(bulkSelectedTickets[0].e_date_time_zone)}` : ''}
          </div>
        ) : null}

        {isBulkSoldTransition ? (
          <div className={styles.ctCurrentStatusRow} style={{ marginTop: 8 }}>
            <span className={styles.ctCurrentStatusLabel}>Tickets selected:</span>
            <b>{bulkSelectedTickets.length}</b>
          </div>
        ) : null}

        <div className={styles.ctCurrentStatusRow}>
          <span className={styles.ctCurrentStatusLabel}>
            {isBulkSoldTransition ? 'Current Status:' : 'Current Statuses:'}
          </span>
          {isBulkSoldTransition ? (
            <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
              {getStatusIcon('List')} Listed
            </span>
          ) : (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Array.from(new Set(bulkSelectedTickets.map(t => getStatusLabel(t.tp_status || '')))).map(status => (
                <span key={status} className={`${styles.ctStatusBadge} ${getCtBadgeClassForStatus(status)}`}>
                  {getStatusIcon(status)} {status}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.ctNewStatusTitle}>Select New Status:</div>
        <div className={styles.ctStatusOptions}>
          {getBulkStatusTransitions(bulkSelectedTickets).map((st) => (
            <button
              key={st.value}
              className={`${styles.ctStatusOption} ${bulkNewStatus === st.value ? styles.ctStatusOptionSelected : ''}`}
              onClick={() => handleBulkNewStatusSelect(st.value)}
            >
              {st.label}
            </button>
          ))}
        </div>

        {isBulkSoldTransition ? (
          <>
            {!bulkSoldFeeConfigReady ? (
              <div style={{ margin: '12px 0', fontSize: 14 }}>Loading fee settings…</div>
            ) : null}

            <div style={{ marginBottom: 16 }}>
              <div className={styles.ctPriceLabel} style={{ marginBottom: 8 }}>Marketplace:</div>
              <select
                className={styles.ctMarketplaceSelect}
                value={bulkSoldMarketplaceId}
                disabled={!bulkSoldFeeConfigReady || marketplacesList.length === 0}
                onChange={e => setBulkSoldMarketplaceId(e.target.value)}
              >
                <option value="">
                  {marketplacesList.length === 0 ? 'No active marketplaces available' : 'Choose a marketplace...'}
                </option>
                {marketplacesList.map(mp => (
                  <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
                ))}
              </select>
            </div>

            {/* <div className={styles.ctPriceLabel} style={{ marginTop: 4 }}>Apply same Logitix amount to all (optional)</div>
            <div style={{ fontSize: 12, marginBottom: 6 }}>
              Defaults to list price minus marketplace fee for each ticket.
            </div>
            <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
              <span className={styles.ctPriceDollar}>$</span>
              <input
                type="text"
                inputMode="decimal"
                className={styles.ctPriceInput}
                value={bulkSoldCommonLogitixAmount}
                onChange={e => setBulkSoldCommonLogitixAmount(sanitizePriceInput(e.target.value))}
                placeholder="Enter amount"
                disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
              />
              <button
                type="button"
                className={styles.ctConfirmBtn}
              
                onClick={handleApplyBulkSoldAmount}
                disabled={!bulkSoldFeeConfigReady || !bulkSoldMarketplaceId}
              >
                Apply to all
              </button>
            </div> */}

            <div className={styles.ctTransitionVisual}>
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeListed}`}>
                {getStatusIcon('List')} Listed
              </span>
              <ArrowRight size={16} className={styles.ctTransArrow} />
              <span className={`${styles.ctStatusBadge} ${styles.ctStatusBadgeSold}`}>
                {getStatusIcon('Sold')} Sold
              </span>
            </div>
          </>
        ) : bulkNewStatus ? (
          <div className={styles.ctTransitionVisual}>
            <span className={styles.ctStatusBadge}>
              {bulkSelectedTickets.length} Ticket(s)
            </span>
            <ArrowRight size={16} className={styles.ctTransArrow} />
            <span className={`${styles.ctStatusBadge} ${getCtBadgeClassForStatus(bulkNewStatus)}`}>
              {getStatusIcon(bulkNewStatus)} {getStatusLabel(bulkNewStatus)}
            </span>
          </div>
        ) : null}

        {isBulkListTransition && isBulkListUndoOnly ? (
          <div style={{ marginTop: 12, fontSize: 13, color: '#4b5563' }}>
            Undo delist and return these tickets to the <strong>Listed</strong> tab. Enter a listing price for each seat (required if current price is $0).
          </div>
        ) : null}

        {isBulkListTransition && (
          <>
            <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>Apply same list price to all (optional)</div>
            <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
              <span className={styles.ctPriceDollar}>$</span>
              <input
                type="text"
                inputMode="decimal"
                className={styles.ctPriceInput}
                value={bulkStatusListingCommonPrice}
                onChange={e => setBulkStatusListingCommonPrice(sanitizePriceInput(e.target.value))}
                placeholder="Enter price"
              />
              <button
                type="button"
                className={styles.ctConfirmBtn}
                style={{ marginLeft: 12, minWidth: 120 }}
                onClick={handleApplyBulkStatusListingPrice}
              >
                Apply to all
              </button>
            </div>
          </>
        )}
        
        {/* Preview of affected tickets */}
        <div style={{ marginTop: 16, maxHeight: isBulkListTransition || isBulkSoldTransition ? '360px' : '200px', overflowY: 'auto', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
            {isBulkListTransition
              ? 'Listing price per ticket (required):'
              : isBulkSoldTransition
                ? 'Amount received from Logitix per ticket:'
                : 'Affected tickets:'}
          </div>
          {(isBulkListTransition || isBulkSoldTransition ? bulkSelectedTickets : bulkSelectedTickets.slice(0, 5)).map(ticket => {
            const soldAmount = Number(ticket.tp_sold_amount ?? 0);
            const hasSoldAmount = Number.isFinite(soldAmount) && soldAmount > 0;
            const currentPrice = hasSoldAmount
              ? soldAmount
              : ticket.tp_list_price != null
                ? Number(ticket.tp_list_price)
                : null;

            if (isBulkSoldTransition) {
              const listPriceNum = Number(ticket.tp_list_price) || 0;
              const ticketFeeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, bulkSoldFeeConfigByTicketId);
              const quickPct = bulkSelectedMp
                ? effectiveSoldQuickCutPercent(
                    bulkSelectedMp.vl_fee_percentage,
                    ticketFeeConfig.feeMode,
                    ticketFeeConfig.superClientPercent,
                  )
                : 0;
              const quickCutDollars = bulkSelectedMp && bulkSoldFeeConfigReady
                ? Number(roundToFixed(listPriceNum * (quickPct / 100), 2))
                : 0;
              const expectedFromLogitix = bulkSelectedMp
                ? Math.round(listPriceNum * (100 - bulkSelectedMp.vl_fee_percentage)) / 100
                : null;
              const logitixParsed = parseFloat(String(bulkSoldLogitixAmounts[ticket.tp_id] || '').trim());
              const hasLogitixInput = !isNaN(logitixParsed) && logitixParsed > 0;
              const receivedForSettlement =
                hasLogitixInput ? logitixParsed : expectedFromLogitix != null ? expectedFromLogitix : null;
              const usingEnteredLogitix = hasLogitixInput;
              const settlementAmount =
                receivedForSettlement != null && bulkSelectedMp && bulkSoldFeeConfigReady
                  ? Math.round((receivedForSettlement - quickCutDollars) * 100) / 100
                  : null;

              const clientFeeLabel =
                ticketFeeConfig.feeMode === 'super' && ticketFeeConfig.superClientPercent != null
                  ? `${ticketFeeConfig.superClientPercent}% client fee`
                  : `${DEFAULT_CONCIERGE_PERCENT}% client fee (default)`;

              return (
                <div key={ticket.tp_id} style={{ padding: '12px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div className={styles.ctEventDetails} style={{ marginBottom: 8 }}>
                    Seat {ticket.tp_seat_no ?? 'N/A'} • Section {ticket.tp_section || 'N/A'} • Row {ticket.tp_row || 'N/A'}
                    {ticket.u_full_name ? ` • ${ticket.u_full_name}` : ''}
                  </div>
                  <div className={styles.ctCurrentStatusRow} style={{ marginTop: 0, marginBottom: 8 }}>
                    <span className={styles.ctCurrentStatusLabel}>List price:</span>
                    <b>${formatNumberWithCommas(listPriceNum)}</b>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{clientFeeLabel}</div>
                  <div className={styles.ctPriceLabel}>Amount received from Logitix</div>
                  <div className={styles.cttPriceInputWrapper} style={{ marginBottom: 8 }}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                    readOnly
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                     
                      value={bulkSoldLogitixAmounts[ticket.tp_id] ?? ''}
                    
                    />
                  </div>
                  {bulkSelectedMp && bulkSoldFeeConfigReady ? (
                    <div className={styles.ctFeeBreakdown}>
                      <div><b>Sold details (preview)</b></div>
                      <div>Marketplace: {bulkSelectedMp.name} ({bulkSelectedMp.vl_fee_percentage}%)</div>
                      <div>
                        QuickAsyst fee ({quickPct}% of list):{' '}
                        <span style={{ color: '#b91c1c' }}>
                          {quickCutDollars != null ? `-$${formatNumberWithCommas(quickCutDollars)}` : '—'}
                        </span>
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <span className={styles.ctCurrentStatusLabel}>Amount settled to user</span>
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 700 }}>
                        {settlementAmount != null ? `$${formatNumberWithCommas(settlementAmount)}` : '—'}
                      </div>
                      {receivedForSettlement != null && quickCutDollars != null && settlementAmount != null ? (
                        <div style={{ fontSize: 12, marginTop: 6 }}>
                          ${formatNumberWithCommas(receivedForSettlement)}{' '}
                          {usingEnteredLogitix ? 'received' : 'expected'} − $
                          {formatNumberWithCommas(quickCutDollars)} QuickAsyst fee
                          {usingEnteredLogitix ? '' : ' (preview)'}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            }

            if (isBulkListTransition) {
              return (
                <div
                  key={ticket.tp_id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.2fr 1fr',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Seat {ticket.tp_seat_no ?? 'N/A'}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {getStatusLabel(ticket.tp_status || '')}
                      {currentPrice != null && <> • Current: ${currentPrice}</>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                      value={bulkStatusListingPrices[ticket.tp_id] ?? ''}
                      onChange={e =>
                        setBulkStatusListingPrices({
                          ...bulkStatusListingPrices,
                          [ticket.tp_id]: sanitizePriceInput(e.target.value),
                        })
                      }
                      placeholder="Price"
                    />
                  </div>
                </div>
              );
            }

            return (
              <div key={ticket.tp_id} style={{ fontSize: 12, padding: '4px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>Seat {ticket.tp_seat_no}</span>
                <span style={{ color: '#6b7280', textAlign: 'right' }}>
                  {getStatusLabel(ticket.tp_status || '')}
                  {currentPrice != null && <> • {formatCurrency(currentPrice)}</>}
                </span>
              </div>
            );
          })}
          {!isBulkListTransition && !isBulkSoldTransition && bulkSelectedTickets.length > 5 && (
            <div style={{ fontSize: 12, color: '#6b7280', paddingTop: 4 }}>
              ... and {bulkSelectedTickets.length - 5} more
            </div>
          )}
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkStatusModal}>
          {isBulkSoldTransition ? 'Back' : 'Cancel'}
        </button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkConfirmDisabled ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkConfirmDisabled}
          onClick={handleBulkStatusChange}
        >
          {bulkStatusChangeLoading || bulkSoldLoading
            ? 'Saving…'
            : isBulkSoldTransition
              ? `Confirm ${bulkSelectedTickets.length} Ticket(s)`
              : `Update ${bulkSelectedTickets.length} Ticket(s)`}
        </button>
      </div>
    </div>
  </div>
  );
})()}

{showBulkListModal && bulkSelectedTickets.length > 0 && (
  <div className={styles.modalOverlay} onClick={closeBulkListModal}>
    <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
          <span className={styles.ctModalTitle}>Bulk List Tickets</span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkListModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        <div className={styles.ctEventName}>{bulkSelectedTickets[0]?.e_name || 'Selected Tickets'}</div>
        <div className={styles.ctEventDetails}>
          {bulkSelectedTickets.length} ticket(s) selected • Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
        </div>

        <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>Apply same list price to all selected tickets</div>
        <div className={styles.ctPriceInputWrapper} style={{ marginBottom: 12 }}>
          <span className={styles.ctPriceDollar}>$</span>
          <input
            type="text"
            inputMode="decimal"
            className={styles.ctPriceInput}
            value={bulkListCommonPrice}
            onChange={e => setBulkListCommonPrice(sanitizePriceInput(e.target.value))}
            placeholder="Enter price"
          />
          <button className={styles.ctConfirmBtn} style={{ marginLeft: 12, minWidth: 120 }} onClick={handleApplyBulkListPrice}>
            Apply to all
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className={styles.ctPriceLabel}>Ticket details & prices</div>
          <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
            {bulkSelectedTickets.map(ticket => (
              <div key={ticket.tp_id} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e5e7eb' }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{ticket.e_name || 'Ticket'}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    Seat {ticket.tp_seat_no || 'N/A'} • Section {ticket.tp_section || 'N/A'} • Row {ticket.tp_row || 'N/A'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className={styles.ctPriceDollar}>$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={styles.ctPriceInput}
                    value={bulkListedPrices[ticket.tp_id] ?? ''}
                    onChange={e => setBulkListedPrices({ ...bulkListedPrices, [ticket.tp_id]: sanitizePriceInput(e.target.value) })}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkListModal}>Back</button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkListLoading ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkListLoading}
          onClick={handleBulkListConfirm}
        >
          {bulkListLoading ? 'Saving...' : `Confirm ${bulkSelectedTickets.length} Tickets`}
        </button>
      </div>
    </div>
  </div>
)}

{showBulkSoldModal && bulkSelectedTickets.length > 0 && (
  <div className={styles.modalOverlay} onClick={closeBulkSoldModal}>
    <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px' }}>
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
          <span className={styles.ctModalTitle}>Bulk Mark as Sold</span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkSoldModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        <div className={styles.ctEventName}>{bulkSelectedTickets[0]?.e_name || 'Selected Tickets'}</div>
        <div className={styles.ctEventDetails}>
          {bulkSelectedTickets.length} ticket(s) selected • Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
        </div>

        <div className={styles.ctPriceLabel} style={{ marginTop: 12 }}>Marketplace</div>
        <select
          className={styles.ctMarketplaceSelect}
          value={bulkSoldMarketplaceId}
          onChange={e => setBulkSoldMarketplaceId(e.target.value)}
          disabled={marketplacesLoading || (!marketplacesList.length && !marketplacesLoading)}
        >
          {marketplacesLoading ? (
            <option value="">Loading marketplaces...</option>
          ) : marketplacesError ? (
            <option value="">Unable to load marketplaces</option>
          ) : marketplacesList.length === 0 ? (
            <option value="">No active marketplaces available</option>
          ) : (
            <option value="">Choose a marketplace...</option>
          )}
          {marketplacesList.map(mp => (
            <option key={mp.id} value={mp.id}>{mp.name} ({mp.vl_fee_percentage}%)</option>
          ))}
        </select>

        {/* <div className={styles.ctPriceLabel} style={{ marginTop: 16 }}>Apply same Logitix amount to all selected tickets</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <div className={styles.ctPriceInputWrapper} style={{ flex: 1 }}>
            <span className={styles.ctPriceDollar}>$</span>
            <input
              type="text"
              inputMode="decimal"
              className={styles.ctPriceInput}
              value={bulkSoldCommonLogitixAmount}
              onChange={e => setBulkSoldCommonLogitixAmount(sanitizePriceInput(e.target.value))}
              placeholder="Enter amount"
            />
          </div>
          <button className={styles.ctConfirmBtn} style={{ minWidth: 120 }} onClick={handleApplyBulkSoldAmount}>
            Apply to all
          </button>
        </div> */}
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
          Defaults to list price minus marketplace fee for each ticket if left blank.
        </div>

        <div style={{ marginTop: 16 }}>
          <div className={styles.ctPriceLabel}>Ticket amounts</div>
          <div style={{ marginTop: 8, display: 'grid', gap: 10 }}>
            {bulkSelectedTickets.map(ticket => {
              const listPrice = Number(ticket.tp_list_price) || 0;
              const selectedMp = marketplacesList.find(m => m.id === bulkSoldMarketplaceId);
              const ticketFeeConfig = getBulkTicketSoldFeeConfig(ticket.tp_id, bulkSoldFeeConfigByTicketId);
              const quickPct = effectiveSoldQuickCutPercent(
                selectedMp?.vl_fee_percentage || 0,
                ticketFeeConfig.feeMode,
                ticketFeeConfig.superClientPercent,
              );
              const quickCutDollars = Number(roundToFixed(listPrice * (quickPct / 100), 2));
              const expectedLogitix = selectedMp ? Math.round(listPrice * (100 - selectedMp.vl_fee_percentage)) / 100 : 0;
              const rawLogitixValue = bulkSoldLogitixAmounts[ticket.tp_id];
              const logitixValue = rawLogitixValue !== undefined && rawLogitixValue !== null && String(rawLogitixValue).trim() !== ''
                ? Number(parseFloat(String(rawLogitixValue).trim()))
                : expectedLogitix;
              const settlement = Number.isFinite(logitixValue)
                ? Math.round((logitixValue - quickCutDollars) * 100) / 100
                : 0;
              return (
                <div key={ticket.tp_id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 10, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e5e7eb' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{ticket.e_name || 'Ticket'}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>
                      Seat {ticket.tp_seat_no || 'N/A'} • Section {ticket.tp_section || 'N/A'} • Row {ticket.tp_row || 'N/A'}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                      List price: ${formatNumberWithCommas(listPrice)}
                    </div>
                    <div style={{ fontSize: 13, color: '#374151' }}>
                      Marketplace fee: {selectedMp ? `${selectedMp.vl_fee_percentage}%` : '—'} • QuickAsyst fee: ${formatNumberWithCommas(quickCutDollars)}
                    </div>
                  </div>
                  <div className={styles.ctPriceInputWrapper} style={{ minWidth: 140 }}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                      value={bulkSoldLogitixAmounts[ticket.tp_id] ?? ''}
                      onChange={e => setBulkSoldLogitixAmounts({ ...bulkSoldLogitixAmounts, [ticket.tp_id]: sanitizePriceInput(e.target.value) })}
                      disabled={!bulkSoldMarketplaceId}
                      placeholder={selectedMp ? String(expectedLogitix) : 'Enter amount'}
                    />
                  </div>
                  <div style={{ fontSize: 13, color: '#1f2937', textAlign: 'right' }}>
                    Settled: ${Number.isNaN(settlement) ? '0.00' : settlement.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkSoldModal}>Back</button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkSoldLoading ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkSoldLoading || !bulkSoldMarketplaceId}
          onClick={handleBulkSoldConfirm}
        >
          {bulkSoldLoading ? 'Saving...' : `Confirm ${bulkSelectedTickets.length} Tickets`}
        </button>
      </div>
    </div>
  </div>
)}

{/* Bulk Price Change Modal */}
{showBulkPriceModal && bulkSelectedTickets.length > 0 && (
  <div className={styles.modalOverlay} onClick={closeBulkPriceModal}>
    <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px' }}>
      <div className={styles.ctModalHeader}>
        <div className={styles.ctModalTitleRow}>
          <DollarSign className={styles.ctRotateIcon} size={20} />
          <span className={styles.ctModalTitle}>Bulk Change Price</span>
        </div>
        <button className={styles.ctModalClose} onClick={closeBulkPriceModal}>×</button>
      </div>
      <div className={styles.ctModalBody}>
        <div className={styles.ctEventName}>{bulkSelectedTickets[0]?.e_name || 'Selected Tickets'}</div>
        <div className={styles.ctEventDetails}>
          {bulkSelectedTickets.length} ticket(s) selected • Section {bulkSelectedTickets[0]?.tp_section || 'N/A'} • Row {bulkSelectedTickets[0]?.tp_row || 'N/A'}
        </div>

        <div className={styles.ctPriceLabel} style={{ marginTop: 16, marginBottom: 8 }}>Apply same price to all selected tickets (optional)</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div className={styles.ctPriceInputWrapper} style={{ flex: 1 }}>
            <span className={styles.ctPriceDollar}>$</span>
            <input
              type="text"
              inputMode="decimal"
              className={styles.ctPriceInput}
              value={bulkPriceCommon}
              onChange={e => setBulkPriceCommon(sanitizePriceInput(e.target.value))}
              placeholder="Enter price"
            />
          </div>
          <button 
            className={styles.ctConfirmBtn} 
            style={{ minWidth: 140 }}
            onClick={handleApplyCommonBulkPrice}
          >
            <span>Apply to all</span>
          </button>
        </div>

        <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: 16, marginTop: 16 }}>
          <div className={styles.ctPriceLabel} style={{ marginBottom: 12 }}>Individual ticket prices</div>
          <div className={styles.bulkPriceScrollContainer} style={{ maxHeight: '400px', overflowY: 'auto', display: 'grid', gap: 12 }}>
            {bulkSelectedTickets.map((ticket) => {
              const currentPrice = ticket.tp_list_price != null ? Number(ticket.tp_list_price) : null;
              return (
                <div 
                  key={ticket.tp_id} 
                  className={styles.bulkPriceTicketCard}
                >
                  <div className={styles.bulkPriceTicketInfo}>
                    <div className={styles.bulkPriceTicketName}>Seat {ticket.tp_seat_no || 'N/A'}</div>
                    <div className={styles.bulkPriceTicketSubtext}>
                      {ticket.u_full_name || 'N/A'} • {ticket.u_email_id || 'N/A'}
                    </div>
                    <div className={styles.bulkPriceTicketCurrentPrice}>
                      Current price: ${currentPrice?.toFixed(2) || '0.00'}
                    </div>
                  </div>
                  <div className={styles.ctPriceInputWrapper}>
                    <span className={styles.ctPriceDollar}>$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={styles.ctPriceInput}
                      value={bulkPriceIndividual[ticket.tp_id] ?? ''}
                      onChange={e =>
                        setBulkPriceIndividual({
                          ...bulkPriceIndividual,
                          [ticket.tp_id]: sanitizePriceInput(e.target.value),
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.ctModalFooter}>
        <button className={styles.ctBackBtn} onClick={closeBulkPriceModal}>Cancel</button>
        <button
          className={`${styles.ctConfirmBtn} ${bulkPriceLoading ? styles.ctConfirmBtnDisabled : ''}`}
          disabled={bulkPriceLoading}
          onClick={handleBulkPriceConfirm}
        >
          {bulkPriceLoading ? 'Saving…' : `Update ${bulkSelectedTickets.length} Ticket${bulkSelectedTickets.length > 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  </div>
)}

{/* Bulk Delist Email Modal */}
{showBulkDelistEmailModal && (
  <div className={styles.modalOverlay} onClick={() => setShowBulkDelistEmailModal(false)}>
    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
      <div className={styles.modalHeader}>
        <div>
          <h3>Bulk Delist Request</h3>
          <p>Please provide the email address to return the tickets to</p>
        </div>
        <button className={styles.modalClose} onClick={() => setShowBulkDelistEmailModal(false)}><X size={18} /></button>
      </div>
      <div className={styles.modalBody}>
        <div className={styles.delistCount}>Delisting <strong>{pendingBulkDelistTpIds.length}</strong> ticket(s)</div>
        <div className={styles.editField}>
          <label>Email</label>
          <input type="email" placeholder="name@company.com" {...registerDelistEmail('email', { required: 'Email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' } })} />
          {delistEmailErrors.email && <small className={styles.errorText}>{delistEmailErrors.email.message as string}</small>}
        </div>
        <div className={styles.editField}>
          <label>Confirm Email</label>
          <input type="email" placeholder="name@company.com" {...registerDelistEmail('confirmEmail', { required: 'Confirm email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' }, validate: (val) => watchDelistEmail('email') === val || 'Emails do not match' })} />
          {delistEmailErrors.confirmEmail && <small className={styles.errorText}>{delistEmailErrors.confirmEmail.message as string}</small>}
        </div>
      </div>
      <div className={styles.modalFooter}>
        <button className={styles.modalCancel} onClick={() => setShowBulkDelistEmailModal(false)} disabled={statusTableBusyCount > 0}>Cancel</button>
        <button className={styles.modalConfirm} onClick={handleDelistEmailSubmit(onBulkDelistEmailSubmit)} disabled={statusTableBusyCount > 0}>
          {statusTableBusyCount > 0 ? 'Submitting…' : 'Submit Delist Request'}
        </button>
      </div>
    </div>
  </div>
)}
              {/* Pagination — show whenever there are rows (even a single page) */}
              {effectiveTotalCount > 0 && !isEventGroupedView && (
                <div className={styles.pagination}>
                  <div className={styles.paginationLeft}>
                    <span>Showing {startIndex} to {endIndex} of {effectiveTotalCount} tickets</span>
                    <div className={styles.pageSizeSelector}>
                      <label>Rows per page:</label>
                      <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                  <div className={styles.paginationRight}>
                    <button onClick={() => handlePageChange(pageOffset - pageSize)} disabled={pageOffset === 0}>
                      <ChevronLeft size={16} /> Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => handlePageChange(pageOffset + pageSize)} disabled={pageOffset + pageSize >= effectiveTotalCount}>
                      Next <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
            </div>
          )}
        </div>
      </div>

      {/* Custom Range Modal */}
      {showCustomRangeModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCustomRangeModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className={styles.modalHeader}>
              <h3><Calendar size={16} style={{ marginRight: 8 }} /> Select Date Range</h3>
              <button className={styles.modalClose} onClick={() => setShowCustomRangeModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.editField}>
                <label>Start Date</label>
                <input
                  type="date"
                  value={customRangeStart}
                  onChange={(e) => setCustomRangeStart(e.target.value)}
                />
              </div>
              <div className={styles.editField}>
                <label>End Date</label>
                <input
                  type="date"
                  value={customRangeEnd}
                  onChange={(e) => setCustomRangeEnd(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={() => setShowCustomRangeModal(false)}>Cancel</button>
              <button className={styles.modalConfirm} onClick={applyCustomRange}>Apply Range</button>
            </div>
          </div>
        </div>
      )}

      {/* Price Change Modal */}
      {showPriceModal && selectedTicketForPrice && (
        <div className={styles.modalOverlay} onClick={() => setShowPriceModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Confirm Price Change</h3>
              <button className={styles.modalClose} onClick={() => setShowPriceModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalEventInfo}>
                <div className={styles.modalEventName}>{selectedTicketForPrice.e_name}</div>
                <div className={styles.modalEventDetails}>
                  Section {selectedTicketForPrice.tp_section || 'N/A'} • Row {selectedTicketForPrice.tp_row || 'N/A'} • Seats {selectedTicketForPrice.tp_seat_no || 'N/A'}
                </div>
              </div>
              <div className={styles.priceCompare}>
                <div className={styles.currentPrice}>
                  <div>Current Price</div>
                  <div>${selectedTicketForPrice.tp_list_price || 0}</div>
                </div>
                <div className={styles.newPriceInput}>
                  <div>New Price</div>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={newPrice}
                    onChange={(e) => setNewPrice(sanitizePriceInput(e.target.value))}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className={styles.selectedCountInfo}>
                <CheckSquare size={12} /> {selectedTpIds.length} ticket(s) selected
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={() => setShowPriceModal(false)} disabled={priceLoading}>Cancel</button>
              <button
                className={styles.modalConfirm}
                onClick={handlePriceChangeConfirm}
                disabled={priceLoading || !newPrice}
              >
                {priceLoading ? 'Saving…' : 'Confirm Price Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedTicketForView && (
        <div className={styles.modalOverlay} onClick={() => setShowViewModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className={styles.modalHeader}>
              <h3><Eye size={18} /> Ticket Details</h3>
              <button className={styles.modalClose} onClick={() => setShowViewModal(false)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.viewDetailsHero}>
                <h4>{selectedTicketForView.e_name || 'N/A'}</h4>
                <p><MapPinned size={12} /> {selectedTicketForView.e_address || 'N/A'}</p>
                <p><Calendar size={12} /> {formatDate(selectedTicketForView.e_date)} • {formatTime(selectedTicketForView.e_date_time_zone)}</p>
              </div>
              <div className={styles.viewDetailsGrid}>
                <div className={styles.detailSection}>
                  <h5>Ticket Details</h5>
                  <div><span>Section</span><span>{selectedTicketForView.tp_section || 'N/A'}</span></div>
                  <div><span>Row</span><span>{selectedTicketForView.tp_row || 'N/A'}</span></div>
                  <div><span>Seat Numbers</span><span>{selectedTicketForView.tp_seat_no || 'N/A'}</span></div>
                  <div><span>Status</span><span className={styles.statusBadge}>{getStatusLabel(selectedTicketForView.tp_status || '')}</span></div>
                  <div><span>List Price</span><span>{formatCurrency(selectedTicketForView.tp_list_price || 0)}</span></div>
                </div>
                <div className={styles.detailSection}>
                  <h5>Client Details</h5>
                  <div><span>Client Name</span><span>{getClientDisplayName(selectedTicketForView)}</span></div>
                  <div><span>Client Email</span><span>{selectedTicketForView.u_email_id || 'N/A'}</span></div>
                  {selectedTicketForView.tp_delist_requested_email?.trim() && (
                    <div><span>Return Email</span><span>{selectedTicketForView.tp_delist_requested_email.trim()}</span></div>
                  )}
                  <div><span>Client Type</span><span>{getResolvedClientType(selectedTicketForView) === 'self-pricing'
                    ? 'Self Pricing'
                    : getResolvedClientType(selectedTicketForView) === 'concierge'
                    ? 'Concierge'
                    : 'N/A'}</span></div>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalConfirm} onClick={() => {
                setShowViewModal(false);
                if (selectedTicketForView.u_id) navigate(`/user/${selectedTicketForView.u_id}?tab=tickets`);
              }}>View Full Profile</button>
              <button className={styles.modalCancel} onClick={() => setShowViewModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ticket Details Modal */}
      {showEditModal && selectedTicketForEdit && (
        <div className={styles.modalOverlay} onClick={handleEditModalBackdropClick}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3><Edit size={18} /> Edit Ticket Details</h3>
              <button type="button" className={styles.modalClose} onClick={closeEditModal}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalEventInfo}>
                <div className={styles.modalEventName}>{selectedTicketForEdit.e_name}</div>
                <div>{formatDate(selectedTicketForEdit.e_date)} • {formatTime(selectedTicketForEdit.e_date_time_zone)}</div>
              </div>
              <div className={styles.editField}>
                <label>Section</label>
                <input type="text" value={editFormData.section} onChange={e => setEditFormData({...editFormData, section: e.target.value})} />
              </div>
              <div className={styles.editField}>
                <label>Row</label>
                <input type="text" value={editFormData.row} onChange={e => setEditFormData({...editFormData, row: e.target.value})} />
              </div>
              <div className={styles.editField}>
                <label>Seat Numbers</label>
                <input type="text" value={editFormData.seatNumbers} onChange={e => setEditFormData({...editFormData, seatNumbers: e.target.value})} />
                <small>Format: single seats (1, 2, 5) or ranges (1-4)</small>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalCancel} onClick={closeEditModal}>Cancel</button>
              <button type="button" className={styles.modalConfirm} onClick={handleEditDetailsConfirm}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Status Modal */}
      {showStatusModal && selectedTicketForStatus && (() => {
        const currentSt = getTicketTransitionStatus(selectedTicketForStatus);
        const pres = getManageTicketsStatusPresentation(selectedTicketForStatus);
        const currentLabel = pres.label;
        const selectedLabel = newStatus ? getStatusLabel(newStatus) : '';
        const getCtBadgeClass = (st: string) => {
          const s = (st || '').toLowerCase().replace(/\s/g, '');
          if (s === 'verified') return styles.ctStatusBadgeVerified;
          if (s === 'list' || s === 'listed') return styles.ctStatusBadgeListed;
          if (s === 'sold') return styles.ctStatusBadgeSold;
          if (s === 'tobeverified') return styles.ctStatusBadgeToBeVerified;
          if (s === 'invalid') return styles.ctStatusBadgeInvalid;
          return styles.ctStatusBadgeDefault;
        };
        const ctBadgeClassForCurrent = () => {
          const ik = (pres.iconStatus || '').toLowerCase().replace(/\s/g, '');
          if (ik === 'settled') return styles.ctStatusBadgeVerified;
          if (ik === 'settlementinprogress') return styles.ctStatusBadgeListed;
          if (ik === 'failed') return styles.ctStatusBadgeInvalid;
          return getCtBadgeClass(currentSt);
        };
        return (
          <div className={styles.modalOverlay} onClick={closeStatusModal}>
            <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()}>
              <div className={styles.ctModalHeader}>
                <div className={styles.ctModalTitleRow}>
                  <RefreshRoundedIcon className={styles.ctRotateIcon} fontSize="small" />
                  <span className={styles.ctModalTitle}>Change Ticket Status</span>
                </div>
                <button className={styles.ctModalClose} onClick={closeStatusModal}>×</button>
              </div>
              <div className={styles.ctModalBody}>
                <div className={styles.ctEventName}>{selectedTicketForStatus.e_name}</div>
                <div className={styles.ctEventDetails}>
                  Section {selectedTicketForStatus.tp_section || 'N/A'} • Row {selectedTicketForStatus.tp_row || 'N/A'} • Seats {selectedTicketForStatus.tp_seat_no || 'N/A'}
                </div>
                <div className={styles.ctEventDate}>
                  {formatDate(selectedTicketForStatus.e_date)}{selectedTicketForStatus.e_date_time_zone ? ` • ${formatTime(selectedTicketForStatus.e_date_time_zone)}` : ''}
                </div>

                <div className={styles.ctCurrentStatusRow}>
                  <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                  <span className={`${styles.ctStatusBadge} ${ctBadgeClassForCurrent()}`}>
                    {getStatusIcon(pres.iconStatus)} {currentLabel}
                  </span>
                </div>

                <div className={styles.ctNewStatusTitle}>Select New Status:</div>
                <div className={styles.ctStatusOptions}>
                  {getStatusTransitions(currentSt).map((st) => {
                    const isDelistOpt = st.value === 'DelistInProgress' || st.value === 'DelistRequested';
                    const isSelected = newStatus === st.value;
                    let btnClass = styles.ctStatusOption;
                    if (isSelected) {
                      btnClass = isDelistOpt
                        ? `${styles.ctStatusOption} ${styles.ctStatusOptionOrangeSelected}`
                        : `${styles.ctStatusOption} ${styles.ctStatusOptionSelected}`;
                    } else if (isDelistOpt) {
                      btnClass = `${styles.ctStatusOption} ${styles.ctStatusOptionOrange}`;
                    }
                    return (
                      <button key={st.value} className={btnClass} onClick={() => setNewStatus(st.value)}>
                        {st.label}
                      </button>
                    );
                  })}
                </div>

                {newStatus && (
                  <div className={styles.ctTransitionVisual}>
                    <span className={`${styles.ctStatusBadge} ${ctBadgeClassForCurrent()}`}>
                      {getStatusIcon(pres.iconStatus)} {currentLabel}
                    </span>
                    <ArrowRight size={16} className={styles.ctTransArrow} />
                    <span className={`${styles.ctStatusBadge} ${getCtBadgeClass(newStatus)}`}>
                      {getStatusIcon(newStatus)} {selectedLabel}
                    </span>
                  </div>
                )}
              </div>
              <div className={styles.ctModalFooter}>
                <button className={styles.ctBackBtn} onClick={closeStatusModal}>Back</button>
                <button
                  className={`${styles.ctConfirmBtn} ${(!newStatus || statusChangeLoading) ? styles.ctConfirmBtnDisabled : ''}`}
                  disabled={!newStatus || statusChangeLoading}
                  onClick={() => {
                    if (selectedTicketForStatus && newStatus) {
                      handleStatusChangeConfirm(selectedTicketForStatus, newStatus);
                    }
                  }}
                >
                  {statusChangeLoading ? 'Saving...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delist Email Modal */}
      {showDelistEmailModal && (
        <div className={styles.modalOverlay} onClick={closeDelistEmailModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3>Delist Request</h3>
                <p>Please provide the email address to return the tickets to</p>
              </div>
              <button className={styles.modalClose} onClick={closeDelistEmailModal}><X size={18} /></button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.delistCount}>Delisting <strong>{pendingDelistTpIds.length}</strong> ticket(s)</div>
              <div className={styles.editField}>
                <label>Email</label>
                <input type="email" placeholder="name@company.com" {...registerDelistEmail('email', { required: 'Email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' } })} />
                {delistEmailErrors.email && <small className={styles.errorText}>{delistEmailErrors.email.message as string}</small>}
              </div>
              <div className={styles.editField}>
                <label>Confirm Email</label>
                <input type="email" placeholder="name@company.com" {...registerDelistEmail('confirmEmail', { required: 'Confirm email is required', pattern: { value: emailRegexPattern, message: 'Enter a valid email' }, validate: (val) => watchDelistEmail('email') === val || 'Emails do not match' })} />
                {delistEmailErrors.confirmEmail && <small className={styles.errorText}>{delistEmailErrors.confirmEmail.message as string}</small>}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={closeDelistEmailModal} disabled={statusTableBusyCount > 0}>Cancel</button>
              <button className={styles.modalConfirm} onClick={handleDelistEmailSubmit(onDelistEmailSubmit)} disabled={statusTableBusyCount > 0}>
                {statusTableBusyCount > 0 ? 'Submitting…' : 'Submit Delist Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && selectedTicketForDelete && (
        <div className={styles.modalOverlay} onClick={() => setShowDeleteConfirmModal(false)}>
          <div className={styles.ctModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.ctModalHeader}>
              <div className={styles.ctModalTitleRow}>
                <AlertCircle className={styles.deleteIconWarning} size={20} />
                <span className={styles.ctModalTitle}>Delete Ticket</span>
              </div>
              <button className={styles.ctModalClose} onClick={() => setShowDeleteConfirmModal(false)}>×</button>
            </div>
            <div className={styles.ctModalBody}>
              <div className={styles.ctEventName}>{selectedTicketForDelete.e_name}</div>
              <div className={styles.ctEventDetails}>
                Section {selectedTicketForDelete.tp_section || 'N/A'} • Row {selectedTicketForDelete.tp_row || 'N/A'} • Seats {selectedTicketForDelete.tp_seat_no || 'N/A'}
              </div>
              <div className={styles.ctEventDate}>
                {formatDate(selectedTicketForDelete.e_date)}{selectedTicketForDelete.e_date_time_zone ? ` • ${formatTime(selectedTicketForDelete.e_date_time_zone)}` : ''}
              </div>

              <div className={styles.ctCurrentStatusRow}>
                <span className={styles.ctCurrentStatusLabel}>Current Status:</span>
                <span className={`${styles.statusBadge} ${getStatusColor(selectedTicketForDelete.tp_status)}`}>
                  {getStatusIcon(selectedTicketForDelete.tp_status)} {getStatusLabel(selectedTicketForDelete.tp_status)}
                </span>
              </div>

              <div className={styles.deleteWarningMessage}>
                <AlertCircle size={16} />
                <span>Are you sure you want to delete this ticket? This action will remove the ticket from active views and cannot be undone.</span>
              </div>
            </div>
            <div className={styles.ctModalFooter}>
              <button className={styles.ctBackBtn} onClick={() => setShowDeleteConfirmModal(false)} disabled={deleteLoading}>Cancel</button>
              <button
                className={`${styles.ctConfirmBtn} ${deleteLoading ? styles.ctConfirmBtnDisabled : ''}`}
                disabled={deleteLoading}
                onClick={handleDeleteConfirm}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Ticket'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Detail Modal for Tracking Issues */}
      {showCardDetailModal && (
        <div className={styles.modalOverlay} onClick={() => setShowCardDetailModal(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '80vh' }}>
            <div className={styles.modalHeader}>
              <h3>
                {showCardDetailModal === 'LISTING_EXPIRED' 
                  ? `Listing Request Expired Tickets (${cardDetailTickets.length})`
                  : `Fail to Transfer Tickets (${cardDetailTickets.length})`}
              </h3>
              <button className={styles.modalClose} onClick={() => setShowCardDetailModal(null)}><X size={18} /></button>
            </div>
            <div className={styles.modalBody} style={{ overflowX: 'auto' }}>
              {cardDetailLoading ? (
                <div className={styles.loadingState}><div className={styles.spinner}></div><p>Loading tickets...</p></div>
              ) : cardDetailTickets.length > 0 ? (
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>User</th>
                      <th>Section</th>
                      <th>Row</th>
                      <th>Seat</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardDetailTickets.map((ticket, idx) => (
                      <tr key={ticket.tp_id || idx}>
                        <td><div className={styles.eventName}>{ticket.e_name || 'N/A'}</div></td>
                        <td>
                          {ticket.u_full_name ||
                            `${ticket.u_first_name || ''} ${ticket.u_last_name || ''}`.trim() ||
                            'N/A'}
                        </td>
                        <td>{ticket.tp_section || 'N/A'}</td>
                        <td>{ticket.tp_row || 'N/A'}</td>
                        <td>{ticket.tp_seat_no || 'N/A'}</td>
                        <td>
                          {showCardDetailModal === 'FAILED_TRANSFER' ? (
                            (() => {
                              const transitions = getStatusTransitions(ticket.tp_status || '');
                              const modalStatusDropdownKey = `failed-${ticket.tp_id}`;
                              if (!transitions.length) {
                                return <span className={`${styles.statusBadge} ${getStatusColor(ticket.tp_status)}`}>{getStatusLabel(ticket.tp_status || '')}</span>;
                              }
                              return (
                                <div className={styles.statusDropdownWrapper}>
                                  <button
                                    className={`${styles.statusBadge} ${getStatusColor(ticket.tp_status)} ${styles.statusDropdownBtn}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenStatusDropdown(openStatusDropdown === modalStatusDropdownKey ? null : modalStatusDropdownKey);
                                    }}
                                  >
                                    {getStatusIcon(ticket.tp_status || '')}
                                    {getStatusLabel(ticket.tp_status || '')}
                                    <ChevronDown size={12} style={{ marginLeft: 4 }} />
                                  </button>
                                  {openStatusDropdown === modalStatusDropdownKey && (
                                    <div className={styles.statusDropdownList}>
                                      {transitions.map((opt) => (
                                        <div
                                          key={opt.value}
                                          className={styles.statusDropdownListItem}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setOpenStatusDropdown(null);
                                            if (opt.value === 'Sold' && normalizeStatusValue(ticket.tp_status || '') === 'List') {
                                              await openSoldModalForTicket(ticket);
                                            } else {
                                              await handleStatusChangeConfirm(ticket, opt.value);
                                            }
                                          }}
                                        >
                                          {getStatusIcon(opt.value)}
                                          {opt.label}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()
                          ) : (
                            <span className={styles.statusBadge}>{getStatusLabel(ticket.tp_status || '')}</span>
                          )}
                        </td>
                        <td>{formatCurrency(ticket.tp_list_price ?? 0)}</td>
                        <td>{formatDate(ticket.e_date)}</td>
                        <td>
                          <button className={styles.viewUserBtn} onClick={() => handleUserClick(ticket.u_id)}>View User</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className={styles.noResults}>No tickets found</div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={() => setShowCardDetailModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTickets;